import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { Col, Row, Spinner, Modal, Button, Offcanvas } from "react-bootstrap";
import { notify, reasonFromError } from "../utilities/notify.js";
import {
  FiActivity, FiAlertTriangle, FiCheckCircle, FiClock,
  FiDollarSign, FiList, FiRefreshCw, FiShield, FiWifi,
  FiWifiOff, FiZap, FiXCircle, FiChevronRight, FiSearch,
  FiTrendingUp, FiAlertOctagon,
  FiUsers, FiSend, FiThumbsUp, FiThumbsDown, FiPackage,
  FiCalendar, FiPlusCircle, FiAlertCircle, FiRotateCcw, FiDownload,
  FiPercent, FiFileText, FiClipboard, FiUpload, FiHash,
  FiMail, FiSettings, FiPieChart, FiX, FiLayers,
} from "react-icons/fi";
import {
  apiWalletGetBalance,
  apiWalletListDisbursements,
  apiWalletGetCircuitBreaker,
  apiWalletProxyHealth,
  apiWalletPollStuck,
  apiWalletGetSummary,
  apiWalletGetCommissions,
  apiGetVendorCommissionContracts,
  apiUpdateVendorCommissionContract,
  apiUpdateVendorVatSettings,
  apiWalletHealth,
  apiWalletFeeBands,
  apiWalletPayoutPurposes,
  apiWalletFeeCorrectionPlan,
  apiWalletApplyFeeCorrections,
  apiWalletListBatches,
  apiWalletGetBatch,
  apiWalletInitiateRiderBatch,
  apiWalletInitiatePartnerBatch,
  apiWalletApproveBatch,
  apiWalletRejectBatch,
  apiWalletListReconciliation,
  apiWalletRunReconciliation,
  apiWalletResolveDiscrepancy,
  apiWalletMarkReviewed,
  apiWalletManualPayout,
  apiWalletLookupPayee,
  apiWalletTreasury,
  apiWalletRecordFwSettlement,
  apiWalletRecordFloatTopup,
  apiWalletGetFloatSettings,
  apiWalletUpdateFloatSettings,
  apiWalletListManualPayouts,
  apiWalletRetryDisbursement,
  apiWalletBulkPayoutValidate,
  apiWalletBulkPayout,
  apiEntitySearch,
  apiWalletScheduler,
  apiWalletReportSummary,
  apiWalletReportRegisterCsv,
  apiWalletReportLedgerCsv,
  apiWalletMtnStatementReconcile,
  apiWalletScheduledReports,
  apiWalletScheduledReport,
  apiWalletGenerateReport,
  apiWalletAuditLogs,
  apiWalletReportSettings,
  apiWalletUpdateReportSettings,
  apiWalletSendTestReportEmail,
  apiWalletReportOverview,
  apiWalletPaymentsAnalytics,
} from "../api/index.js";
import ProcessRefundForm from "./Forms/ProcessRefundForm.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import socket from "../utilities/socket";
import {
  stageTarget,
  formatStage,
  buildPayoutTimeline,
  formatTimeline,
  retryLabel,
  confirmationMeta,
  formatClock,
  computeClockSkew,
  patchRows,
  formatQueueStrip,
  mergeAuditTimeline,
  anyInFlight,
} from "../utilities/payoutTimeline.js";
import {
  MTN_FIELDS,
  MTN_FIELD_LABELS,
  mapMtnStatementColumns,
  normaliseMtnRows,
  mappingProblems,
  buildMismatchCsv,
} from "../utilities/mtnStatementImport.js";
import {
  MAX_RECIPIENTS,
  ATTACHMENT_KEYS,
  ATTACHMENT_LABELS,
  addRecipients,
  removeRecipient,
  parseRecipients,
  hourOptionsEAT,
  formatHourEAT,
  toSettingsForm,
  settingsPayload,
  diffSettings,
  formatSettingsDiff,
  formatMoneyShort,
  successRateOf,
} from "../utilities/reportSettings.js";
import ConfirmDialog, { useConfirmDialog } from "../components/ConfirmDialog";
import {
  C, S, fmtUGX, fmtNum, fmtDate, fmtDay, truncate, CellStack, VendorCell, fmtVendorType, DateTimeCell, PeriodCell,
  mtnFeeForAmount, localDateStr, quickRange, rangeDays, autoBucket, rangeToIso, StatCard,
} from "../components/wallet/walletUi";
import MoneyInTab from "../components/wallet/MoneyInTab";
// Tooltips on the settings edited here (float reserve, vendor contracts, report
// schedule) come from the Settings help registry (Settings/help/*.js).
import SettingHelp from "./Settings/settingsHelp";
import AppSelect from "../components/ui/AppSelect";
import CopyValue from "../components/ui/CopyValue";
import AppButton from "../components/ui/AppButton";
import AppTabs from "../components/ui/AppTabs";
import { Link } from "react-router-dom";
import { EFRIS_STATE_OPTIONS, describeEfrisState, efrisState } from "../utilities/efrisState.js";

// The manual and bulk payout routes answer 201 when at least one row queued
// and 400 when every row failed, but both carry the same { message, results }
// body. Axios throws on the 400, so the per-row reasons arrive on the error
// object. Only a body WITHOUT results is a real submission failure.
const payoutResultsFrom = (body) => (Array.isArray(body?.results) ? body.results : null);

// Payee type badge tone, shared by every payout table.
// Tab ids a ?tab= deep link may select (the strip below the cards).
const WALLET_TAB_IDS = ["disbursements", "moneyIn", "batches", "manual", "bulk", "commission", "reconciliation", "reports", "refund"];

const PAYEE_TYPE_TONE = { RIDER: "blue", PARTNER: "purple", VENDOR: "yellow", CUSTOMER: "green", STAFF: "yellow", EXTERNAL: "neutral" };

// Who may be paid for what. The wallet service owns this rule and serves it
// at GET /admin/payout-purposes; this is the shape an older wallet deploy
// implies (platform parties only), used until the live catalogue arrives.
const LEGACY_PAYOUT_PURPOSES = {
  payeeTypes:        ["RIDER", "VENDOR", "PARTNER", "CUSTOMER"],
  expensePayeeTypes: [],
  purposesByPayeeType: Object.fromEntries(["RIDER", "VENDOR", "PARTNER", "CUSTOMER"].map((t) => [t, ["PAYOUT", "REFUND", "BONUS", "CORRECTION", "PENALTY_REVERSAL", "OTHER"]])),
  defaultPurposeByPayeeType: { RIDER: "PAYOUT", VENDOR: "PAYOUT", PARTNER: "PAYOUT", CUSTOMER: "PAYOUT" },
  labels: {
    PAYOUT: "Payout (earnings)", REFUND: "Refund", BONUS: "Bonus", CORRECTION: "Correction",
    PENALTY_REVERSAL: "Penalty reversal", OTHER: "Other (reason required)",
  },
};
const PAYEE_TYPE_LABEL = { RIDER: "Rider", VENDOR: "Vendor", PARTNER: "Partner", CUSTOMER: "Customer", STAFF: "Staff", EXTERNAL: "External (any MTN number)" };
// Purposes that are a cost to Lusuku rather than money owed to a party.
const EXPENSE_PURPOSES = ["FACILITATION", "REIMBURSEMENT", "SALARY", "ALLOWANCE", "SUPPLIER_PAYMENT"];
const purposeTone = (purpose) =>
  purpose === "REFUND" ? "purple"
  : purpose === "BONUS" ? "green"
  : EXPENSE_PURPOSES.includes(purpose) ? "yellow"
  : purpose === "PAYOUT" || !purpose ? "neutral"
  : "blue";

// The note on a payout reaches the payee as the "Reason" line of MTN's SMS,
// after the 14-character Lusuku reference ("LSK-6DDE970D: "). MTN takes 160
// characters in payeeNote; what is left for the operator's words is this.
const PAYEE_NOTE_MAX = 140;

// Reconciliation discrepancy types, in plain words.
const DISCREPANCY_LABEL = {
  BALANCE_MISMATCH: "Balance short",
  UNRECORDED_INFLOW: "Money in not recorded",
  MISSING_CALLBACK: "No callback",
  STUCK_PROCESSING: "Stuck processing",
  ORPHAN_DISBURSEMENT: "Orphan payout",
  DUPLICATE_DISBURSEMENT: "Possible duplicate",
  COMMISSION_MISMATCH: "Commission mismatch",
  FEE_BAND_MISMATCH: "MTN fee band wrong",
};

// EFRIS identity in the three states ops tells apart (filing LIVE to URA, set
// up but not live yet, not set up at all) comes from utilities/efrisState.js.
// The EFRIS column, its filter and the Vendor tax settings page under Tax all
// read the same helper, so no two screens can disagree about who is live.

// Vendor Commission Contracts filters: one select each, as
// [value, label, test]. Adding a filter is one entry here. Country is added
// at runtime, only when the list spans more than one market.
const CONTRACT_FILTERS = [
  {
    key: "type",
    label: "All types",
    options: ["RESTAURANT", "RETAILER", "WHOLESALER", "HEALTHCARE", "BRAND"].map((t) => [t, fmtVendorType(t), (c) => c.vendorType === t]),
  },
  {
    key: "status",
    label: "Any contract status",
    options: [
      ["CONFIRMED", "Confirmed", (c) => c.commissionConfigured],
      ["REVIEW", "Review required", (c) => !c.commissionConfigured],
    ],
  },
  {
    key: "vat",
    label: "Any VAT status",
    options: [
      // `chargesVat` is the server's answer (registered AND a TIN on file).
      ["CHARGING", "Charging VAT", (c) => c.chargesVat === true],
      ["NO_TIN", "Registered, no TIN, so not charging", (c) => c.vatRegistered !== false && !c.vatRegistrationNumber],
      ["NOT_CHARGING", "Not VAT registered", (c) => c.vatRegistered === false],
    ],
  },
  {
    key: "efris",
    label: "Any EFRIS status",
    options: EFRIS_STATE_OPTIONS.map((o) => [o.key, o.label, (c) => efrisState(c) === o.key]),
  },
];
const EMPTY_CONTRACT_FILTERS = { q: "", type: "", country: "", status: "", vat: "", efris: "" };

const is404 = (err) => err?.response?.status === 404;
const REPORTS_NEED_WALLET = "Reports need the updated wallet service";
// Axios blob errors carry the JSON body as a Blob — read it before giving up.
const blobErrorMessage = async (err) => {
  const data = err?.response?.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try { const j = JSON.parse(await data.text()); return j.error || j.message || null; } catch { return null; }
  }
  return data?.error || data?.message || err?.message || null;
};
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
// Save an axios blob response, honouring a server Content-Disposition filename.
const saveBlobResponse = (res, fallbackName) => {
  const cd = res?.headers?.["content-disposition"] || "";
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  saveBlob(res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/csv;charset=utf-8" }), name);
};
const saveText = (text, filename, type = "text/csv;charset=utf-8") => saveBlob(new Blob(["\ufeff" + text], { type }), filename);
const fmtLatency = (sec) => (sec == null || !Number.isFinite(Number(sec))) ? "—" : formatCountdownSec(Number(sec));
const formatCountdownSec = (sec) => sec < 60 ? `${Math.round(sec)}s` : sec < 3600 ? `${Math.floor(sec / 60)}m ${String(Math.round(sec % 60)).padStart(2, "0")}s` : `${(sec / 3600).toFixed(1)}h`;
const fmtPct = (v) => (v == null || !Number.isFinite(Number(v))) ? "—" : `${(Number(v) <= 1 ? Number(v) * 100 : Number(v)).toFixed(1)}%`;
const shortHash = (h) => h ? `${String(h).slice(0, 8)}…${String(h).slice(-4)}` : "—";

// ── Reports v2 (Overview / Payouts / Payments / Scheduled & Email) ───────────
const REPORT_SECTIONS = [
  { id: "overview", label: "Overview",          icon: <FiLayers size={13} /> },
  { id: "payouts",  label: "Payouts",           icon: <FiFileText size={13} /> },
  { id: "payments", label: "Payments",          icon: <FiPieChart size={13} /> },
  { id: "schedule", label: "Scheduled & Email", icon: <FiMail size={13} /> },
];
const WINDOW_LABELS = { today: "Today", last7d: "Last 7 days", last30d: "Last 30 days" };
// Treasury RAG palette: the same three tones the hero strip paints.
const ragTone = (status) => status === "RED"
  ? { bg: "#fdecea", border: C.red,    fg: C.red,    label: "TOP-UP REQUIRED" }
  : status === "AMBER"
    ? { bg: "#fef7e6", border: C.orange, fg: C.orange, label: "FLOAT RUNNING LOW" }
    : { bg: "#eef7f1", border: C.teal,   fg: C.teal,   label: "FLOAT HEALTHY" };
const fmtHours = (h) => (h == null || !Number.isFinite(Number(h))) ? "—" : Number(h) < 48 ? `${Number(h).toFixed(1)} h` : `${(Number(h) / 24).toFixed(1)} d`;
const NOTICE_STYLE = { background: C.orangeSoft, border: "1px solid rgba(246,150,30,0.4)", borderRadius: C.radius, padding: "10px 16px", marginBottom: 16, fontSize: 12, color: C.orange, display: "flex", alignItems: "center", gap: 8 };
const LOCK_STYLE   = { background: "#f0f4fa", border: `1px solid ${C.border}`, borderRadius: C.radiusSm, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: C.textMid, display: "flex", alignItems: "center", gap: 8 };
const CHECK_LABEL  = { fontSize: 12, color: C.textMid, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" };

const STATUS_META = {
  SUCCESSFUL: { color: "green", icon: <FiCheckCircle size={13} /> },
  FAILED:     { color: "red",   icon: <FiXCircle size={13} /> },
  PROCESSING: { color: "yellow", icon: <FiClock size={13} /> },
  QUEUED:     { color: "purple", icon: <FiClock size={13} /> },
  VALIDATING: { color: "purple", icon: <FiSend size={13} /> },
  PENDING:    { color: "purple", icon: <FiClock size={13} /> },
};

const CB_META = {
  CLOSED:    { color: "green",  label: "CLOSED",   icon: <FiShield size={13} /> },
  OPEN:      { color: "red",    label: "OPEN",      icon: <FiAlertOctagon size={13} /> },
  HALF_OPEN: { color: "yellow", label: "HALF-OPEN", icon: <FiActivity size={13} /> },
};

const BATCH_STATUS_META = {
  PREPARING:          { color: "purple" },
  AWAITING_APPROVAL:  { color: "yellow" },
  APPROVED:           { color: "blue"   },
  PROCESSING:         { color: "yellow" },
  COMPLETED:          { color: "green"  },
  PARTIALLY_COMPLETED:{ color: "yellow" },
  FAILED:             { color: "red"    },
  REJECTED:           { color: "red"    },
  CANCELLED:          { color: "purple" },
};

// ── Shared 1 s ticker ────────────────────────────────────────────────────────
// One interval for the whole page (not one per row). `active` lets the caller
// stop it entirely when nothing on screen is counting.
function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

// Server-aligned "now" for a scheduler snapshot `{ data, receivedAt, skewMs }`.
const serverNow = (scheduler, now) => now + (scheduler?.skewMs || 0);

// What an in-flight row is waiting for, driven by the pure stageTarget().
// Presentational only: never wraps, never runs its own timer.
function StageTimer({ row, scheduler, now }) {
  const st = stageTarget(row, scheduler?.data || null, serverNow(scheduler, now));
  if (!st) return null;
  const due = st.targetMs != null && st.remainingMs <= 0;
  const color = st.stage === "PROCESSING" ? C.orange : C.purple;
  return (
    <span
      title={st.hint || undefined}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color, fontWeight: 600, whiteSpace: "nowrap" }}
    >
      <span
        className={due || st.targetMs == null ? "pulse-dot" : undefined}
        style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block" }}
      />
      {formatStage(st)}
    </span>
  );
}

// "Queued 15:59:12 → MTN 16:04:51 (+5m39s) → ✓ 16:06:40 (+1m49s · poll)"
function TimelineLine({ row }) {
  const line = formatTimeline(buildPayoutTimeline(row));
  const retry = retryLabel(row);
  if (!line && !retry) return null;
  return (
    <div style={{ ...S.cellMeta, lineHeight: 1.4, whiteSpace: "normal" }}>
      {line}
      {retry && <div style={{ color: C.orange, fontWeight: 600 }}>{retry}</div>}
    </div>
  );
}

// CONFIRMATION cell chip (resolvedVia-aware, legacy-safe).
function ConfirmationChip({ row }) {
  const { label, tone } = confirmationMeta(row);
  const style = {
    green:  { bg: "#e6faf7", color: "#00875a", icon: <FiCheckCircle size={11} /> },
    red:    { bg: "#fff0f3", color: "#c0152f", icon: <FiXCircle size={11} /> },
    yellow: { bg: "#fff8e1", color: "#b76e00", icon: <FiClock size={11} /> },
    grey:   { bg: "#f4f6fb", color: C.textLight, icon: null },
  }[tone];
  // A label, not a control: tinted, no border, so it is never read as one of
  // the filled navy buttons beside it.
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: style.bg, color: style.color, whiteSpace: "nowrap",
    }}>
      {style.icon} {label}
    </span>
  );
}

// Hero queue line. Owns its own ticker so the rest of the page only re-renders
// per second while a row is actually in flight.
function QueueStrip({ scheduler }) {
  const now = useNow(!!scheduler?.data);
  if (!scheduler?.data) return null;
  const text = formatQueueStrip(scheduler.data, serverNow(scheduler, now));
  const q = scheduler.data.queue || {};
  return (
    <div style={{ flexBasis: "100%", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "rgba(255,255,255,0.85)", marginTop: 4, flexWrap: "wrap" }}>
      <FiActivity size={12} style={{ opacity: 0.8 }} />
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{text}</span>
      {Number(q.queuedDeferred) > 0 && (
        <span style={{ opacity: 0.7 }}>· {q.queuedDeferred} awaiting retry</span>
      )}
      {Number(q.processing) > 0 && (
        <span style={{ opacity: 0.7 }}>· {q.processing} at MTN</span>
      )}
      {scheduler.data.instanceId && (
        <span style={{ opacity: 0.5, fontSize: 11, marginLeft: "auto" }}>{scheduler.data.instanceId}</span>
      )}
    </div>
  );
}

// The "Audit" control on every disbursement row. A real (filled) row button,
// not blue text: on this dashboard a control that does something is a filled
// navy rectangle, and text that looks like body text is read as body text.
function AuditLink({ onClick }) {
  return (
    <AppButton size="sm" variant="quiet" onClick={onClick} title="Open the audit trail for this disbursement">
      <FiClipboard size={11} /> Audit
    </AppButton>
  );
}

// ── Reports tab presentational pieces ────────────────────────────────────────
function ReportTile({ label, value, sub, color }) {
  return (
    <div style={{ flex: "1 1 150px", minWidth: 150, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ ...S.statLabel, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || C.text, letterSpacing: "-0.3px", fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// key / count / amount / fees breakdown (byStatus, byTriggerType, …)
function DimTable({ title, rows, keyLabel = "Key", emptyText = "No rows in this period" }) {
  const list = Array.isArray(rows) ? rows : [];
  const total = list.reduce((a, r) => a + (Number(r.count) || 0), 0);
  return (
    <div style={{ flex: "1 1 260px", minWidth: 240 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>{title}</div>
      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: C.textLight }}>{emptyText}</div>
      ) : (
        <table style={{ ...S.table, fontSize: 12 }}>
          <thead><tr>
            <th style={S.th}>{keyLabel}</th>
            <th style={{ ...S.th, textAlign: "right" }}>Count</th>
            <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
            <th style={{ ...S.th, textAlign: "right" }}>Fees</th>
          </tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={String(r.key ?? r.reason ?? "—")}>
                <td style={{ ...S.td, padding: "7px 10px", fontWeight: 600, color: C.text }}>{String(r.key ?? "—").replace(/_/g, " ")}
                  <span style={{ color: C.textLight, fontWeight: 400, marginLeft: 6, fontSize: 11 }}>{total ? `${Math.round((Number(r.count) || 0) / total * 100)}%` : ""}</span>
                </td>
                <td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Number(r.count || 0).toLocaleString()}</td>
                <td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Number(r.amount || 0).toLocaleString()}</td>
                <td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: C.textLight }}>{Number(r.fees || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Pure-CSS bars: amount per bucket, failed share in red. The key props let
// the Payments section draw collections (gross / commission) with the same bars.
function SeriesBars({ series, bucket, valueKey = "amount", unit = "payouts", subKey = "fees", subLabel = "fees", failedKey = "failed" }) {
  const list = Array.isArray(series) ? series : [];
  if (!list.length) return <div style={{ fontSize: 12, color: C.textLight }}>No activity in this period</div>;
  const max = Math.max(1, ...list.map((b) => Number(b[valueKey]) || 0));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 120, overflowX: "auto", paddingBottom: 18, position: "relative" }}>
      {list.map((b) => {
        const amt = Number(b[valueKey]) || 0;
        const h = Math.max(2, Math.round((amt / max) * 100));
        const failedShare = failedKey && Number(b.count) > 0 ? Math.min(1, (Number(b[failedKey]) || 0) / Number(b.count)) : 0;
        return (
          <div key={b.bucket} title={`${b.bucket}\n${Number(b.count || 0)} ${unit} · UGX ${amt.toLocaleString()}\n${subLabel} ${Number(b[subKey] || 0).toLocaleString()}${failedKey ? ` · failed ${Number(b[failedKey] || 0)}` : ""}`}
            style={{ flex: "1 0 14px", maxWidth: 40, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", position: "relative" }}>
            <div style={{ width: "100%", height: `${h}%`, background: C.blue, borderRadius: "3px 3px 0 0", position: "relative", overflow: "hidden", opacity: amt ? 1 : 0.25 }}>
              {failedShare > 0 && <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: `${Math.round(failedShare * 100)}%`, background: C.red }} />}
            </div>
            <div style={{ position: "absolute", bottom: -16, fontSize: 11, color: C.textLight, whiteSpace: "nowrap" }}>
              {bucket === "day" ? String(b.bucket).slice(5) : String(b.bucket).slice(2)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Overview: one EAT window (Today / 7 d / 30 d) of disbursements + collections.
function WindowTile({ label, win }) {
  const d = win?.disbursements || {};
  const c = win?.collections || {};
  const rate = successRateOf(d);
  return (
    <div style={{ flex: "1 1 220px", minWidth: 200, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ ...S.statLabel, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: C.blue, letterSpacing: "-0.3px", fontVariantNumeric: "tabular-nums" }} title={fmtUGX(d.amount)}>{formatMoneyShort(d.amount)}</div>
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
        {Number(d.count || 0).toLocaleString()} payout{Number(d.count) === 1 ? "" : "s"} · fees {formatMoneyShort(d.fees)}
      </div>
      <div style={{ fontSize: 11, marginTop: 4, color: Number(d.failedCount) > 0 ? C.orange : C.teal, fontWeight: 600 }}>
        {rate == null ? "no payouts" : `${fmtPct(rate)} success`}{Number(d.failedCount) > 0 ? ` · ${Number(d.failedCount)} failed` : ""}
      </div>
      <div style={{ borderTop: `1px dashed ${C.border}`, marginTop: 8, paddingTop: 8, fontSize: 11, color: C.textMid }}>
        <span style={{ fontWeight: 600, color: C.text }}>Collections</span> <span title={fmtUGX(c.gross)}>{formatMoneyShort(c.gross)}</span>
        <span style={{ color: C.textLight }}> · {Number(c.count || 0).toLocaleString()} order{Number(c.count) === 1 ? "" : "s"} · net {formatMoneyShort(c.net)}</span>
      </div>
    </div>
  );
}

// Overview: what is moving right now, with the queue strip's own countdown.
function InFlightTile({ inFlight, scheduler }) {
  const now = useNow(!!scheduler?.data);
  const f = inFlight || {};
  const total = Number(f.queued || 0) + Number(f.validating || 0) + Number(f.processing || 0);
  const strip = scheduler?.data ? formatQueueStrip(scheduler.data, serverNow(scheduler, now)) : "";
  return (
    <div style={{ flex: "1 1 220px", minWidth: 200, background: total > 0 ? C.purpleSoft : C.inputBg, border: `1px solid ${total > 0 ? "rgba(112,72,232,0.35)" : C.border}`, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ ...S.statLabel, marginBottom: 6 }}>In flight now</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: total > 0 ? C.purple : C.text, fontVariantNumeric: "tabular-nums" }}>{total.toLocaleString()}</div>
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
        {Number(f.queued || 0)} queued · {Number(f.validating || 0)} validating · {Number(f.processing || 0)} at MTN
      </div>
      <div style={{ fontSize: 11, color: C.textMid, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
        {strip || "Queue timing needs the updated wallet service"}
      </div>
    </div>
  );
}

// Payments: key / count / money columns (byRail, byVendorType, byStatus).
function MoneyTable({ title, rows, keyLabel = "Key", cols, emptyText = "No rows in this period" }) {
  const list = Array.isArray(rows) ? rows : [];
  const total = list.reduce((a, r) => a + (Number(r.count) || 0), 0);
  return (
    <div style={{ flex: "1 1 260px", minWidth: 240 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>{title}</div>
      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: C.textLight }}>{emptyText}</div>
      ) : (
        <table style={{ ...S.table, fontSize: 12 }}>
          <thead><tr>
            <th style={S.th}>{keyLabel}</th>
            <th style={{ ...S.th, textAlign: "right" }}>Count</th>
            {cols.map(([k, label]) => <th key={k} style={{ ...S.th, textAlign: "right" }}>{label}</th>)}
          </tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={String(r.key ?? "—")}>
                <td style={{ ...S.td, padding: "7px 10px", fontWeight: 600, color: C.text }}>{String(r.key ?? "—").replace(/_/g, " ")}
                  <span style={{ color: C.textLight, fontWeight: 400, marginLeft: 6, fontSize: 11 }}>{total ? `${Math.round((Number(r.count) || 0) / total * 100)}%` : ""}</span>
                </td>
                <td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Number(r.count || 0).toLocaleString()}</td>
                {cols.map(([k]) => <td key={k} style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{Number(r[k] || 0).toLocaleString()}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Recipient chip with its remove control (Scheduled & Email settings card).
function RecipientChip({ email, onRemove, disabled }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.blueSoft, color: C.blue, borderRadius: 14, padding: "3px 6px 3px 10px", fontSize: 12, fontWeight: 600 }}>
      {email}
      <button
        type="button"
        aria-label={`Remove ${email}`}
        title="Remove"
        disabled={disabled}
        onClick={onRemove}
        style={{ border: "none", background: "transparent", color: C.blue, cursor: disabled ? "default" : "pointer", padding: 0, display: "inline-flex", opacity: disabled ? 0.4 : 1 }}
      >
        <FiX size={12} />
      </button>
    </span>
  );
}

export default function WalletDashboard() {
  const user = getUserFromLocalStorage();
  const isSuperUser = user?.result?.isSuperAdmin === true || user?.result?.user_role === "SUPERUSER";
  const isAdmin = isSuperUser || user?.result?.user_role === "ADMIN";
  const confirmDialog = useConfirmDialog();

  const [health, setHealth]           = useState(null);
  const [balance, setBalance]         = useState(null);
  const [summary, setSummary]         = useState(null);
  const [cb, setCb]                   = useState(null);
  const [proxyHealth, setProxyHealth] = useState(null);
  const [disbursements, setDisbursements] = useState([]);
  const [pagination, setPagination]   = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading]         = useState(true);
  const [polling, setPolling]         = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [searchId, setSearchId]       = useState("");
  const [page, setPage]               = useState(1);
  const [confirmPoll, setConfirmPoll] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [retryingId, setRetryingId]   = useState(null); // disbursementId being retried
  const autoRefreshRef    = useRef(null);
  const silentTickRef      = useRef(0);   // counts silent refresh cycles
  const backoffRef         = useRef(30000); // current poll interval ms (adaptive)
  // When any disbursement is still in-flight (QUEUED/PROCESSING) we poll much
  // faster so its terminal status + callback surface in near real-time instead
  // of waiting the full 30s cycle.
  const hasInFlightRef     = useRef(false);

  // ── Phase 4: Batch Payout state ────────────────────────────────────────────
  const [activeTab, setActiveTab]         = useState("disbursements"); // "disbursements" | "batches"
  // Deep links from the Settings menu (and anywhere else): /wallet-dashboard?tab=reports,
  // ?tab=moneyIn, ?tab=commission; `&open=float` also opens the float reserve editor.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab && WALLET_TAB_IDS.includes(tab)) setActiveTab(tab);
    if (params.get("open") === "float") setTimeout(() => openFloatModalRef.current?.(), 300);
  }, []);
  const openFloatModalRef = useRef(null);
  // Bumped by the MoMo Balance card so the Money In tab opens on everything.
  const [moneyInFocus, setMoneyInFocus]   = useState(0);

  // ── Live pipeline: scheduler snapshot + shared ticker ─────────────────────
  // { data:{serverTime,instanceId,jobs,queue}, receivedAt, skewMs } | null.
  // null also covers "old wallet, endpoint 404" — every new UI hides then.
  const [scheduler, setScheduler]       = useState(null);
  const schedulerUnavailableRef         = useRef(false); // 404 seen → stop retrying until the next full load
  const [currentDelayMs, setCurrentDelayMs] = useState(30000); // the poll delay actually scheduled
  const [hasInFlight, setHasInFlight]   = useState(false);
  const now = useNow(hasInFlight);       // one 1 s ticker for every row timer
  const activeTabRef          = useRef("disbursements");
  const manualHistoryQueryRef = useRef({ page: 1, filter: "" });
  const selectedBatchRef      = useRef(null);
  const batchInFlightRef      = useRef(false);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  const applySchedulerSnapshot = useCallback((data) => {
    if (!data || typeof data !== "object" || !data.jobs) return;
    const receivedAt = Date.now();
    schedulerUnavailableRef.current = false;
    // Socket snapshots carry jobs but not the queue aggregate — keep the last
    // queue block so the strip does not blink between "3 waiting" and nothing.
    setScheduler((prev) => ({
      data: { ...data, queue: data.queue || prev?.data?.queue || null },
      receivedAt,
      skewMs: computeClockSkew(data.serverTime, receivedAt),
    }));
  }, []);

  const fetchScheduler = useCallback(async () => {
    if (schedulerUnavailableRef.current) return;
    try {
      const r = await apiWalletScheduler();
      applySchedulerSnapshot(r.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        schedulerUnavailableRef.current = true;
        setScheduler(null);
      }
      // network / 5xx / 429: keep the last snapshot — countdowns keep ticking off it
    }
  }, [applySchedulerSnapshot]);

  // ── Reports tab state (report pack; wallet ≥ 2026-09) ─────────────────────
  const [reportRange, setReportRange]         = useState(() => quickRange("month"));
  const [reportSummary, setReportSummary]     = useState(null);
  const [reportLoading, setReportLoading]     = useState(false);
  const [reportsUnavailable, setReportsUnavailable] = useState(false); // 404 → old wallet
  const [reportError, setReportError]         = useState("");
  const [reportDownloading, setReportDownloading] = useState(null); // "register" | "ledger" | null
  // MTN statement reconcile
  const [mtnFileName, setMtnFileName]         = useState("");
  const [mtnHeaders, setMtnHeaders]           = useState([]);
  const [mtnRawRows, setMtnRawRows]           = useState([]);
  const [mtnMapping, setMtnMapping]           = useState(null);
  const [mtnUseRange, setMtnUseRange]         = useState(false);
  const [mtnReconciling, setMtnReconciling]   = useState(false);
  const [mtnResult, setMtnResult]             = useState(null);
  const [mtnResultTab, setMtnResultTab]       = useState("matched");
  const mtnFileRef = useRef();
  // Scheduled (hash-chained) reports
  const [scheduledReports, setScheduledReports]   = useState([]);
  const [scheduledLoading, setScheduledLoading]   = useState(false);
  const [scheduledChain, setScheduledChain]       = useState({}); // reportId → chainValid
  const [scheduledDetail, setScheduledDetail]     = useState(null);
  const [scheduledDetailLoading, setScheduledDetailLoading] = useState(false);
  const [verifyingChain, setVerifyingChain]       = useState(false);
  const [generateKind, setGenerateKind]           = useState("DAILY");
  const [generateStart, setGenerateStart]         = useState("");
  const [generateEnd, setGenerateEnd]             = useState("");
  const [generating, setGenerating]               = useState(false);
  const [reportReadyTick, setReportReadyTick]     = useState(0); // bumped by disbursement:reportReady
  // Audit drawer
  const [auditRow, setAuditRow]                   = useState(null);
  const [auditLogs, setAuditLogs]                 = useState([]);
  const [auditLoading, setAuditLoading]           = useState(false);
  const [auditUnavailable, setAuditUnavailable]   = useState(false);

  // ── Reports v2: inner sections, overview, payments analytics, schedule settings
  const [reportSection, setReportSection]         = useState("overview"); // overview | payouts | payments | schedule
  const [overview, setOverview]                   = useState(null);
  const [overviewLoading, setOverviewLoading]     = useState(false);
  const [overviewUnavailable, setOverviewUnavailable] = useState(false); // 404 → old wallet
  const overviewUnavailableRef                    = useRef(false);
  const [overviewError, setOverviewError]         = useState("");
  const [payments, setPayments]                   = useState(null);
  const [paymentsLoading, setPaymentsLoading]     = useState(false);
  const [paymentsUnavailable, setPaymentsUnavailable] = useState(false);
  const [paymentsError, setPaymentsError]         = useState("");
  const [settingsForm, setSettingsForm]           = useState(() => toSettingsForm(null));
  const [settingsSaved, setSettingsSaved]         = useState(null);  // last copy from the wallet (dirty flag + diff toast)
  const [settingsMeta, setSettingsMeta]           = useState(null);  // { source, envLocks }
  const [settingsLoading, setSettingsLoading]     = useState(false);
  const [settingsSaving, setSettingsSaving]       = useState(false);
  const [settingsUnavailable, setSettingsUnavailable] = useState(false);
  const [recipientDraft, setRecipientDraft]       = useState("");
  const [recipientError, setRecipientError]       = useState("");
  const [testEmailTo, setTestEmailTo]             = useState("");
  const [testEmailSending, setTestEmailSending]   = useState(false);
  const [testEmailResult, setTestEmailResult]     = useState(null);  // { recipients, attachments, at } | { error }

  // One cheap call; the deep tick re-runs it silently while the tab shows.
  const loadOverview = useCallback(async ({ silent = false } = {}) => {
    if (silent && overviewUnavailableRef.current) return;
    if (!silent) { setOverviewLoading(true); setOverviewError(""); }
    try {
      const r = await apiWalletReportOverview();
      setOverview(r.data || null);
      overviewUnavailableRef.current = false;
      setOverviewUnavailable(false);
    } catch (err) {
      if (is404(err)) { overviewUnavailableRef.current = true; setOverviewUnavailable(true); }
      else if (!silent) setOverviewError(err?.response?.data?.error || err?.message || "Could not load the overview");
    } finally {
      if (!silent) setOverviewLoading(false);
    }
  }, []);

  // ── Treasury strip: MTN float vs obligations (go-live guardrail) ──────────
  const [treasury, setTreasury] = useState(null);
  const loadTreasury = useCallback(async () => {
    try {
      const res = await apiWalletTreasury();
      setTreasury(res.data);
    } catch {
      /* older wallet deploys have no treasury endpoint — the strip hides itself */
    }
  }, []);

  // The MTN band table, from the wallet service. Every fee estimate on this
  // page (manual rows, totals, float pre-check) reads it through estimateFee.
  const [feeBands, setFeeBands] = useState(null);
  useEffect(() => {
    apiWalletFeeBands()
      .then((res) => setFeeBands(res.data?.bands || null))
      .catch(() => { /* older wallet deploys: no estimate rather than a wrong one */ });
  }, []);
  const estimateFee = useCallback((amount) => mtnFeeForAmount(feeBands, amount) ?? 0, [feeBands]);

  // Payee types and the purposes each allows, from the wallet service.
  const [payoutPurposes, setPayoutPurposes] = useState(LEGACY_PAYOUT_PURPOSES);
  useEffect(() => {
    apiWalletPayoutPurposes()
      .then((res) => { if (res.data?.payeeTypes?.length) setPayoutPurposes(res.data); })
      .catch(() => { /* older wallet deploys: platform parties only */ });
  }, []);
  const isExpenseType   = (type) => (payoutPurposes.expensePayeeTypes || []).includes(type);
  const purposesFor     = (type) => payoutPurposes.purposesByPayeeType?.[type] || ["PAYOUT"];
  const defaultPurpose  = (type) => payoutPurposes.defaultPurposeByPayeeType?.[type] || purposesFor(type)[0];
  const purposeLabel    = (purpose) => payoutPurposes.labels?.[purpose] || LEGACY_PAYOUT_PURPOSES.labels[purpose] || purpose;
  // A row knows who it pays: a searched platform party, or an external payee named by hand.
  const rowPayeeKnown   = (row) => row.payeeType === "EXTERNAL" ? row.payeeName.trim().length >= 2 : !!row.payeeId;
  // The same rule the wallet service applies (payoutPurposeProblem), so the
  // form can say what is missing before the request is sent.
  const rowReasonProblem = (row) => {
    const note = row.note.trim();
    if (isExpenseType(row.payeeType) && note.length < 5) return "A reason of at least 5 characters is required: it reaches the payee and the books";
    if (row.purpose === "OTHER" && !note) return "A reason is required when purpose is Other";
    if (row.purpose === "REFUND" && !row.linkedOrderId) return "Select the linked order for a refund";
    return null;
  };
  useEffect(() => {
    loadTreasury();
    const t = setInterval(loadTreasury, 120000); // refresh every 2 min
    return () => clearInterval(t);
  }, [loadTreasury]);

  // ── Record treasury movements (FW settlement / float top-up) ──────────────
  const [treasuryModal, setTreasuryModal] = useState(null); // null | "fw" | "topup"
  const EMPTY_TREASURY_FORM = { gross: "", fwFees: "", amount: "", reference: "", note: "", occurredAt: null, coversDay: null };
  const [treasuryForm, setTreasuryForm] = useState(EMPTY_TREASURY_FORM);
  const [treasurySaving, setTreasurySaving] = useState(false);

  // ── Live float controls ──────────────────────────────────────────────────
  // The reserve is a treasury control, not a constant: it must be correctable
  // at the moment it is found to be wrong, not one redeploy later. Writes are
  // super-admin only (the server enforces this; we mirror it to keep the UI
  // honest rather than offering a button that 403s).
  const isSuperAdminUser = (() => {
    const r = user?.result || {};
    const role = String(r.user_role || r.role || "").toUpperCase();
    return r.isSuperAdmin === true || role === "SUPERUSER" || role === "SUPERADMIN";
  })();
  const [floatModal, setFloatModal] = useState(false);
  const [floatForm, setFloatForm] = useState({ watermark: "", enforced: true, reason: "" });
  const [floatSaving, setFloatSaving] = useState(false);
  // Set when a submission is stopped by the reserve. Carries the real numbers
  // so the dialog can explain itself and offer a way forward.
  const [floatBlock, setFloatBlock] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  const openFloatModal = async () => {
    // Seed from the treasury snapshot so the modal opens instantly, then
    // reconcile against the authoritative settings endpoint. The strip only
    // polls every 2 minutes and the wallet service caches settings for 30s, so
    // without this refresh the form can open on a stale reserve, and saving it
    // would silently revert a change another admin made moments ago.
    setFloatForm({
      watermark: treasury?.watermark != null ? String(treasury.watermark) : "",
      enforced: treasury?.enforced !== false,
      reason: "",
    });
    setFloatModal(true);
    try {
      const { data } = await apiWalletGetFloatSettings();
      setFloatForm((f) => ({
        ...f,
        watermark: data?.watermark != null ? String(data.watermark) : f.watermark,
        enforced: data?.enforced !== false,
      }));
    } catch (e) {
      // Non-fatal: the snapshot values already seeded the form.
    }
  };
  openFloatModalRef.current = openFloatModal;

  const submitFloatSettings = async () => {
    const n = Number(floatForm.watermark);
    if (!Number.isFinite(n) || n < 0) {
      notify.error("The reserve must be zero or a positive number.");
      return;
    }
    setFloatSaving(true);
    try {
      const res = await apiWalletUpdateFloatSettings({
        watermark: Math.floor(n),
        enforced: !!floatForm.enforced,
        reason: floatForm.reason.trim() || undefined,
      });
      notify.success(
        `Reserve is now UGX ${Number(res.data.watermark).toLocaleString()}` +
        `${res.data.enforced ? "" : " (enforcement off)"}`
      );
      setFloatModal(false);
      setFloatBlock(null);
      await loadTreasury();
    } catch (err) {
      notify.fromError(err, "Could not update the float settings");
    } finally {
      setFloatSaving(false);
    }
  };
  // `prefill` comes from the Money In tab's "not in the books" panel: the
  // amount the wallet grew by, the day it happened and the day it explains.
  // The reference still has to be typed; it is what keeps the entry unique.
  const openTreasuryModal = (kind, prefill = null) => {
    setTreasuryForm({
      ...EMPTY_TREASURY_FORM,
      ...(prefill ? { amount: String(prefill.amount ?? ""), note: prefill.note || "", occurredAt: prefill.occurredAt || null, coversDay: prefill.coversDay || null } : {}),
    });
    setTreasuryModal(kind);
  };
  const submitTreasuryMove = async () => {
    const ref = treasuryForm.reference.trim();
    if (ref.length < 3) {
      notify.error("A statement/transfer reference is required (used for idempotency + audit)");
      return;
    }
    const isFw = treasuryModal === "fw";
    const mainAmount = Number(isFw ? treasuryForm.gross : treasuryForm.amount);
    if (!mainAmount || mainAmount <= 0) {
      notify.error(isFw ? "Gross settlement amount must be positive" : "Top-up amount must be positive");
      return;
    }
    setTreasurySaving(true);
    try {
      const res = isFw
        ? await apiWalletRecordFwSettlement({
            gross: mainAmount,
            fwFees: Number(treasuryForm.fwFees) || 0,
            reference: ref,
            note: treasuryForm.note.trim(),
          })
        : await apiWalletRecordFloatTopup({
            amount: mainAmount,
            reference: ref,
            note: treasuryForm.note.trim(),
            occurredAt: treasuryForm.occurredAt || undefined,
            coversDay: treasuryForm.coversDay || undefined,
          });
      notify.success(res.data.message || "Recorded");
      setTreasuryModal(null);
      loadTreasury();
    } catch (err) {
      notify.fromError(err, "Failed to record treasury movement");
    } finally {
      setTreasurySaving(false);
    }
  };
  const [batches, setBatches]             = useState([]);
  const [batchPagination, setBatchPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [batchPage, setBatchPage]         = useState(1);
  const [batchLoading, setBatchLoading]   = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetail, setBatchDetail]     = useState(null);
  const [batchDetailLoading, setBatchDetailLoading] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(null); // batchId
  const [confirmReject, setConfirmReject]   = useState(null); // batchId
  const [rejectReason, setRejectReason]     = useState("");
  const [initiateModal, setInitiateModal]   = useState(null); // "rider" | "partner"
  const [initPeriodStart, setInitPeriodStart] = useState("");
  const [initPeriodEnd, setInitPeriodEnd]     = useState("");
  const [batchActionLoading, setBatchActionLoading] = useState(false);

  // ── Batch filter state ────────────────────────────────────────────────────
  const [batchSearchId,     setBatchSearchId]     = useState("");
  const [batchFilterStatus, setBatchFilterStatus] = useState("");
  const [batchFilterType,   setBatchFilterType]   = useState("");

  // Derived: how many batches are sitting at AWAITING_APPROVAL
  const pendingApprovalCount = batches.filter((b) => b.status === "AWAITING_APPROVAL").length;

  // ── Reconciliation state (Phase 5) ───────────────────────────────────────
  const [reconReports,     setReconReports]     = useState([]);
  const [reconPagination,  setReconPagination]  = useState({ page: 1, pages: 1, total: 0 });
  const [reconPage,        setReconPage]        = useState(1);
  const [reconLoading,     setReconLoading]     = useState(false);
  const [reconStats,       setReconStats]       = useState(null);
  const [reconFilterStatus,    setReconFilterStatus]    = useState("");
  const [reconFilterDiscrepancy, setReconFilterDiscrepancy] = useState("");
  const [reconRunModal,    setReconRunModal]    = useState(false);
  const [reconPeriodStart, setReconPeriodStart] = useState("");
  const [reconPeriodEnd,   setReconPeriodEnd]   = useState("");
  const [reconRunLoading,  setReconRunLoading]  = useState(false);
  const [reconDetail,      setReconDetail]      = useState(null); // selected report
  const [resolveModal,     setResolveModal]     = useState(null); // { reportId, discrepancyId, description }
  const [resolveNote,      setResolveNote]      = useState("");
  const [resolveLoading,   setResolveLoading]   = useState(false);

  // ── Company commission reporting + vendor contracts ─────────────────────
  const [commissionReport, setCommissionReport] = useState(null);
  const [commissionContracts, setCommissionContracts] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionType, setCommissionType] = useState("");
  const [commissionStart, setCommissionStart] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [commissionEnd, setCommissionEnd] = useState(new Date().toISOString().slice(0, 10));
  const [commissionEdit, setCommissionEdit] = useState(null);
  const [commissionEditRate, setCommissionEditRate] = useState("");
  const [commissionEditReason, setCommissionEditReason] = useState("");
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [contractFilters, setContractFilters] = useState(EMPTY_CONTRACT_FILTERS);
  // VAT registration (WIRE_CONTRACT §17.2): a vendor-level compliance fact,
  // administered on the same row as the commission contract.
  const [vatEdit, setVatEdit] = useState(null);
  const [vatEditRegistered, setVatEditRegistered] = useState(true);
  const [vatEditNumber, setVatEditNumber] = useState("");
  const [vatSaving, setVatSaving] = useState(false);
  // EFRIS identity (§17.3) is READ here and edited under Tax, Vendor tax
  // settings, through the one shared editor (components/fiscal/
  // EfrisIdentityEditor.js). It used to be a third modal on this table; two
  // copies of the form that decides whether a sale is filed to URA for real
  // was one too many.

  // ── Bulk Upload state ────────────────────────────────────────────────────
  const [bulkFile,          setBulkFile]          = useState(null);
  const [bulkRows,          setBulkRows]          = useState(null);   // parsed preview rows from validate
  const [bulkSummary,       setBulkSummary]       = useState(null);   // { total, valid, invalid, totalAmount, canProceed }
  const [bulkValidating,    setBulkValidating]    = useState(false);
  const [bulkSubmitting,    setBulkSubmitting]    = useState(false);
  const [bulkResults,       setBulkResults]       = useState(null);   // post-submit results
  const [bulkError,         setBulkError]         = useState("");
  const bulkFileRef = useRef();
  // The sheet's rows as sent to validation, kept so "Check with MTN again"
  // can re-run the dry run (and the MTN payee check) without a re-upload.
  const bulkPayoutsRef = useRef([]);

  // The one dry run: the wallet validates every row and asks MTN who each
  // number reaches. Nothing is written and no money moves.
  const runBulkValidate = async (payouts) => {
    bulkPayoutsRef.current = payouts;
    setBulkValidating(true);
    setBulkError("");
    try {
      const res = await apiWalletBulkPayoutValidate(payouts);
      setBulkRows(res.data.rows);
      setBulkSummary(res.data.summary);
    } catch (err) {
      setBulkError(err.response?.data?.error || "Validation request failed");
    } finally {
      setBulkValidating(false);
    }
  };

  // Generate and download a pre-formatted Excel template with dropdowns and instructions
  const downloadBulkTemplate = () => {
    const platformTypes   = (payoutPurposes.payeeTypes || []).filter((t) => !isExpenseType(t));
    const partyPurposes   = purposesFor(platformTypes[0] || "RIDER");
    const expensePurposes = (payoutPurposes.expensePayeeTypes || []).length ? purposesFor(payoutPurposes.expensePayeeTypes[0]) : [];
    const allPurposes     = Array.from(new Set([...partyPurposes, ...expensePurposes]));
    const wb = XLSX.utils.book_new();

    // ── PAYOUTS sheet ────────────────────────────────
    const headers = [
      "payeeType", "payeeId", "payeeName", "phone", "amount", "purpose", "note", "linkedOrderId",
    ];
    const examples = [
      ["RIDER",   "66f51168eb312b17f79c558a", "John Rider",          "256779991454", 15000, "PAYOUT",  "April earnings",    ""],
      ["VENDOR",  "669fdbccb0f4117fc8938aef", "Suzenda Restaurant",  "256700123456", 250000, "PAYOUT", "",                  ""],
      ["CUSTOMER","64a00a0f3ad4800002694b4e", "Jane Customer",       "256756001234", 8000,  "REFUND",  "Order refund",      "ORDER-ID-HERE"],
    ];

    const wsData = [headers, ...examples];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Column widths
    ws["!cols"] = [
      { wch: 12 }, // payeeType
      { wch: 28 }, // payeeId
      { wch: 24 }, // payeeName
      { wch: 16 }, // phone
      { wch: 12 }, // amount
      { wch: 20 }, // purpose
      { wch: 30 }, // note
      { wch: 28 }, // linkedOrderId
    ];

    // Freeze header row
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    // Data validation: payeeType dropdown (column A, rows 2-201)
    ws["!dataValidation"] = ws["!dataValidation"] || [];
    ws["!dataValidation"].push({
      sqref: "A2:A201",
      type: "list",
      formula1: `"${(payoutPurposes.payeeTypes || []).join(",")}"`,
      showDropDown: false,
      showErrorMessage: true,
      errorTitle: "Invalid payeeType",
      error: `Must be one of ${(payoutPurposes.payeeTypes || []).join(", ")}`,
    });

    // Data validation: purpose dropdown (column F, rows 2-201)
    ws["!dataValidation"].push({
      sqref: "F2:F201",
      type: "list",
      formula1: `"${allPurposes.join(",")}"`,
      showDropDown: false,
      showErrorMessage: true,
      errorTitle: "Invalid purpose",
      error: `Must be one of ${allPurposes.join(", ")}`,
    });

    XLSX.utils.book_append_sheet(wb, ws, "Payouts");

    // ── INSTRUCTIONS sheet ───────────────────────────
    const instructions = [
      ["LUSUKU BULK PAYOUT TEMPLATE — Instructions"],
      [""],
      ["HOW TO USE"],
      ["1. Fill in the 'Payouts' sheet. Do not rename or delete columns."],
      ["2. Delete the 3 example rows before uploading."],
      ["3. Upload the file in the Wallet Dashboard → Bulk Upload tab."],
      ["4. Review the validation preview — ALL rows must pass before payments are queued."],
      ["5. Confirm to queue payments. Payments are submitted to MTN MoMo within seconds (at most 5 minutes)."],
      [""],
      ["COLUMN REFERENCE"],
      ["Column",       "Required", "Allowed Values / Notes"],
      ["payeeType",    "YES",      `${(payoutPurposes.payeeTypes || []).join(" | ")}  (dropdown)`],
      ["payeeId",      "YES*",     "MongoDB ObjectId of the payee, copied from the dashboard. Leave empty for EXTERNAL (anyone with an MTN number)"],
      ["payeeName",    "YES",      "Display name for the audit trail and the books"],
      ["phone",        "YES",      "MTN MoMo number to receive funds, e.g. 256779991454"],
      ["amount",       "YES",      "UGX amount. Minimum 500. No commas or currency symbols."],
      ["purpose",      "No",       `Parties (${platformTypes.join(", ")}): ${partyPurposes.join(" | ")}. Staff and external: ${expensePurposes.join(" | ")}`],
      ["note",         "No*",      "The reason. Required (5+ characters) for STAFF and EXTERNAL payees and when purpose = OTHER; reaches the payee's MTN SMS"],
      ["linkedOrderId","No",       "Required if purpose = REFUND — paste the Order ID"],
      [""],
      ["SAFETY RULES"],
      ["- All rows are validated before ANY payment is queued."],
      ["- If even one row is invalid, the entire batch is blocked until fixed."],
      ["- Minimum UGX 500 per row. Maximum 200 rows per upload."],
      ["- Each row has an idempotency key — double-uploading the same file will NOT duplicate payments."],
      ["- Payments are processed via MTN MoMo and cannot be reversed once SUCCESSFUL."],
    ];
    const wsInfo = XLSX.utils.aoa_to_sheet(instructions);
    wsInfo["!cols"] = [{ wch: 30 }, { wch: 12 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsInfo, "Instructions");

    XLSX.writeFile(wb, "Lusuku_Bulk_Payout_Template.xlsx");
  };

  // ── Manual Payout state ───────────────────────────────────────────────────
  const emptyRow = () => ({
    // Entity identity (filled via search, not raw typing)
    payeeType:         "RIDER",
    payeeId:           "",
    payeeName:         "",
    registeredPhone:   "",    // phone on file — shown for reference
    phone:             "",    // actual MTN number to send to (editable, may differ)
    phoneWasOverridden: false,
    // Payment context
    purpose:           "PAYOUT",
    linkedOrderId:     "",    // only required for REFUND
    linkedOrderLabel:  "",    // display string for the selected order
    amount:            "",
    note:              "",
    // Search UI state (not sent to API)
    _searchQuery:      "",
    _searchResults:    [],
    _searchLoading:    false,
    _orderQuery:       "",
    _orderResults:     [],
    _orderLoading:     false,
    // MTN's answer for `phone`: who the money would reach. Filled by the
    // payee lookup (debounced, per row) and required before Send is enabled.
    // { status: loading|ok|error, phone, active, registeredName, nameAvailable, message }
    _lookup:           null,
  });
  const [manualRows,          setManualRows]          = useState([emptyRow()]);
  const [manualLoading,       setManualLoading]       = useState(false);
  const [manualResults,       setManualResults]       = useState(null);  // null | results[]
  const [manualHistory,       setManualHistory]       = useState([]);
  const [manualHistoryPage,   setManualHistoryPage]   = useState(1);
  const [manualHistoryPages,  setManualHistoryPages]  = useState(1);
  const [manualHistoryTotal,  setManualHistoryTotal]  = useState(0);
  const [manualHistoryLoading,setManualHistoryLoading]= useState(false);
  const [manualHistoryFilter, setManualHistoryFilter] = useState("");
  const searchTimers = useRef({});  // debounce timers keyed by row index

  // ── Retry a FAILED disbursement ───────────────────────────────────────────
  const handleRetry = useCallback(async (disbursementId) => {
    setRetryingId(disbursementId);
    try {
      const res = await apiWalletRetryDisbursement(disbursementId);
      notify.success(res.data?.message || "Re-queued. Submitting to MTN shortly.");
      // Optimistically flip status to QUEUED in local state so the UI updates immediately
      setDisbursements((prev) =>
        prev.map((d) =>
          d.disbursementId === disbursementId
            ? { ...d, status: "QUEUED", failureReason: null }
            : d
        )
      );
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to retry disbursement";
      notify.error(msg);
    } finally {
      setRetryingId(null);
    }
  }, []);

  // ── Full load (initial + explicit refresh) ───────────────────────────────────
  // Fires all 5 endpoints sequentially with a 200 ms gap so the server
  // receives one request at a time instead of a 5-request burst.
  const load = useCallback(async (pg = 1, status = "", refId = "") => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (status) params.status = status;
      if (refId)  params.referenceId = refId;

      const delay = (ms) => new Promise((r) => setTimeout(r, ms));

      try { const r = await apiWalletHealth();                   setHealth(r.data); }    catch {}
      await delay(200);
      try { const r = await apiWalletGetBalance();               setBalance(r.data); }   catch {}
      await delay(200);
      try { const r = await apiWalletGetSummary();               setSummary(r.data); }   catch {}
      await delay(200);
      try { const r = await apiWalletGetCircuitBreaker();        setCb(r.data); }        catch {}
      await delay(200);
      try { const r = await apiWalletProxyHealth();               setProxyHealth(r.data); } catch {}
      await delay(200);
      try {
        const r = await apiWalletListDisbursements(params);
        setDisbursements(r.data.disbursements || []);
        setPagination(r.data.pagination || { page: 1, pages: 1, total: 0 });
      } catch {}
      await delay(200);
      schedulerUnavailableRef.current = false; // a full load re-probes the new endpoint
      await fetchScheduler();

      backoffRef.current = 30000; // reset backoff on successful full load
    } catch (err) {
      notify.error("Failed to load wallet data");
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [fetchScheduler]);

  // ── Silent background refresh ────────────────────────────────────────────────
  // Only fetches the 2 highest-churn endpoints per tick to minimise request
  // count. Every 5th tick (2.5 min) also refreshes the slower-changing data
  // (balance, summary, circuit-breaker). 429 response doubles the interval
  // up to a 5-minute ceiling so the dashboard degrades gracefully under load.
  const silentRefresh = useCallback(async (pg, status, refId) => {
    silentTickRef.current += 1;
    const tick    = silentTickRef.current;
    const isDeep  = tick % 5 === 0; // full refresh every 5th silent tick
    const params  = { page: pg, limit: 20 };
    if (status) params.status = status;
    if (refId)  params.referenceId = refId;

    let got429 = false;
    const safeGet = async (fn, setter) => {
      try {
        const r = await fn();
        setter(r.data);
      } catch (err) {
        if (err?.response?.status === 429) got429 = true;
      }
    };

    // Always refresh health + disbursement list (highest churn)
    await safeGet(apiWalletHealth, setHealth);
    await safeGet(
      () => apiWalletListDisbursements(params),
      (d) => {
        setDisbursements(d.disbursements || []);
        setPagination(d.pagination || { page: 1, pages: 1, total: 0 });
      }
    );

    // Live pipeline: the scheduler snapshot is one cheap aggregate — fetch it
    // every tick so "submits in m:ss" stays truthful (404 → off until next load).
    await fetchScheduler();

    // Whatever else is currently showing in-flight rows refreshes on the same
    // tick, so the Manual Payout history and an open batch modal move too.
    if (activeTabRef.current === "manual") {
      const { page: mp, filter: mf } = manualHistoryQueryRef.current;
      const mParams = { page: mp, limit: 20 };
      if (mf) mParams.status = mf;
      await safeGet(
        () => apiWalletListManualPayouts(mParams),
        (d) => {
          setManualHistory(d.records || []);
          setManualHistoryPages(d.pagination?.pages || 1);
          setManualHistoryTotal(d.pagination?.total || 0);
        }
      );
    }
    if (selectedBatchRef.current && batchInFlightRef.current) {
      const bid = selectedBatchRef.current;
      await safeGet(
        () => apiWalletGetBatch(bid),
        (d) => { if (selectedBatchRef.current === bid) setBatchDetail(d); }
      );
    }

    // Deep tick: also refresh balance, summary, circuit-breaker
    if (isDeep) {
      await safeGet(apiWalletGetBalance,      setBalance);
      await safeGet(apiWalletGetSummary,      setSummary);
      await safeGet(apiWalletGetCircuitBreaker, setCb);
      await safeGet(apiWalletProxyHealth, setProxyHealth);
      // Reports v2 overview rides the deep tick while the Reports tab is showing.
      if (activeTabRef.current === "reports") await loadOverview({ silent: true });
    }

    setLastUpdated(new Date());

    // Adaptive backoff: double interval on 429, reset to 30s on clean run
    if (got429) {
      backoffRef.current = Math.min(backoffRef.current * 2, 300000); // cap at 5 min
    } else {
      backoffRef.current = 30000;
    }
  }, [fetchScheduler, loadOverview]);

  useEffect(() => { load(page, filterStatus, searchId); }, [load, page, filterStatus]);

  // ── Real-time disbursement status updates via socket ─────────────────────
  useEffect(() => {
    const handler = (update) => {
      if (!update || !update.disbursementId) return;
      // Patch the row in-place wherever it is showing — the main list, the
      // Manual Payout history and an open batch detail. patchRows() keeps a
      // null in the payload from erasing a value we already have.
      setDisbursements((prev) => patchRows(prev, update));
      setManualHistory((prev) => patchRows(prev, update));
      setBatchDetail((prev) => {
        if (!prev || !Array.isArray(prev.disbursements)) return prev;
        const next = patchRows(prev.disbursements, update);
        return next === prev.disbursements ? prev : { ...prev, disbursements: next };
      });
      // Toast the admin with outcome
      const name = update.payeeName || update.payeePhone || update.disbursementId;
      if (update.event === "SUBMITTED") {
        notify.info(`Submitted to MTN · confirming… (${name})`, undefined, { autoClose: 3000, id: `submitted-${update.disbursementId}` });
      } else if (update.status === "SUCCESSFUL") {
        notify.success(`Disbursement to ${name} succeeded`, undefined, { id: `disbursement-ok-${update.disbursementId}` });
      } else if (update.status === "FAILED") {
        notify.error(`Disbursement to ${name} failed`, update.failureReason || "MTN error", { id: `disbursement-failed-${update.disbursementId}` });
      }
    };
    const schedulerHandler = (snapshot) => applySchedulerSnapshot(snapshot);
    const reportReadyHandler = (info) => {
      const kind = info?.kind ? String(info.kind).toLowerCase() : "scheduled";
      if (info?.test) {
        const n = Number(info.recipientCount);
        notify.info(`Test report email posted${Number.isFinite(n) ? ` to ${n} recipient${n === 1 ? "" : "s"}` : ""}`, undefined, { autoClose: 6000, id: `report-test-${info?.reportId || "x"}` });
        return;
      }
      notify.info(`Payout ${kind} report ready${info?.periodStart ? ` · ${String(info.periodStart).slice(0, 10)}` : ""}`, undefined, { autoClose: 6000, id: `report-${info?.reportId || "x"}` });
      setReportReadyTick((n) => n + 1);
    };
    // Join adminRoom so the MTN-callback broadcast actually reaches this client.
    // Without this, disbursement:statusUpdate (emitted to adminRoom) is never
    // received and the dashboard only updates on the slow poll cycle.
    const joinAdmin = () => socket.emit("joinAdmin");
    if (socket.connected) joinAdmin();
    socket.on("connect", joinAdmin);
    socket.on("disbursement:statusUpdate", handler);
    socket.on("disbursement:schedulerUpdate", schedulerHandler);
    socket.on("disbursement:reportReady", reportReadyHandler);
    return () => {
      socket.off("connect", joinAdmin);
      socket.off("disbursement:statusUpdate", handler);
      socket.off("disbursement:schedulerUpdate", schedulerHandler);
      socket.off("disbursement:reportReady", reportReadyHandler);
    };
  }, [applySchedulerSnapshot]);

  // Track whether anything on screen is still in-flight: the main list, the
  // Manual Payout history (when that tab is showing) and an open batch detail.
  // Drives both the fast poll cadence and the shared 1 s ticker.
  useEffect(() => {
    const inList   = anyInFlight(disbursements);
    const inManual = activeTab === "manual" && anyInFlight(manualHistory);
    const inBatch  = !!selectedBatch && anyInFlight(batchDetail?.disbursements);
    batchInFlightRef.current = inBatch;
    const any = inList || inManual || inBatch;
    hasInFlightRef.current = any;
    setHasInFlight(any);
  }, [disbursements, manualHistory, activeTab, selectedBatch, batchDetail]);
  useEffect(() => { selectedBatchRef.current = selectedBatch; }, [selectedBatch]);

  // Schedule next silent tick with the current backoff interval.
  // Uses recursive setTimeout (not setInterval) so the interval adapts
  // dynamically when backoffRef changes after a 429.
  useEffect(() => {
    let cancelled = false;
    const schedule = () => {
      // Poll fast (~6s) while a disbursement is in-flight; otherwise the
      // adaptive backoff interval (30s baseline). Never poll faster than the
      // 429 backoff floor.
      const delay = hasInFlightRef.current
        ? Math.min(backoffRef.current, 6000)
        : backoffRef.current;
      setCurrentDelayMs(delay); // the hero shows the cadence really in use
      autoRefreshRef.current = setTimeout(async () => {
        if (cancelled) return;
        await silentRefresh(page, filterStatus, searchId);
        schedule(); // schedule next tick using updated backoffRef
      }, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      clearTimeout(autoRefreshRef.current);
    };
    // hasInFlight is a dep on purpose: when a row goes in-flight (or the last
    // one settles) the pending timer is replaced so the new cadence applies now.
  }, [silentRefresh, page, filterStatus, searchId, hasInFlight]);

  const handlePollStuck = async () => {
    setConfirmPoll(false);
    setPolling(true);
    try {
      const res = await apiWalletPollStuck();
      const d = res.data;
      notify.success(`Poller complete. Resolved ${d.resolved || 0}, failed ${d.failed || 0}`);
      load(page, filterStatus, searchId);
    } catch (err) {
      notify.fromError(err, "Poll failed");
    } finally {
      setPolling(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load(1, filterStatus, searchId);
    setPage(1);
  };

  const handleStatusFilter = (s) => {
    setFilterStatus(s);
    setPage(1);
  };

  // ── Phase 4: Batch handlers ───────────────────────────────────────────────
  const loadBatches = useCallback(async (pg = 1, silent = false, searchId = "", statusF = "", typeF = "") => {
    if (!silent) setBatchLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (searchId) params.batchId   = searchId;
      if (statusF)  params.status    = statusF;
      if (typeF)    params.batchType = typeF;
      const res = await apiWalletListBatches(params);
      setBatches(res.data.batches || []);
      setBatchPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      if (!silent) notify.error("Failed to load batches");
    } finally {
      if (!silent) setBatchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "batches") loadBatches(batchPage, false, batchSearchId, batchFilterStatus, batchFilterType);
  }, [activeTab, batchPage, batchFilterStatus, batchFilterType, loadBatches]);

  const openBatchDetail = async (batchId) => {
    setSelectedBatch(batchId);
    setBatchDetail(null);
    setBatchDetailLoading(true);
    try {
      const res = await apiWalletGetBatch(batchId);
      setBatchDetail(res.data);
    } catch (err) {
      notify.error("Failed to load batch detail");
    } finally {
      setBatchDetailLoading(false);
    }
  };

  const handleApproveBatch = async () => {
    if (!confirmApprove) return;
    const batchIdToApprove = confirmApprove;
    setBatchActionLoading(true);
    try {
      await apiWalletApproveBatch(batchIdToApprove);
      notify.success("Batch approved and queued for processing");
      loadTreasury();
      setConfirmApprove(null);
      loadBatches(batchPage, false, batchSearchId, batchFilterStatus, batchFilterType);
      if (selectedBatch === batchIdToApprove) openBatchDetail(batchIdToApprove);
    } catch (err) {
      notify.fromError(err, "Approval failed");
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleRejectBatch = async () => {
    if (!confirmReject || !rejectReason.trim()) return;
    setBatchActionLoading(true);
    try {
      await apiWalletRejectBatch(confirmReject, rejectReason);
      notify.success("Batch rejected");
      setConfirmReject(null);
      setRejectReason("");
      loadBatches(batchPage);
      if (selectedBatch === confirmReject) { setSelectedBatch(null); setBatchDetail(null); }
    } catch (err) {
      notify.fromError(err, "Rejection failed");
    } finally {
      setBatchActionLoading(false);
    }
  };

  const handleBatchSearch = (e) => {
    e.preventDefault();
    setBatchPage(1);
    loadBatches(1, false, batchSearchId, batchFilterStatus, batchFilterType);
  };

  const handleBatchStatusFilter = (s) => {
    setBatchFilterStatus(s);
    setBatchPage(1);
  };

  const handleBatchTypeFilter = (t) => {
    setBatchFilterType(t);
    setBatchPage(1);
  };

  const clearBatchFilters = () => {
    setBatchSearchId("");
    setBatchFilterStatus("");
    setBatchFilterType("");
    setBatchPage(1);
    loadBatches(1, false, "", "", "");
  };

  const handleInitiateBatch = async () => {
    if (!initPeriodStart || !initPeriodEnd) return notify.error("Select period start and end");
    setBatchActionLoading(true);
    try {
      const fn = initiateModal === "rider" ? apiWalletInitiateRiderBatch : apiWalletInitiatePartnerBatch;
      const res = await fn(initPeriodStart, initPeriodEnd);
      const d = res.data;
      notify.success(
        `${initiateModal === "rider" ? "Rider" : "Partner"} batch initiated — ` +
        `${d.totalPayees} payees, awaiting approval`
      );
      setInitiateModal(null);
      setInitPeriodStart(""); setInitPeriodEnd("");
      loadBatches(1, false, batchSearchId, batchFilterStatus, batchFilterType); setBatchPage(1);
    } catch (err) {
      notify.fromError(err, "Failed to initiate batch");
    } finally {
      setBatchActionLoading(false);
    }
  };

  // ── Phase 5: Reconciliation handlers ─────────────────────────────────────
  const loadRecon = useCallback(async (pg = 1, statusF = "", discrepancyF = "") => {
    setReconLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (statusF)      params.status          = statusF;
      if (discrepancyF) params.hasDiscrepancy  = discrepancyF;
      const res = await apiWalletListReconciliation(params);
      setReconReports(res.data.reports || []);
      setReconPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
      setReconStats(res.data.stats || null);
    } catch (err) {
      notify.error("Failed to load reconciliation reports");
    } finally {
      setReconLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "reconciliation") loadRecon(reconPage, reconFilterStatus, reconFilterDiscrepancy);
  }, [activeTab, reconPage, reconFilterStatus, reconFilterDiscrepancy, loadRecon]);

  const loadCommissions = useCallback(async () => {
    setCommissionLoading(true);
    try {
      const params = { periodStart: commissionStart, periodEnd: commissionEnd };
      if (commissionType) params.vendorType = commissionType;
      const [reportRes, contractsRes] = await Promise.all([
        apiWalletGetCommissions(params),
        // Every contract, whatever the report's type picker says: the
        // contracts table has its own search and filters, and the report
        // looks vendor names up in this list.
        apiGetVendorCommissionContracts({}),
      ]);
      setCommissionReport(reportRes.data);
      setCommissionContracts(contractsRes.data.contracts || []);
    } catch (error) {
      notify.fromError(error, "Failed to load commission data");
    } finally {
      setCommissionLoading(false);
    }
  }, [commissionStart, commissionEnd, commissionType]);

  useEffect(() => {
    if (activeTab === "commission") loadCommissions();
  }, [activeTab, loadCommissions]);

  // A contract save with no response at all is a timeout or a dropped
  // connection, which the browser reports as a CORS failure. The write may
  // still have landed (a commission change did exactly that on 26 Sep 2026),
  // so reload the table to show what the server now holds instead of guessing.
  const reportContractSaveError = (error, fallback) => {
    if (error?.response) {
      notify.error(error.response.data?.message || fallback);
      return;
    }
    notify.error("No reply from the server. The table has been reloaded: check it before saving again.");
    loadCommissions();
  };

  const contractFilterDefs = useMemo(() => {
    const countries = [...new Set(commissionContracts.map((c) => c.country).filter(Boolean))].sort();
    return countries.length > 1
      ? [...CONTRACT_FILTERS, { key: "country", label: "All countries", options: countries.map((cc) => [cc, cc, (c) => c.country === cc]) }]
      : CONTRACT_FILTERS;
  }, [commissionContracts]);

  const visibleContracts = useMemo(() => {
    const q = contractFilters.q.trim().toLowerCase();
    return commissionContracts.filter((c) => {
      if (q && ![c.name, c.vendorType, c.vatRegistrationNumber, c.efris?.tin].some((v) => String(v || "").toLowerCase().includes(q))) {
        return false;
      }
      return contractFilterDefs.every(({ key, options }) => {
        const option = contractFilters[key] && options.find(([value]) => value === contractFilters[key]);
        return !option || option[2](c);
      });
    });
  }, [commissionContracts, contractFilters, contractFilterDefs]);

  const contractFiltersActive = Object.values(contractFilters).some(Boolean);

  const openCommissionEdit = (contract) => {
    setCommissionEdit(contract);
    setCommissionEditRate(String(contract.commissionRate));
    setCommissionEditReason("");
  };

  const openVatEdit = (contract) => {
    setVatEdit(contract);
    setVatEditRegistered(contract.vatRegistered !== false);
    setVatEditNumber(contract.vatRegistrationNumber || "");
  };

  const saveVatSettings = async () => {
    const tin = vatEditNumber.trim();
    // Mirrors the server rule (validateVatRegistrationInput) so the operator is
    // told before the round trip, not after it.
    if (vatEditRegistered && !tin) {
      return notify.error("A VAT-registered vendor must have a TIN");
    }
    setVatSaving(true);
    try {
      const res = await apiUpdateVendorVatSettings(
        vatEdit.vendorType,
        vatEdit.contractId,
        vatEditRegistered
          ? { vatRegistered: true, vatRegistrationNumber: tin }
          : { vatRegistered: false }
      );
      notify.success(res.data.message || "VAT settings updated");
      setVatEdit(null);
      await loadCommissions();
    } catch (error) {
      reportContractSaveError(error, "VAT update failed");
    } finally {
      setVatSaving(false);
    }
  };

  const saveCommissionContract = async () => {
    const rate = Number(commissionEditRate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      return notify.error("Commission rate must be between 0 and 100");
    }
    if (commissionEditReason.trim().length < 3) {
      return notify.error("Enter a reason for this commission change");
    }
    setCommissionSaving(true);
    try {
      const res = await apiUpdateVendorCommissionContract(
        commissionEdit.vendorType,
        commissionEdit.contractId,
        { commissionRate: rate, reason: commissionEditReason.trim() }
      );
      notify.success(res.data.message || "Commission contract updated");
      setCommissionEdit(null);
      await loadCommissions();
    } catch (error) {
      reportContractSaveError(error, "Commission update failed");
    } finally {
      setCommissionSaving(false);
    }
  };

  const handleRunReconciliation = async () => {
    if (!reconPeriodStart || !reconPeriodEnd) return notify.error("Select period start and end");
    setReconRunLoading(true);
    try {
      const res = await apiWalletRunReconciliation(reconPeriodStart, reconPeriodEnd);
      const r = res.data;
      notify.success(
        `Reconciliation complete — ${r.discrepancies?.length || 0} discrepancies found, status: ${r.status}`
      );
      setReconRunModal(false);
      setReconPeriodStart(""); setReconPeriodEnd("");
      loadRecon(1, reconFilterStatus, reconFilterDiscrepancy);
      setReconPage(1);
    } catch (err) {
      notify.fromError(err, "Reconciliation run failed");
    } finally {
      setReconRunLoading(false);
    }
  };

  const handleResolveDiscrepancy = async () => {
    if (!resolveModal || !resolveNote.trim()) return;
    setResolveLoading(true);
    try {
      const updated = await apiWalletResolveDiscrepancy(resolveModal.reportId, resolveModal.discrepancyId, resolveNote);
      notify.success("Discrepancy marked as resolved");
      setResolveModal(null);
      setResolveNote("");
      if (reconDetail && reconDetail.reportId === resolveModal.reportId) {
        setReconDetail(updated.data);
      }
      loadRecon(reconPage, reconFilterStatus, reconFilterDiscrepancy);
    } catch (err) {
      notify.fromError(err, "Failed to resolve discrepancy");
    } finally {
      setResolveLoading(false);
    }
  };

  const handleMarkReviewed = async (reportId, reviewNote = "") => {
    try {
      await apiWalletMarkReviewed(reportId, reviewNote);
      notify.success("Report marked as reviewed");
      if (reconDetail && reconDetail.reportId === reportId) {
        setReconDetail((prev) => ({ ...prev, status: "REVIEWED" }));
      }
      loadRecon(reconPage, reconFilterStatus, reconFilterDiscrepancy);
    } catch (err) {
      notify.fromError(err, "Failed to mark reviewed");
    }
  };

  // ── Manual Payout CSV export ─────────────────────────────────────────────
  const exportManualHistoryCSV = () => {
    if (!manualHistory.length) return;
    const headers = [
      "Disbursement ID", "Status", "Payee Type", "Payee Name", "Phone", "Paid By",
      "Purpose", "Amount (UGX)", "MTN Fee (UGX)", "Linked Order ID", "Note", "Queued At",
    ];
    const rows = manualHistory.map((d) => [
      d.disbursementId || "",
      d.status || "",
      d.payeeType || "",
      d.payeeName || "",
      d.payeePhone || "",
      d.initiatedBy?.name || d.initiatedBy?.email || "",
      d.purpose || "PAYOUT",
      d.amount || 0,
      d.mtnFeeAbsorbed || 0,
      d.linkedOrderId || "",
      d.payeeNote || "",
      d.createdAt ? new Date(d.createdAt).toISOString() : "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href     = url;
    link.download = `manual_payouts_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ── Manual Payout handlers ───────────────────────────────────────────────
  const loadManualHistory = useCallback(async (pg = 1, statusF = "") => {
    setManualHistoryLoading(true);
    try {
      const params = { page: pg, limit: 20 };
      if (statusF) params.status = statusF;
      const res = await apiWalletListManualPayouts(params);
      setManualHistory(res.data.records || []);
      setManualHistoryPages(res.data.pagination?.pages || 1);
      setManualHistoryTotal(res.data.pagination?.total || 0);
    } catch (err) {
      notify.error("Failed to load manual payout history");
    } finally {
      setManualHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "manual") loadManualHistory(manualHistoryPage, manualHistoryFilter);
  }, [activeTab, manualHistoryPage, manualHistoryFilter, loadManualHistory]);
  // The silent poll refreshes the history with the page/filter currently shown.
  useEffect(() => {
    manualHistoryQueryRef.current = { page: manualHistoryPage, filter: manualHistoryFilter };
  }, [manualHistoryPage, manualHistoryFilter]);

  const handleManualRowChange = (idx, field, value) => {
    setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleManualAddRow = () => {
    if (manualRows.length >= 50) return notify.error("Maximum 50 rows per submission");
    setManualRows((prev) => [...prev, emptyRow()]);
  };

  const handleManualRemoveRow = (idx) => {
    if (manualRows.length === 1) return;
    setManualRows((prev) => prev.filter((_, i) => i !== idx));
    // Clear any pending search timer for this row
    if (searchTimers.current[`e${idx}`]) clearTimeout(searchTimers.current[`e${idx}`]);
    if (searchTimers.current[`o${idx}`]) clearTimeout(searchTimers.current[`o${idx}`]);
  };

  // ── Entity search (debounced 350ms) ───────────────────────────────────────
  const handleEntitySearchChange = (idx, query) => {
    // Capture payeeType NOW (before the async delay) to avoid stale closure
    const rowType = manualRows[idx]?.payeeType || "RIDER";

    setManualRows((prev) => prev.map((r, i) =>
      i === idx ? { ...r, _searchQuery: query, _searchResults: query.length < 2 ? [] : r._searchResults } : r
    ));
    if (searchTimers.current[`e${idx}`]) clearTimeout(searchTimers.current[`e${idx}`]);
    if (query.length < 2) return;

    searchTimers.current[`e${idx}`] = setTimeout(async () => {
      setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, _searchLoading: true } : r));
      try {
        const res = await apiEntitySearch(rowType, query);
        setManualRows((prev) => prev.map((r, i) =>
          i === idx ? { ...r, _searchResults: res.data.results || [], _searchLoading: false } : r
        ));
      } catch {
        setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, _searchLoading: false } : r));
      }
    }, 350);
  };

  // ── Payee lookup: who the money would reach (debounced 500ms) ─────────────
  // One implementation for every way a phone lands in a row (typed, or
  // pre-filled from the entity). Asks the wallet, which asks MTN, for the
  // number's active flag and registered name. The row's `_lookup` holds the
  // answer keyed by the phone it was asked for, so an edited number is never
  // sent on the strength of the previous number's answer.
  const schedulePayeeLookup = (idx, phone) => {
    const trimmed = (phone || "").trim();
    if (searchTimers.current[`p${idx}`]) clearTimeout(searchTimers.current[`p${idx}`]);
    if (!trimmed || detectPhoneNetwork(trimmed) !== "MTN") {
      setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, _lookup: null } : r));
      return;
    }
    setManualRows((prev) => prev.map((r, i) =>
      i === idx ? { ...r, _lookup: { status: "loading", phone: trimmed } } : r
    ));
    searchTimers.current[`p${idx}`] = setTimeout(async () => {
      try {
        const res = await apiWalletLookupPayee(trimmed);
        const d = res.data || {};
        setManualRows((prev) => prev.map((r, i) =>
          i === idx && r.phone.trim() === trimmed
            ? { ...r, _lookup: {
                status:         "ok",
                phone:          trimmed,
                msisdn:         d.msisdn || null,
                supported:      d.supported !== false,
                active:         d.active === true,
                registeredName: d.registeredName || null,
                nameAvailable:  d.nameAvailable === true,
                message:        d.message || null,
              } }
            : r
        ));
      } catch (err) {
        const message = err?.response?.data?.error || "Could not check this number with MTN";
        setManualRows((prev) => prev.map((r, i) =>
          i === idx && r.phone.trim() === trimmed
            ? { ...r, _lookup: { status: "error", phone: trimmed, message } }
            : r
        ));
      }
    }, 500);
  };

  // A lookup counts only when it answered for the phone now in the row.
  const lookupFor = (row) => {
    const l = row._lookup;
    return l && l.phone === row.phone.trim() ? l : null;
  };
  const isPayeeConfirmed = (row) => {
    const l = lookupFor(row);
    return !!(l && l.status === "ok" && l.supported && l.active);
  };

  // Does MTN's registered name share a word with the payee on file? A bare
  // hint for the operator; it blocks nothing, since a vendor's MoMo line is
  // often in a director's or a spouse's name.
  const namesLookDifferent = (payeeName, registeredName) => {
    const words = (s) => String(s || "").toLowerCase().split(/[^a-z]+/).filter((w) => w.length > 2);
    const a = words(payeeName);
    const b = words(registeredName);
    if (!a.length || !b.length) return false;
    return !a.some((w) => b.includes(w));
  };

  // Called when admin clicks a result from the entity dropdown
  const handleSelectEntity = (idx, entity) => {
    setManualRows((prev) => prev.map((r, i) =>
      i === idx ? {
        ...r,
        payeeId:          entity.id,
        payeeName:        entity.label,
        registeredPhone:  entity.registeredPhone || "",
        phone:            entity.phone || "",            // pre-fill MTN field
        phoneWasOverridden: false,
        _searchQuery:     entity.label,
        _searchResults:   [],
        _lookup:          null,
      } : r
    ));
    schedulePayeeLookup(idx, entity.phone || "");
  };

  // ── Order search for REFUND linkage (debounced 350ms) ─────────────────────
  const handleOrderSearchChange = (idx, query) => {
    setManualRows((prev) => prev.map((r, i) =>
      i === idx ? { ...r, _orderQuery: query, _orderResults: query.length < 2 ? [] : r._orderResults } : r
    ));
    if (searchTimers.current[`o${idx}`]) clearTimeout(searchTimers.current[`o${idx}`]);
    if (query.length < 2) return;

    searchTimers.current[`o${idx}`] = setTimeout(async () => {
      setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, _orderLoading: true } : r));
      try {
        const res = await apiEntitySearch("ORDER", query);
        setManualRows((prev) => prev.map((r, i) =>
          i === idx ? { ...r, _orderResults: res.data.results || [], _orderLoading: false } : r
        ));
      } catch {
        setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, _orderLoading: false } : r));
      }
    }, 350);
  };

  const handleSelectOrder = (idx, order) => {
    setManualRows((prev) => prev.map((r, i) =>
      i === idx ? {
        ...r,
        linkedOrderId:    order.id,
        linkedOrderLabel: order.label + " · " + order.subLabel,
        _orderQuery:      order.label,
        _orderResults:    [],
      } : r
    ));
  };

  // When payeeType changes: clear entity selection and search state
  const handlePayeeTypeChange = (idx, newType) => {
    setManualRows((prev) => prev.map((r, i) =>
      i === idx ? {
        ...emptyRow(),
        amount:   r.amount,
        note:     r.note,
        purpose:  purposesFor(newType).includes(r.purpose) ? r.purpose : defaultPurpose(newType),
        payeeType: newType,
      } : r
    ));
    if (searchTimers.current[`e${idx}`]) clearTimeout(searchTimers.current[`e${idx}`]);
  };

  // Correct booked MTN fees to what MTN actually kept (the next step after a
  // FEE_BAND_MISMATCH discrepancy). Dry run first, then the operator confirms
  // the totals, then the wallet service rewrites rows, posts FEE_CORRECTION
  // journals and logs the run.
  const [feeCorrectionBusy, setFeeCorrectionBusy] = useState(false);
  const handleCorrectBookedFees = async () => {
    setFeeCorrectionBusy(true);
    try {
      const { data: plan } = await apiWalletFeeCorrectionPlan();
      if (!plan.toCorrect) {
        notify.info("Booked fees already match the band table", `${plan.total} successful payout${plan.total === 1 ? "" : "s"} checked.`);
        return;
      }
      confirmDialog.ask({
        title: `Correct booked fees on ${plan.toCorrect} payout${plan.toCorrect === 1 ? "" : "s"}?`,
        body: `MTN fees booked: ${fmtUGX(plan.bookedTotal)}. What MTN actually kept: ${fmtUGX(plan.correctTotal)}. The payout rows are updated and a dated FEE_CORRECTION journal reverses the difference on each; the original journals are kept.`,
        note: "Every change is written to the audit log.",
        tone: "primary",
        confirmLabel: "Correct the books",
        busyLabel: "Correcting...",
        onConfirm: async () => {
          try {
            const { data } = await apiWalletApplyFeeCorrections({ reason: "Band table corrected from measured MTN charges" });
            notify.success(`${data.applied.length} payout${data.applied.length === 1 ? "" : "s"} corrected`, `MTN fees now ${fmtUGX(data.correctTotal)} (was ${fmtUGX(data.bookedTotal)}).`);
            loadRecon(1);
            setReconDetail(null);
            loadTreasury();
          } catch (err) {
            notify.fromError(err, "Fee correction failed");
          }
        },
      });
    } catch (err) {
      notify.fromError(err, "Could not read the fee correction plan");
    } finally {
      setFeeCorrectionBusy(false);
    }
  };

  // One slot for the manual payout outcome, so a repeat submit updates the
  // toast on screen instead of stacking a second copy beside the first.
  const MANUAL_PAYOUT_RESULT_TOAST = "manual-payout-result";
  const FLOAT_LOG_ONLY_TOAST = "manual-payout-float-log-only";

  // Turns a failed result row into the detail line and, where the server
  // sent a structured code, the actions that fix it in place. The wallet
  // service codes AMOUNT_MISMATCH the same way it codes FLOAT_INSUFFICIENT:
  // details carry the figures, the dashboard offers the correction.
  const describePayoutFailure = (row, idx) => {
    if (row?.code === "AMOUNT_MISMATCH" && row.details?.owed != null) {
      const { owed, tolerance, amount } = row.details;
      return {
        detail:
          `${fmtUGX(amount)} does not match the ${fmtUGX(owed)} the ledger says ${row.payeeName || "this payee"} is owed` +
          `${tolerance ? ` (tolerance ${fmtUGX(tolerance)})` : ""}. A bonus, correction or other purpose is not reconciled against the ledger.`,
        actions: [
          { label: `Use ${fmtUGX(owed)}`, primary: true, onClick: () => handleManualRowChange(idx, "amount", String(owed)) },
          { label: "Change purpose", onClick: () => document.getElementById(`manual-payout-row-${idx}`)?.scrollIntoView({ behavior: "smooth", block: "center" }) },
        ],
      };
    }
    return { detail: row?.error || "The wallet service gave no reason.", actions: undefined };
  };

  const handleManualSubmit = async ({ override = null } = {}) => {
    const errors = [];
    manualRows.forEach((r, i) => {
      if (!rowPayeeKnown(r))       errors.push(`Row ${i + 1}: ${r.payeeType === "EXTERNAL" ? "Enter the payee's name" : "Select a payee using the search"}`);
      if (!r.phone.trim())         errors.push(`Row ${i + 1}: MTN phone number is required`);
      else if (detectPhoneNetwork(r.phone) === "AIRTEL") errors.push(`Row ${i + 1}: Airtel numbers are not supported yet — provide an MTN number (07X, 076, 077, 078)`);
      else if (detectPhoneNetwork(r.phone) === "UNKNOWN") errors.push(`Row ${i + 1}: Unrecognised phone number — use format 07XXXXXXXX or +2567XXXXXXXX`);
      else if (!isPayeeConfirmed(r)) errors.push(`Row ${i + 1}: Wait for MTN to confirm who this number is registered to before sending`);
      if (!r.amount || Number(r.amount) < 500) errors.push(`Row ${i + 1}: Amount must be ≥ UGX 500`);
      const reasonProblem = rowReasonProblem(r);
      if (reasonProblem) errors.push(`Row ${i + 1}: ${reasonProblem}`);
    });
    if (errors.length > 0) {
      errors.forEach((e) => notify.error(e, undefined, { autoClose: 3500 }));
      return;
    }

    // ── Float-solvency pre-check (the server enforces the hard gate too) ────
    const totalDebit = manualRows.reduce(
      (s, r) => s + (Number(r.amount) || 0) + estimateFee(r.amount),
      0
    );
    if (treasury?.float?.balance != null && !override) {
      const spendable = treasury.float.balance - (treasury.watermark || 0);
      if (totalDebit > spendable) {
        if (treasury.enforced) {
          // Show the actual numbers and a way forward. The old copy named only
          // Flutterwave and quoted "UGX 0 spendable" with no balance or reserve
          // beside it, which reads as a bug when the float visibly holds money.
          setFloatBlock({
            required: totalDebit,
            balance: treasury.float.balance,
            watermark: treasury.watermark || 0,
            spendable: Math.max(0, spendable),
          });
          setOverrideReason("");
          return;
        }
        notify.warn(
          "Float is short, enforcement is log-only",
          "Submitting anyway. MTN is the final arbiter and will reject if the money is not there.",
          { id: FLOAT_LOG_ONLY_TOAST }
        );
      }
    }

    setManualLoading(true);
    setManualResults(null);
    let results = null;
    try {
      const res = await apiWalletManualPayout({
        payouts: manualRows.map((r) => ({
          payeeType:          r.payeeType,
          payeeId:            r.payeeId || undefined,       // none for an EXTERNAL payee
          payeeName:          r.payeeName.trim(),
          phone:              r.phone.trim(),
          amount:             Number(r.amount),
          note:               r.note.trim(),
          purpose:            r.purpose || defaultPurpose(r.payeeType),
          linkedOrderId:      r.linkedOrderId || undefined,
          registeredPhone:    r.registeredPhone || undefined,
          phoneWasOverridden: !!r.phoneWasOverridden,
          momoRegisteredName: lookupFor(r)?.registeredName || undefined,
        })),
        ...(override
          ? { overrideFloat: true, overrideReason: override.reason }
          : {}),
      });
      results = payoutResultsFrom(res.data) || [];
    } catch (err) {
      results = payoutResultsFrom(err?.response?.data);
      if (!results) notify.fromError(err, "Submission failed", { id: MANUAL_PAYOUT_RESULT_TOAST });
    } finally {
      setManualLoading(false);
    }
    if (!results) return;

    setManualResults(results);
    const ok  = results.filter((r) => r.ok).length;
    const bad = results.filter((r) => !r.ok).length;
    if (bad === 0) {
      notify.success(
        `${ok} payout${ok !== 1 ? "s" : ""} queued`,
        "Money moves on the next queue run, within 5 minutes.",
        { id: MANUAL_PAYOUT_RESULT_TOAST }
      );
      setManualRows([emptyRow()]);
      setFloatBlock(null);
    } else {
      // Only the failure should be on screen now: the pre-submit float
      // warning has nothing to add once the server has answered.
      notify.dismiss(FLOAT_LOG_ONLY_TOAST);
      if (bad === 1) {
        // One failed row carries its own reason and, when the server knows
        // the fix, the fix itself. Say it here rather than sending the
        // operator to the results panel for one line.
        const idx = results.findIndex((r) => !r.ok);
        const { detail, actions } = describePayoutFailure(results[idx], idx);
        notify.error(ok ? `${ok} queued, 1 not queued` : "Payout not queued", detail, { id: MANUAL_PAYOUT_RESULT_TOAST, actions });
      } else {
        notify.error(`${ok} queued, ${bad} not queued`, "Each row's reason is in the results below.", { id: MANUAL_PAYOUT_RESULT_TOAST });
      }
    }
    loadManualHistory(1, manualHistoryFilter);
    setManualHistoryPage(1);
    loadTreasury();
  };

  // ── Reports tab handlers ─────────────────────────────────────────────────
  // Every call here can hit an older wallet (404). That flips
  // reportsUnavailable, which shows one notice and disables the controls —
  // the rest of the page is untouched.
  const failSoft = useCallback((err, fallback) => {
    if (is404(err)) { setReportsUnavailable(true); return; }
    const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
    notify.error(msg || fallback);
  }, []);

  const loadReportSummary = useCallback(async (range) => {
    setReportLoading(true);
    setReportError("");
    try {
      const r = await apiWalletReportSummary({ ...rangeToIso(range), bucket: autoBucket(range) });
      setReportSummary(r.data || null);
      setReportsUnavailable(false);
    } catch (err) {
      if (is404(err)) setReportsUnavailable(true);
      else setReportError(err?.response?.data?.error || err?.message || "Could not load the report summary");
    } finally {
      setReportLoading(false);
    }
  }, []);

  const loadScheduledReports = useCallback(async () => {
    setScheduledLoading(true);
    try {
      const r = await apiWalletScheduledReports({ page: 1, limit: 20 });
      setScheduledReports(r.data?.reports || []);
    } catch (err) {
      if (is404(err)) setReportsUnavailable(true);
    } finally {
      setScheduledLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "reports" && reportSection === "payouts") loadReportSummary(reportRange);
  }, [activeTab, reportSection, reportRange, loadReportSummary]);

  useEffect(() => {
    if (activeTab === "reports" && reportSection === "schedule") loadScheduledReports();
  }, [activeTab, reportSection, reportReadyTick, loadScheduledReports]);

  const setRangeField = (field, value) => {
    if (!value) return;
    setReportRange((r) => {
      const next = { ...r, [field]: value };
      if (next.from > next.to) return field === "from" ? { from: value, to: value } : { from: value, to: value };
      return next;
    });
  };

  const downloadReportCsv = async (kind) => {
    setReportDownloading(kind);
    try {
      const fn = kind === "ledger" ? apiWalletReportLedgerCsv : apiWalletReportRegisterCsv;
      const res = await fn(rangeToIso(reportRange));
      saveBlobResponse(res, `lusuku_${kind === "ledger" ? "ledger" : "payouts"}_${reportRange.from}_${reportRange.to}.csv`);
      if (String(res.headers?.["x-row-cap-hit"] || "").toLowerCase() === "true") {
        notify.warn("Row cap (50 000) reached. Narrow the period for a complete register.", undefined, { autoClose: 8000 });
      }
    } catch (err) {
      if (is404(err)) { setReportsUnavailable(true); notify.info(REPORTS_NEED_WALLET); }
      else notify.error((await blobErrorMessage(err)) || "Download failed");
    } finally {
      setReportDownloading(null);
    }
  };

  const exportSummaryXlsx = () => {
    if (!reportSummary) return;
    const t   = reportSummary.totals  || {};
    const lat = reportSummary.latency || {};
    const tr  = reportSummary.treasury || {};
    const wb  = XLSX.utils.book_new();
    const sheet = (name, rows) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ note: "no data" }]), name);
    const dim = (arr) => (arr || []).map((r) => ({ key: r.key, count: r.count, amount: r.amount, fees: r.fees }));
    const latRow = (k) => ({ metric: `${k} latency p50/p90/max/avg (s)`, value: [lat[k]?.p50, lat[k]?.p90, lat[k]?.max, lat[k]?.avg].map((v) => v ?? "—").join(" / ") });
    sheet("Summary", [
      { metric: "Period from",     value: reportSummary.period?.from || reportRange.from },
      { metric: "Period to",       value: reportSummary.period?.to   || reportRange.to },
      { metric: "Bucket",          value: reportSummary.period?.bucket || autoBucket(reportRange) },
      { metric: "Count",           value: t.count ?? 0 },
      { metric: "Amount (UGX)",    value: t.amount ?? 0 },
      { metric: "Fees absorbed",   value: t.feesAbsorbed ?? 0 },
      { metric: "MoMo debit",      value: t.momoDebit ?? 0 },
      { metric: "Successful",      value: t.successCount ?? 0 },
      { metric: "Failed",          value: t.failedCount ?? 0 },
      { metric: "In flight",       value: t.inFlightCount ?? 0 },
      { metric: "Success rate",    value: fmtPct(t.successRate) },
      latRow("queue"), latRow("confirm"), latRow("total"),
      { metric: "Generated at",    value: reportSummary.generatedAt || new Date().toISOString() },
    ]);
    sheet("ByStatus",  dim(reportSummary.byStatus));
    sheet("ByTrigger", dim(reportSummary.byTriggerType));
    sheet("ByPayee",   dim(reportSummary.byPayeeType));
    sheet("ByPurpose", dim(reportSummary.byPurpose));
    sheet("Failures",  (reportSummary.failureReasons || []).map((r) => ({ reason: r.reason, count: r.count, amount: r.amount })));
    sheet("Series",    (reportSummary.series || []).map((r) => ({ bucket: r.bucket, count: r.count, amount: r.amount, fees: r.fees, failed: r.failed })));
    sheet("Treasury", [
      { item: "FW settlements",  count: tr.fwSettlements?.count ?? 0, gross: tr.fwSettlements?.gross ?? 0, fees: tr.fwSettlements?.fees ?? 0, net: tr.fwSettlements?.net ?? 0 },
      { item: "Float top-ups",   count: tr.floatTopups?.count ?? 0,   amount: tr.floatTopups?.amount ?? 0 },
      { item: "Payout journals", count: tr.payoutJournals?.count ?? 0, amount: tr.payoutJournals?.amount ?? 0 },
      ...(reportSummary.resolvedVia || []).map((r) => ({ item: `resolved via ${r.key}`, count: r.count })),
    ]);
    XLSX.writeFile(wb, `lusuku_payout_summary_${reportRange.from}_${reportRange.to}.xlsx`);
  };

  // MTN statement: parse locally with xlsx, map columns, preview, reconcile.
  const handleMtnFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, { type: "array", cellDates: true });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });
      const headerRow = XLSX.utils.sheet_to_json(ws, { header: 1, range: 0 })[0] || [];
      const headers = rows.length ? Object.keys(rows[0]) : headerRow.map((h) => String(h ?? ""));
      setMtnFileName(file.name);
      setMtnHeaders(headers);
      setMtnRawRows(rows);
      setMtnMapping(mapMtnStatementColumns(headers));
      setMtnResult(null);
      if (!rows.length) notify.warn("That file has a header but no rows");
    } catch (err) {
      notify.error("Could not read that file. A CSV or XLSX statement is expected.");
    }
  };

  const clearMtnFile = () => {
    setMtnFileName(""); setMtnHeaders([]); setMtnRawRows([]); setMtnMapping(null); setMtnResult(null);
    if (mtnFileRef.current) mtnFileRef.current.value = "";
  };

  const mtnPreviewRows = mtnMapping ? normaliseMtnRows(mtnRawRows.slice(0, 5), mtnMapping) : [];
  const mtnProblems    = mtnMapping ? mappingProblems(mtnMapping) : [];

  const runMtnReconcile = async () => {
    if (mtnProblems.length) return notify.error(mtnProblems[0]);
    const rows = normaliseMtnRows(mtnRawRows, mtnMapping);
    if (!rows.length) return notify.error("No usable rows in the statement");
    if (rows.length > 5000) return notify.error("Maximum 5 000 statement rows per reconcile. Split the file.");
    setMtnReconciling(true);
    try {
      const body = { rows };
      if (mtnUseRange) Object.assign(body, rangeToIso(reportRange));
      const r = await apiWalletMtnStatementReconcile(body);
      setMtnResult(r.data || null);
      const sm = r.data?.summary || {};
      const first = ["amountMismatch", "statusMismatch", "missingInLusuku", "missingAtMtn", "matched"].find((k) => Number(sm[k]) > 0) || "matched";
      setMtnResultTab(first);
      notify.success(`Reconciled ${sm.mtnRows ?? rows.length} MTN rows, ${sm.matched ?? 0} matched`);
    } catch (err) {
      if (is404(err)) { setReportsUnavailable(true); notify.info(REPORTS_NEED_WALLET); }
      else failSoft(err, "Reconcile failed");
    } finally {
      setMtnReconciling(false);
    }
  };

  const downloadMismatchCsv = () => {
    if (!mtnResult) return;
    saveText(buildMismatchCsv(mtnResult), `mtn_reconcile_mismatches_${localDateStr(new Date())}.csv`);
  };

  // Scheduled reports
  const openScheduledReport = async (reportId) => {
    setScheduledDetail({ reportId });
    setScheduledDetailLoading(true);
    try {
      const r = await apiWalletScheduledReport(reportId);
      setScheduledDetail(r.data || { reportId });
      if (typeof r.data?.chainValid === "boolean") setScheduledChain((m) => ({ ...m, [reportId]: r.data.chainValid }));
    } catch (err) {
      setScheduledDetail(null);
      failSoft(err, "Could not load that report");
    } finally {
      setScheduledDetailLoading(false);
    }
  };

  const verifyScheduledChain = async () => {
    if (!scheduledReports.length) return;
    setVerifyingChain(true);
    try {
      for (const rep of scheduledReports) {
        try {
          const r = await apiWalletScheduledReport(rep.reportId);
          if (typeof r.data?.chainValid === "boolean") setScheduledChain((m) => ({ ...m, [rep.reportId]: r.data.chainValid }));
        } catch (err) {
          if (is404(err)) { setReportsUnavailable(true); break; }
        }
        await new Promise((res) => setTimeout(res, 150));
      }
    } finally {
      setVerifyingChain(false);
    }
  };

  const generateReportNow = async () => {
    const body = { kind: generateKind };
    if (generateKind === "ADHOC") {
      if (!generateStart || !generateEnd) return notify.error("An ADHOC report needs both period dates");
      if (generateStart > generateEnd) return notify.error("Period start must be on or before period end");
      body.periodStart = generateStart;
      body.periodEnd   = generateEnd;
    }
    setGenerating(true);
    try {
      const r = await apiWalletGenerateReport(body);
      notify.success(`Report ${r.data?.reportId || "generated"} · seq ${r.data?.sequence ?? "n/a"}`);
      loadScheduledReports();
    } catch (err) {
      if (err?.response?.status === 403) notify.error("Only admins can generate reports");
      else failSoft(err, "Report generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // ── Reports v2: payments analytics, schedule settings, test email ─────────
  const loadPayments = useCallback(async (range) => {
    setPaymentsLoading(true);
    setPaymentsError("");
    try {
      const r = await apiWalletPaymentsAnalytics({ ...rangeToIso(range), bucket: autoBucket(range) });
      setPayments(r.data || null);
      setPaymentsUnavailable(false);
    } catch (err) {
      if (is404(err)) setPaymentsUnavailable(true);
      else setPaymentsError(err?.response?.data?.error || err?.message || "Could not load payments analytics");
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  const applySettingsResponse = useCallback((data) => {
    const form = toSettingsForm(data?.settings);
    setSettingsForm(form);
    setSettingsSaved(form);
    setSettingsMeta({ source: data?.source || null, envLocks: data?.envLocks || null });
    setSettingsUnavailable(false);
  }, []);

  const loadReportSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const r = await apiWalletReportSettings();
      applySettingsResponse(r.data);
    } catch (err) {
      if (is404(err)) setSettingsUnavailable(true);
      else notify.fromError(err, "Could not load the report settings");
    } finally {
      setSettingsLoading(false);
    }
  }, [applySettingsResponse]);

  useEffect(() => {
    if (activeTab === "reports" && reportSection === "overview") loadOverview();
  }, [activeTab, reportSection, loadOverview]);

  useEffect(() => {
    if (activeTab === "reports" && reportSection === "payments") loadPayments(reportRange);
  }, [activeTab, reportSection, reportRange, loadPayments]);

  useEffect(() => {
    if (activeTab === "reports" && reportSection === "schedule") loadReportSettings();
  }, [activeTab, reportSection, loadReportSettings]);

  const settingsDirty   = settingsSaved ? diffSettings(settingsSaved, settingsForm).length > 0 : false;
  const canEditSettings = isAdmin; // the wallet's PUT /settings is isAdmin: ADMIN or super user

  const setSetting    = (key, value) => setSettingsForm((f) => ({ ...f, [key]: value }));
  const setAttachment = (key, value) => setSettingsForm((f) => ({ ...f, attachments: { ...f.attachments, [key]: value } }));

  // Chip input: Enter, comma or blur commits the draft; invalid tokens stay in the box.
  const commitRecipientDraft = () => {
    if (!recipientDraft.trim()) { setRecipientError(""); return; }
    const r = addRecipients(settingsForm.recipients, recipientDraft);
    if (r.added.length) setSetting("recipients", r.recipients);
    if (r.invalid.length) setRecipientError(`Not an email address: ${r.invalid.join(", ")}`);
    else if (r.overflow.length) setRecipientError(`Maximum ${MAX_RECIPIENTS} recipients. Not added: ${r.overflow.join(", ")}`);
    else setRecipientError("");
    setRecipientDraft(r.invalid.join(", "));
  };
  const onRecipientKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitRecipientDraft();
    } else if (e.key === "Backspace" && !recipientDraft && settingsForm.recipients.length) {
      setSetting("recipients", settingsForm.recipients.slice(0, -1));
    }
  };

  const saveReportSettings = async () => {
    const body = settingsPayload(settingsForm);
    if (recipientDraft.trim()) {
      const r = addRecipients(body.recipients, recipientDraft);
      if (r.invalid.length) return notify.error(`Fix the recipient box first: ${r.invalid.join(", ")}`);
      body.recipients = r.recipients;
    }
    const before = settingsSaved || toSettingsForm(null);
    setSettingsSaving(true);
    try {
      const r = await apiWalletUpdateReportSettings(body);
      applySettingsResponse(r.data);
      setRecipientDraft("");
      setRecipientError("");
      const changes = diffSettings(before, r.data?.settings || body);
      notify.success(changes.length ? `Report settings saved · ${formatSettingsDiff(changes)}` : "Report settings saved · no changes", undefined, { autoClose: 8000 });
    } catch (err) {
      if (is404(err)) { setSettingsUnavailable(true); notify.info(REPORTS_NEED_WALLET); }
      else if (err?.response?.status === 403) notify.error("Only admins can change the report settings");
      else notify.fromError(err, "Could not save the report settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  const sendTestReportEmail = async () => {
    const body = {};
    if (testEmailTo.trim()) {
      const { valid, invalid } = parseRecipients(testEmailTo);
      if (invalid.length) return notify.error(`Not an email address: ${invalid.join(", ")}`);
      if (valid.length) body.recipients = valid;
    }
    setTestEmailSending(true);
    setTestEmailResult(null);
    try {
      const r = await apiWalletSendTestReportEmail(body);
      const d = r.data || {};
      const list = Array.isArray(d.recipients) ? d.recipients : [];
      setTestEmailResult({ recipients: list, attachments: Array.isArray(d.attachments) ? d.attachments : [], at: new Date() });
      notify.success(`Test email posted to ${list.length} recipient${list.length === 1 ? "" : "s"}`);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Test email failed";
      if (is404(err)) { setSettingsUnavailable(true); notify.info(REPORTS_NEED_WALLET); }
      else if (err?.response?.status === 403) notify.error("Only admins can send the test email");
      else { setTestEmailResult({ error: msg }); notify.error(msg); }
    } finally {
      setTestEmailSending(false);
    }
  };

  const exportPaymentsXlsx = () => {
    if (!payments) return;
    const col = payments.collections || {};
    const ct  = col.totals || {};
    const es  = payments.escrow || {};
    const nf  = payments.netFlow || {};
    const wb  = XLSX.utils.book_new();
    const sheet = (name, rows) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ note: "no data" }]), name);
    const dim = (arr) => (arr || []).map((r) => ({ key: r.key, count: r.count, gross: r.gross, commission: r.commission, net: r.net }));
    sheet("Summary", [
      { metric: "Period from",         value: payments.period?.from || reportRange.from },
      { metric: "Period to",           value: payments.period?.to   || reportRange.to },
      { metric: "Bucket",              value: payments.period?.bucket || autoBucket(reportRange) },
      { metric: "Collections count",   value: ct.count ?? 0 },
      { metric: "Collections gross",   value: ct.gross ?? 0 },
      { metric: "Commission",          value: ct.commission ?? 0 },
      { metric: "Collections net",     value: ct.net ?? 0 },
      { metric: "Refunds count",       value: payments.refunds?.count ?? 0 },
      { metric: "Refunds amount",      value: payments.refunds?.amount ?? 0 },
      { metric: "COD deposits count",  value: payments.cod?.deposits?.count ?? 0 },
      { metric: "COD deposits amount", value: payments.cod?.deposits?.amount ?? 0 },
      { metric: "Payouts count",       value: payments.payouts?.count ?? 0 },
      { metric: "Payouts amount",      value: payments.payouts?.amount ?? 0 },
      { metric: "Payout fees",         value: payments.payouts?.fees ?? 0 },
      { metric: "Net flow: collections gross", value: nf.collectionsGross ?? 0 },
      { metric: "Net flow: payouts",   value: nf.payouts ?? 0 },
      { metric: "Net flow: refunds",   value: nf.refunds ?? 0 },
      { metric: "Net flow",            value: nf.net ?? 0 },
      { metric: "Generated at",        value: payments.generatedAt || new Date().toISOString() },
    ]);
    sheet("ByRail",       dim(col.byRail));
    sheet("ByVendorType", dim(col.byVendorType));
    sheet("ByStatus",     (col.byStatus || []).map((r) => ({ key: r.key, count: r.count, gross: r.gross, net: r.net })));
    sheet("Series",       (col.series || []).map((r) => ({ bucket: r.bucket, count: r.count, gross: r.gross, commission: r.commission, net: r.net })));
    sheet("Escrow", [
      { item: "Held now",             count: es.heldNow?.count ?? 0,          net: es.heldNow?.net ?? 0 },
      { item: "Released in period",   count: es.releasedInPeriod?.count ?? 0, net: es.releasedInPeriod?.net ?? 0 },
      { item: "Reversed in period",   count: es.reversedInPeriod?.count ?? 0, net: es.reversedInPeriod?.net ?? 0 },
      { item: "Average hold (hours)", value: es.avgHoldHours ?? null },
    ]);
    XLSX.writeFile(wb, `lusuku_payments_${reportRange.from}_${reportRange.to}.xlsx`);
  };

  // Audit drawer: wallet AuditLog rows for this disbursement + its lifecycle.
  // The wallet writes entityId = disbursementId; _id is tried as a fallback.
  const openAudit = async (row) => {
    if (!row) return;
    setAuditRow(row);
    setAuditLogs([]);
    setAuditUnavailable(false);
    setAuditLoading(true);
    try {
      const ids = [row.disbursementId, row._id].filter(Boolean).map(String).filter((v, i, a) => a.indexOf(v) === i);
      let logs = [];
      for (const id of ids) {
        const r = await apiWalletAuditLogs({ entityType: "DisbursementRequest", entityId: id, limit: 50 });
        logs = (r.data?.logs || []).filter((l) => l?.entityId == null || String(l.entityId) === id);
        if (logs.length) break;
      }
      setAuditLogs(logs);
    } catch (err) {
      if (is404(err)) setAuditUnavailable(true);
      else notify.fromError(err, "Could not load the audit trail");
    } finally {
      setAuditLoading(false);
    }
  };

  // ── Phone network detection (mirrors utilities/phoneValidation.js) ────────
  const detectPhoneNetwork = (phone) => {
    if (!phone || !phone.trim()) return null;
    const cleaned = phone.replace(/[\s\-\+\(\)]/g, "");
    let msisdn = cleaned;
    if (/^07[0-9]\d{7}$/.test(cleaned))  msisdn = "256" + cleaned.substring(1);
    else if (/^7\d{8}$/.test(cleaned))   msisdn = "256" + cleaned;
    else if (/^256\d{9}$/.test(cleaned)) msisdn = cleaned;
    else return "UNKNOWN";
    const prefix = msisdn.substring(3, 5);
    if (["76","77","78","31","39"].includes(prefix)) return "MTN";
    if (["70","74","75","20"].includes(prefix))      return "AIRTEL";
    return "UNKNOWN";
  };
  // ─────────────────────────────────────────────────────────────────────────

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: C.textMid }}>
        <FiShield size={40} style={{ color: C.red, marginBottom: 12 }} />
        <div style={{ fontSize: 16, fontWeight: 600 }}>Admin access required</div>
      </div>
    );
  }

  const cbMeta = CB_META[cb?.state] || CB_META.CLOSED;
  const svcUp  = health?.status === "healthy";
  // /admin/summary → { today:{ byStatus:[{_id,count,totalAmount,totalFeesAbsorbed}], totalDisbursed }, allTime:{…} }
  const todaySuccess      = (summary?.today?.byStatus || []).find((b) => b?._id === "SUCCESSFUL");
  const todaySuccessCount = Number(todaySuccess?.count) || 0;
  const todayFees         = Number(todaySuccess?.totalFeesAbsorbed) || 0;
  // MTN takes a fee on both legs: a band fee on every payout (absorbed, never
  // deducted from the payee) and a percentage of every MoMo collection before
  // it lands. The card shows the total with both parts under it.
  const payoutFees        = Number(summary?.allTime?.totalFeesAbsorbed ?? summary?.totalFeesAbsorbed) || 0;
  const collectionFees    = Number(summary?.collectionFees?.allTime) || 0;
  const todayCollectionFees = Number(summary?.collectionFees?.today) || 0;

  const isFormReady = manualRows.every((r) =>
    rowPayeeKnown(r) &&
    r.phone.trim() &&
    detectPhoneNetwork(r.phone) === "MTN" &&
    isPayeeConfirmed(r) &&
    Number(r.amount) >= 500 &&
    !rowReasonProblem(r)
  );

  return (
    <div style={S.page}>
      <ConfirmDialog {...confirmDialog.props} />
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse-glow { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        .pulse-dot { animation: pulse-glow 1.5s ease-in-out infinite; }
      `}</style>
      {/* Hero */}
      <div style={S.hero}>
        <div>
          <div style={S.heroTitle}><FiDollarSign style={{ marginRight: 8 }} />Wallet & Disbursements</div>
          <div style={S.heroSub}>MTN MoMo disbursement operations · Lusuku Wallet Service{lastUpdated && <span style={{ marginLeft: 10, opacity: 0.6, fontSize: 11 }}>· Updated {lastUpdated.toLocaleTimeString()} · refreshing every {Math.round(currentDelayMs / 1000)}s</span>}</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {health && (
            <span style={{ display: "flex", alignItems: "center", gap: 6, background: svcUp ? "rgba(0,182,155,0.18)" : "rgba(230,55,87,0.18)", border: `1px solid ${svcUp ? "rgba(0,182,155,0.4)" : "rgba(230,55,87,0.4)"}`, borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: svcUp ? "#00b69b" : "#e63757" }}>
              {svcUp ? <FiWifi size={12} /> : <FiWifiOff size={12} />}
              Wallet Service {svcUp ? "Operational" : "Down"}
            </span>
          )}
          {proxyHealth && (
            <span style={{ display: "flex", alignItems: "center", gap: 6, background: proxyHealth.status === "healthy" ? "rgba(0,182,155,0.18)" : proxyHealth.status === "not_configured" ? "rgba(200,200,200,0.25)" : "rgba(230,55,87,0.18)", border: `1px solid ${proxyHealth.status === "healthy" ? "rgba(0,182,155,0.4)" : proxyHealth.status === "not_configured" ? "rgba(200,200,200,0.5)" : "rgba(230,55,87,0.4)"}`, borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: proxyHealth.status === "healthy" ? "#00b69b" : proxyHealth.status === "not_configured" ? "#999" : "#e63757" }}>
              {proxyHealth.status === "healthy" ? <FiWifi size={12} /> : proxyHealth.status === "not_configured" ? <FiAlertCircle size={12} /> : <FiWifiOff size={12} />}
              Proxy {proxyHealth.status === "healthy" ? `OK (${proxyHealth.latencyMs}ms)` : proxyHealth.status === "not_configured" ? "N/A" : "Down"}
            </span>
          )}
          <button style={S.btn("ghost")} onClick={() => load(page, filterStatus, searchId)} disabled={loading}>
            <FiRefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
          <button
            style={{ ...S.btn("teal"), opacity: polling ? 0.7 : 1 }}
            onClick={() => setConfirmPoll(true)}
            disabled={polling}
          >
            {polling ? <Spinner animation="border" size="sm" /> : <FiZap size={13} />}
            Poll Stuck
          </button>
        </div>
        {/* Live queue strip — hidden entirely on an older wallet (no /admin/scheduler) */}
        <QueueStrip scheduler={scheduler} />
      </div>

      {/* Stat Cards */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            label="MoMo Balance"
            loading={loading}
            value={fmtUGX(balance?.availableBalance)}
            sub={balance?.currency || "UGX"}
            icon={<FiDollarSign size={20} color={C.teal} />}
            iconBg={C.tealSoft}
            hint="All money in"
            onClick={() => { setMoneyInFocus((n) => n + 1); setActiveTab("moneyIn"); }}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            label="Total Disbursed"
            loading={loading}
            value={fmtUGX(summary?.allTime?.totalDisbursed ?? summary?.totalDisbursed)}
            sub={`${(summary?.allTime?.successCount ?? summary?.successCount) || 0} successful transfers · today ${fmtUGX(summary?.today?.totalDisbursed)} · ${todaySuccessCount} transfer${todaySuccessCount === 1 ? "" : "s"}`}
            icon={<FiTrendingUp size={20} color={C.blue} />}
            iconBg={C.blueSoft}
            hint="All successful payouts"
            onClick={() => { handleStatusFilter("SUCCESSFUL"); setSearchId(""); setActiveTab("disbursements"); }}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            label="MTN Fees"
            loading={loading}
            value={fmtUGX(payoutFees + collectionFees)}
            sub={
              `Payouts ${fmtUGX(payoutFees)} · collections ${fmtUGX(collectionFees)}` +
              (todayFees + todayCollectionFees > 0 ? ` · today ${fmtUGX(todayFees + todayCollectionFees)}` : "") +
              " · Lusuku absorbs all of it"
            }
            icon={<FiShield size={20} color={C.purple}   hint="Fee reports"
            onClick={() => setActiveTab("reports")}
          />}
            iconBg={C.purpleSoft}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <StatCard
            label="Circuit Breaker"
            value={cb ? cbMeta.label : "—"}
            sub={cb?.failureCount !== undefined ? `${cb.failureCount} / ${cb.failureThreshold || 5} failures` : ""}
            icon={cbMeta.icon ? React.cloneElement(cbMeta.icon, { size: 20, color: cbMeta.color === "green" ? C.teal : cbMeta.color === "red" ? C.red : C.orange }) : <FiActivity size={20} />}
            iconBg={cbMeta.color === "green" ? C.tealSoft : cbMeta.color === "red" ? C.accentSoft : C.orangeSoft}
          />
        </Col>
      </Row>

      {/* Proxy Health Banner (if unhealthy) */}
      {proxyHealth && proxyHealth.status === "unhealthy" && (
        <div style={{ background: "rgba(230,55,87,0.08)", border: `1px solid rgba(230,55,87,0.3)`, borderRadius: C.radius, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <FiWifiOff color={C.red} size={18} />
          <div>
            <div style={{ fontSize: 13, color: C.red, fontWeight: 600 }}>Oracle VM Proxy Unreachable — MTN API calls may fail</div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
              Proxy: {proxyHealth.proxyUrl} → {proxyHealth.testTarget} — {proxyHealth.error || "connection failed"} ({proxyHealth.latencyMs}ms)
            </div>
            <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>
              SSH into <code>ubuntu@145.241.205.19</code> and run: <code>sudo systemctl status tinyproxy</code>
            </div>
          </div>
        </div>
      )}

      {/* Service Health Banner (if features disabled) */}
      {health && (!health.features?.disbursements) && (
        <div style={{ background: C.accentSoft, border: `1px solid ${C.accentBorder}`, borderRadius: C.radius, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <FiAlertTriangle color={C.red} size={18} />
          <span style={{ fontSize: 13, color: C.red, fontWeight: 600 }}>
            Disbursements feature is <strong>disabled</strong> on the Wallet Service — check env vars and restart.
          </span>
        </div>
      )}

      {/* Circuit Breaker Detail */}
      {cb?.state === "OPEN" && (
        <div style={{ background: C.accentSoft, border: `1px solid ${C.accentBorder}`, borderRadius: C.radius, padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <FiAlertOctagon color={C.red} size={18} />
          <div>
            <div style={{ fontSize: 13, color: C.red, fontWeight: 600 }}>Circuit Breaker OPEN — MTN API calls are blocked</div>
            <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
              Resets to HALF_OPEN in ~{cb.resetTimeoutMs ? Math.ceil(cb.resetTimeoutMs / 60000) : 5} minutes. Last failure: {fmtDate(cb.lastFailureTime)}
            </div>
          </div>
        </div>
      )}

      {/* ── Treasury strip: MTN float vs obligations (go-live guardrail) ── */}
      {treasury && (
        <div
          style={{
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
            background:
              treasury.float?.status === "RED" ? "#fdecea"
              : treasury.float?.status === "AMBER" ? "#fef7e6"
              : "#eef7f1",
            border: `1px solid ${
              treasury.float?.status === "RED" ? C.red
              : treasury.float?.status === "AMBER" ? C.orange
              : C.teal}`,
            borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13,
          }}
        >
          <FiShield
            size={15}
            color={
              treasury.float?.status === "RED" ? C.red
              : treasury.float?.status === "AMBER" ? C.orange
              : C.teal
            }
          />
          <b>MTN payout float:</b>
          <span>
            {treasury.float?.balance != null
              ? `UGX ${Number(treasury.float.balance).toLocaleString()}`
              : "unavailable"}
          </span>
          <span style={{ color: C.textMid }}>
            reserve UGX {Number(treasury.watermark || 0).toLocaleString()}
            {treasury.watermarkSource === "setting" && " (live)"}
            {isSuperAdminUser && (
              <button
                type="button"
                onClick={openFloatModal}
                title="Change the float reserve"
                style={{
                  marginLeft: 6, border: "none", background: "transparent",
                  color: C.teal, cursor: "pointer", fontWeight: 600,
                  textDecoration: "underline", padding: 0, fontSize: "inherit",
                }}
              >
                Edit
              </button>
            )}
          </span>
          <span style={{ color: C.textMid }}>
            | queued debits UGX {Number(treasury.obligations?.queuedDebit || 0).toLocaleString()} ({treasury.obligations?.queuedCount || 0})
          </span>
          <span style={{ color: C.textMid }}>
            | owed out UGX {Number(treasury.totalOwed || 0).toLocaleString()}
          </span>
          {treasury.coverage != null && (
            <span style={{ color: C.textMid }}>
              | coverage {(treasury.coverage * 100).toFixed(0)}%
            </span>
          )}
          <span
            style={{
              marginLeft: "auto", fontWeight: 600,
              color:
                treasury.float?.status === "RED" ? C.red
                : treasury.float?.status === "AMBER" ? C.orange
                : C.teal,
            }}
          >
            {treasury.float?.status === "RED"
              ? "TOP-UP REQUIRED: settle Flutterwave to the bank, then top up the float"
              : treasury.float?.status === "AMBER"
              ? "FLOAT RUNNING LOW"
              : "FLOAT HEALTHY"}
          </span>
          <button
            onClick={() => openTreasuryModal("fw")}
            title="Record a Flutterwave settlement arriving at the bank"
            style={{ border: `1px solid ${C.textMid}`, borderRadius: 6, background: "#fff", cursor: "pointer", color: C.textMid, fontSize: 12, padding: "3px 8px" }}
          >
            Record FW settlement
          </button>
          <button
            onClick={() => openTreasuryModal("topup")}
            title="Record a bank → MTN float top-up"
            style={{ border: `1px solid ${C.textMid}`, borderRadius: 6, background: "#fff", cursor: "pointer", color: C.textMid, fontSize: 12, padding: "3px 8px" }}
          >
            Record float top-up
          </button>
          <button
            onClick={loadTreasury}
            title="Refresh treasury"
            style={{ border: "none", background: "transparent", cursor: "pointer", color: C.textMid }}
          >
            <FiRefreshCw size={14} />
          </button>
        </div>
      )}

      {/* ── Record treasury movement modal ── */}
      <Modal show={!!treasuryModal} onHide={() => setTreasuryModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {treasuryModal === "fw" ? "Record Flutterwave settlement" : "Record MTN float top-up"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13 }}>
          <p style={{ color: C.textMid, marginTop: 0 }}>
            {treasuryModal === "fw"
              ? "Books: Dr CASH_BANK (net) + Dr FW_FEE_EXPENSE (fees) / Cr CASH_FLW (gross). Idempotent on the reference — re-submitting the same reference never double-books."
              : "Books: Dr CASH_MTN / Cr CASH_BANK. Idempotent on the reference — re-submitting the same reference never double-books."}
          </p>
          {treasuryModal === "fw" ? (
            <>
              <label style={{ display: "block", marginBottom: 8 }}>
                Gross amount leaving Flutterwave (UGX)
                <input
                  type="number" min="1"
                  value={treasuryForm.gross}
                  onChange={(e) => setTreasuryForm((f) => ({ ...f, gross: e.target.value }))}
                  style={{ width: "100%", padding: 6, marginTop: 3 }}
                />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                Flutterwave fees deducted (UGX, optional)
                <input
                  type="number" min="0"
                  value={treasuryForm.fwFees}
                  onChange={(e) => setTreasuryForm((f) => ({ ...f, fwFees: e.target.value }))}
                  style={{ width: "100%", padding: 6, marginTop: 3 }}
                />
              </label>
            </>
          ) : (
            <label style={{ display: "block", marginBottom: 8 }}>
              Amount moved into the MTN float (UGX)
              {treasuryForm.coversDay && (
                <div style={{ fontSize: 12, color: C.textMid, fontWeight: 400, marginTop: 2 }}>
                  Booked to {fmtDay(treasuryForm.coversDay)}, the day the wallet grew; it clears that day from "not in the books".
                </div>
              )}
              <input
                type="number" min="1"
                value={treasuryForm.amount}
                onChange={(e) => setTreasuryForm((f) => ({ ...f, amount: e.target.value }))}
                style={{ width: "100%", padding: 6, marginTop: 3 }}
              />
            </label>
          )}
          <label style={{ display: "block", marginBottom: 8 }}>
            Statement / transfer reference (required)
            <input
              type="text"
              value={treasuryForm.reference}
              onChange={(e) => setTreasuryForm((f) => ({ ...f, reference: e.target.value }))}
              placeholder={treasuryModal === "fw" ? "FW settlement id / bank ref" : "bank / MTN transfer ref"}
              style={{ width: "100%", padding: 6, marginTop: 3 }}
            />
          </label>
          <label style={{ display: "block", marginBottom: 0 }}>
            Note (optional)
            <input
              type="text"
              value={treasuryForm.note}
              onChange={(e) => setTreasuryForm((f) => ({ ...f, note: e.target.value }))}
              style={{ width: "100%", padding: 6, marginTop: 3 }}
            />
          </label>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setTreasuryModal(null)} disabled={treasurySaving}>
            Cancel
          </Button>
          <Button size="sm" onClick={submitTreasuryMove} disabled={treasurySaving} style={{ background: C.teal, border: "none" }}>
            {treasurySaving ? <Spinner size="sm" animation="border" /> : "Record"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Edit the float reserve (live, no redeploy) ── */}
      <Modal show={floatModal} onHide={() => !floatSaving && setFloatModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>MTN float reserve</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13 }}>
          <p style={{ color: C.textMid, marginTop: 0 }}>
            Manual payouts, payout batch approvals and vendor withdrawals may only spend
            the part of the MTN wallet that sits <b>above</b> this reserve. MoMo collections
            refill it as they land, net of MTN's collection fee (see Money In). Flutterwave
            card and Airtel takings do not: they settle to the bank and are topped up by
            hand, so the reserve covers payouts due before that money arrives.
          </p>
          <div style={{ background: "#f8fafc", borderRadius: 6, padding: 10, marginBottom: 12 }}>
            <div>Float now: <b>UGX {Number(treasury?.float?.balance ?? 0).toLocaleString()}</b></div>
            <div>
              Spendable at this reserve:{" "}
              <b>
                UGX{" "}
                {Math.max(
                  0,
                  Number(treasury?.float?.balance ?? 0) - (Number(floatForm.watermark) || 0)
                ).toLocaleString()}
              </b>
            </div>
          </div>
          <label style={{ display: "block", marginBottom: 10 }}>
            Reserve held back (UGX)<SettingHelp topic="floatReserve" />
            <span style={{ color: C.textMid, display: "block" }}>
              0 lets payouts use the whole balance.
              {treasury?.watermarkDefault != null
                ? ` Default UGX ${Number(treasury.watermarkDefault).toLocaleString()}${
                    treasury.watermarkSource === "setting" ? "; a value saved here is in use now"
                    : treasury.watermarkSource === "env" ? " (hosting environment)" : " (shipped)"}.`
                : ""}
            </span>
            <input
              type="number" min="0"
              value={floatForm.watermark}
              onChange={(e) => setFloatForm((f) => ({ ...f, watermark: e.target.value }))}
              style={{ width: "100%", padding: 6, marginTop: 3 }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={!!floatForm.enforced}
              onChange={(e) => setFloatForm((f) => ({ ...f, enforced: e.target.checked }))}
              style={{ marginTop: 3 }}
            />
            <span>
              Block payouts that breach the reserve.<span onClick={(e) => e.preventDefault()}><SettingHelp topic="floatEnforced" /></span>
              <span style={{ color: C.textMid, display: "block" }}>
                Unticked, a shortfall is only logged and the payout still goes to MTN, which
                refuses it if the money is not there.
                {typeof treasury?.enforcedDefault === "boolean"
                  ? ` Default ${treasury.enforcedDefault ? "ticked" : "unticked"}.`
                  : ""}
              </span>
            </span>
          </label>
          <label style={{ display: "block", marginBottom: 0 }}>
            Reason for the change (optional, kept in the audit log)
            <input
              type="text"
              value={floatForm.reason}
              onChange={(e) => setFloatForm((f) => ({ ...f, reason: e.target.value }))}
              style={{ width: "100%", padding: 6, marginTop: 3 }}
            />
          </label>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setFloatModal(false)} disabled={floatSaving}>
            Cancel
          </Button>
          <Button size="sm" onClick={submitFloatSettings} disabled={floatSaving} style={{ background: C.teal, border: "none" }}>
            {floatSaving ? <Spinner size="sm" animation="border" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Submission stopped by the reserve ── */}
      <Modal show={!!floatBlock} onHide={() => setFloatBlock(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>The reserve is blocking this payout</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13 }}>
          {floatBlock && (
            <>
              <table style={{ width: "100%", marginBottom: 12 }}>
                <tbody>
                  <tr><td style={{ color: C.textMid }}>This submission needs</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>UGX {floatBlock.required.toLocaleString()}</td></tr>
                  <tr><td style={{ color: C.textMid }}>MTN disbursement float holds</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>UGX {floatBlock.balance.toLocaleString()}</td></tr>
                  <tr><td style={{ color: C.textMid }}>Reserve held back</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>UGX {floatBlock.watermark.toLocaleString()}</td></tr>
                  <tr><td style={{ color: C.textMid }}>Leaving spendable</td>
                      <td style={{ textAlign: "right", fontWeight: 600, color: C.red }}>UGX {floatBlock.spendable.toLocaleString()}</td></tr>
                </tbody>
              </table>
              <p style={{ color: C.textMid }}>
                The float is not short of the payout itself; it is short of the payout
                <b> plus the reserve</b>. Wait for MoMo collections to land (they refill the
                same MTN wallet), top it up from the bank once Flutterwave has settled, lower
                the reserve, or push this one through.
              </p>
              {isSuperAdminUser ? (
                <label style={{ display: "block", marginTop: 12 }}>
                  Reason for overriding (required, at least 5 characters, kept in the audit log)
                  <input
                    type="text"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="e.g. float top-up already sent, settling now"
                    style={{ width: "100%", padding: 6, marginTop: 3 }}
                  />
                </label>
              ) : (
                <p style={{ color: C.textMid, marginBottom: 0 }}>
                  Only a super administrator can change the reserve or override this check.
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setFloatBlock(null)}>
            Cancel
          </Button>
          {isSuperAdminUser && (
            <>
              <Button size="sm" variant="light" onClick={() => { setFloatBlock(null); openFloatModal(); }}>
                Lower the reserve
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const reason = overrideReason.trim();
                  if (reason.length < 5) {
                    notify.error("Give a reason of at least 5 characters to override.");
                    return;
                  }
                  setFloatBlock(null);
                  handleManualSubmit({ override: { reason } });
                }}
                style={{ background: C.orange, border: "none" }}
              >
                Override once
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Tab Switcher: one row on a wide screen, an even grid on a phone
          (AppTabs). The selected tab is filled in the action navy (Izi,
          31 Oct 2026); the page's actions for the open tab sit beside the
          tabs, or on their own row on a phone. */}
      <AppTabs
        ariaLabel="Wallet sections"
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: "moneyIn",         label: "Money In",        icon: <FiDownload size={13} /> },
          { id: "disbursements",   label: "Disbursements",   icon: <FiList size={13} /> },
          { id: "batches",         label: "Payout Batches",  icon: <FiPackage size={13} /> },
          { id: "manual",          label: "Manual Payout",   icon: <FiSend size={13} /> },
          { id: "bulk",            label: "Bulk Upload",     icon: <FiPlusCircle size={13} /> },
          { id: "commission",      label: "Commission",      icon: <FiPercent size={13} /> },
          { id: "reconciliation",  label: "Reconciliation",  icon: <FiActivity size={13} />, badge: reconStats?.unresolved > 0 ? reconStats.unresolved : null },
          { id: "reports",         label: "Reports",         icon: <FiFileText size={13} /> },
          { id: "refund",          label: "Customer Refund", icon: <FiRotateCcw size={13} /> },
        ]}
        actions={
          activeTab === "batches" ? (
            <>
              <button style={S.btn("primary")} onClick={() => setInitiateModal("rider")}>
                <FiUsers size={13} /> Rider Batch
              </button>
              <button style={{ ...S.btn("primary"), background: C.purple, borderColor: C.purple }} onClick={() => setInitiateModal("partner")}>
                <FiSend size={13} /> Partner Batch
              </button>
            </>
          ) : activeTab === "reconciliation" ? (
            <button style={{ ...S.btn("primary"), background: C.teal, borderColor: C.teal }} onClick={() => setReconRunModal(true)}>
              <FiActivity size={13} /> Run Reconciliation
            </button>
          ) : null
        }
      />

      {/* ── Money In (tab) ───────────────────────────────────────── */}
      {activeTab === "moneyIn" && (
        <MoneyInTab
          isSuperAdmin={isSuperAdminUser}
          rangeKey={moneyInFocus > 0 ? "all" : "month"}
          focusToken={moneyInFocus}
          onRecordTopUp={(prefill) => openTreasuryModal("topup", prefill)}
        />
      )}

      {/* ── Disbursements Table (tab) ────────────────────────────── */}
      {activeTab === "disbursements" && (
      <div style={S.panel}>
        <div style={S.panelTitle}>
          <FiList size={16} color={C.blue} />
          Disbursements
          <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, fontWeight: 400 }}>
            {pagination.total} total
          </span>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 240 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <FiSearch size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.textLight }} />
              <input
                style={{ ...S.input, paddingLeft: 30 }}
                placeholder="Reference ID…"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
              />
            </div>
            <button type="submit" style={S.btn("primary")}>Search</button>
          </form>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["", "SUCCESSFUL", "PROCESSING", "FAILED", "QUEUED"].map((s) => (
              <button
                key={s}
                style={S.filterBtn(filterStatus === s)}
                onClick={() => handleStatusFilter(s)}
              >
                {s || "All"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: C.textLight }}>
            <Spinner animation="border" size="sm" style={{ color: C.blue }} />
            <div style={{ marginTop: 8, fontSize: 13 }}>Loading disbursements…</div>
          </div>
        ) : disbursements.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: C.textLight }}>
            <FiList size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div style={{ fontSize: 13 }}>No disbursements found</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}`, boxShadow: "0 1px 8px rgba(26,43,74,0.07)" }}>
            <table style={{ ...S.table, borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: C.header }}>
                  {[
                    { label: "REFERENCE ID",   w: 150 },
                    { label: "STATUS",          w: 130 },
                    { label: "PAYEE",           w: 170 },
                    { label: "AMOUNT (UGX)",      w: 110, align: "right" },
                    { label: "MTN FEE (UGX)",     w: 100, align: "right" },
                    { label: "TOTAL DEBIT (UGX)", w: 120, align: "right" },
                    { label: "SUBMITTED",       w: 210 },
                    { label: "CONFIRMATION",    w: 120 },
                    { label: "FAILURE / SOURCE", w: 200 },
                    { label: "ACTIONS",         w: 110, align: "center" },
                  ].map((col) => (
                    <th key={col.label} style={{ ...S.gridTh(col.align), minWidth: col.w }}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {disbursements.map((d, rowIdx) => {
                  const meta = STATUS_META[d.status] || { color: "blue", icon: <FiClock size={12} /> };
                  const isEven = rowIdx % 2 === 0;
                  const rowBg  = isEven ? "#ffffff" : "#f7f9fd";
                  const tdBase = S.gridTd(rowBg);

                  return (
                    <tr key={d._id}
                      onMouseEnter={(e) => { Array.from(e.currentTarget.cells).forEach((c) => { c.style.background = "#eef3fc"; }); }}
                      onMouseLeave={(e) => { Array.from(e.currentTarget.cells).forEach((c) => { c.style.background = rowBg; }); }}
                    >
                      {/* Reference ID: the short reference on the payee's MoMo
                          statement first (it is what they will quote), our full
                          disbursement id under it. Both copy in one click. */}
                      <td style={tdBase}>
                        {d.payeeReference ? (
                          <>
                            <CopyValue value={d.payeeReference} strong color={C.text} />
                            <div style={S.cellMeta}><CopyValue value={d.disbursementId} max={18} /></div>
                          </>
                        ) : (
                          <CopyValue value={d.disbursementId} max={20} strong color={C.text} />
                        )}
                      </td>

                      {/* Status */}
                      <td style={tdBase}>
                        <span style={S.badge(meta.color)}>
                          {meta.icon} {d.status}
                          </span>
                        {anyInFlight([d]) && (
                          <div style={{ marginTop: 3 }}>
                            <StageTimer row={d} scheduler={scheduler} now={now} />
                          </div>
                        )}
                      </td>

                      {/* Payee */}
                      <td style={tdBase}>
                        <CellStack strong meta={d.payeeName ? d.payeePhone : null}>
                          {d.payeeName || d.payeePhone || "—"}
                        </CellStack>
                        {d.payeeType && (
                          <span style={{ ...S.badge(PAYEE_TYPE_TONE[d.payeeType]), marginTop: 5 }}>{d.payeeType}</span>
                        )}
                      </td>

                      {/* Amount */}
                      <td style={{ ...tdBase, ...S.cellNum, ...S.cellStrong }}>{fmtNum(d.amount)}</td>

                      {/* MTN Fee */}
                      <td style={{ ...tdBase, ...S.cellNum }}>{fmtNum(d.mtnFeeAbsorbed)}</td>

                      {/* Total Debit */}
                      <td style={{ ...tdBase, ...S.cellNum, ...S.cellStrong, background: isEven ? "#f0f4ff" : "#e8eefa" }}>{fmtNum(d.totalMomoDebit)}</td>

                      {/* Submitted + lifecycle timeline */}
                      <td style={{ ...tdBase, color: C.textMid }}>
                        {d.submittedAt || d.createdAt
                          ? <>
                              <div style={{ ...S.cellStrong, whiteSpace: "nowrap" }}>
                                {fmtDay(d.submittedAt || d.createdAt)}
                                <span style={{ fontWeight: 400, color: C.textLight, marginLeft: 6, fontSize: 11 }}>
                                  {new Date(d.submittedAt || d.createdAt).toLocaleTimeString("en-UG", { hour: "2-digit", minute: "2-digit" })}
                                  {!d.submittedAt && " · not yet submitted"}
                                </span>
                              </div>
                              <TimelineLine row={d} />
                            </>
                          : "—"
                        }
                      </td>

                      {/* Confirmation (resolvedVia-aware) */}
                      <td style={tdBase}>
                        <ConfirmationChip row={d} />
                        {d.status === "SUCCESSFUL" && d.momoFinancialTxId && (
                          <div style={S.cellMeta}>
                            <CopyValue value={d.momoFinancialTxId} label="on the payee's SMS" />
                          </div>
                        )}
                      </td>

                      {/* Failure / Source. A failure is this row's STATE only
                          while it is not SUCCESSFUL; a refused attempt before
                          a retry that went through is a quiet note. */}
                      <td style={{ ...tdBase, maxWidth: 200, fontSize: 11 }}>
                        {d.failureReason && d.status !== "SUCCESSFUL" ? (
                          <div>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 4, color: "#c0152f" }}>
                              <FiAlertCircle size={11} style={{ flexShrink: 0, marginTop: 2 }} />
                              <span style={{ wordBreak: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                                {d.failureReason}
                              </span>
                            </div>
                            {d.failureCode && d.failureCode !== "UNKNOWN" && (
                              <div style={{ color: C.textLight, marginTop: 3, fontFamily: S.cellMono.fontFamily, background: "#f7f9fd", borderRadius: 3, padding: "1px 5px", display: "inline-block" }}>
                                {d.failureCode}
                              </div>
                            )}
                            {d.triggerType && (
                              <div style={{ color: C.textLight, marginTop: 3 }}>
                                Via: {d.triggerType.replace(/_/g, " ")}
                              </div>
                            )}
                          </div>
                        ) : d.triggerType ? (
                          <div>
                            <span style={{ color: C.textMid, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              {d.triggerType.replace(/_/g, " ")}
                            </span>
                            {(d.previousAttemptFailure?.reason || (d.status === "SUCCESSFUL" && d.failureReason)) && (
                              <div style={{ color: C.textLight, marginTop: 3, whiteSpace: "normal", lineHeight: 1.4 }}>
                                Retry succeeded; attempt {Number(d.previousAttemptFailure?.attempt ?? d.retryCount ?? 0) + 1 || 1} was refused: {d.previousAttemptFailure?.reason || d.failureReason}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: C.border }}>—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ ...tdBase, textAlign: "center" }}>
                        {d.status === "FAILED" && (
                          <button
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              padding: "5px 12px", borderRadius: 5,
                              fontSize: 12, fontWeight: 600, cursor: retryingId === d.disbursementId ? "not-allowed" : "pointer",
                              background: retryingId === d.disbursementId ? "#f5f5f5" : "#fff4e6",
                              color: C.orange, border: `1.5px solid ${C.orange}`,
                              opacity: retryingId === d.disbursementId ? 0.6 : 1,
                              transition: "all 0.15s",
                            }}
                            disabled={!!retryingId}
                            onClick={() => handleRetry(d.disbursementId)}
                          >
                            <FiRotateCcw size={11}
                              style={{ animation: retryingId === d.disbursementId ? "spin 1s linear infinite" : "none" }}
                            />
                            {retryingId === d.disbursementId ? "…" : "Retry"}
                          </button>
                        )}
                        <div style={{ marginTop: d.status === "FAILED" ? 5 : 0 }}>
                          <AuditLink onClick={() => openAudit(d)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}>
            <button style={S.btn("ghost")} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
            <span style={{ padding: "8px 16px", fontSize: 13, color: C.textMid }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <button style={S.btn("ghost")} disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </div>
      )} {/* end disbursements tab */}

      {/* ── Batches Tab ─────────────────────────────────────────── */}
      {activeTab === "batches" && (
      <div style={S.panel}>
        <div style={S.panelTitle}>
          <FiPackage size={16} color={C.purple} />
          Payout Batches
          {pendingApprovalCount > 0 && (
            <span style={{ marginLeft: 8, background: "rgba(230,55,87,0.15)", border: "1px solid rgba(230,55,87,0.4)", borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 600, color: C.red }}>
              {pendingApprovalCount} awaiting approval
            </span>
          )}
          <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, fontWeight: 400 }}>
            {batchPagination.total} total
          </span>
        </div>

        {pendingApprovalCount > 0 && (
          <div style={{ background: "rgba(230,55,87,0.08)", border: "1px solid rgba(230,55,87,0.25)", borderRadius: 8, padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
            <FiAlertCircle size={15} color={C.red} style={{ flexShrink: 0 }} />
            <span style={{ color: C.text }}>
              <strong style={{ color: C.red }}>{pendingApprovalCount} batch{pendingApprovalCount > 1 ? "es" : ""} awaiting your approval.</strong>
              {" "}Review each batch's payees and amounts before approving — money moves immediately once approved.
            </span>
          </div>
        )}

        {/* ── Batch Filter Bar ── */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          <form onSubmit={handleBatchSearch} style={{ display: "flex", gap: 8, flex: 1, minWidth: 220 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <FiSearch size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.textLight }} />
              <input
                style={{ ...S.input, paddingLeft: 30 }}
                placeholder="Search by Batch ID…"
                value={batchSearchId}
                onChange={(e) => setBatchSearchId(e.target.value)}
              />
            </div>
            <button type="submit" style={S.btn("primary")}>Search</button>
          </form>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: C.textLight, marginRight: 2 }}>Status:</span>
            {["", "AWAITING_APPROVAL", "APPROVED", "PROCESSING", "COMPLETED", "PARTIALLY_COMPLETED", "FAILED", "REJECTED"].map((s) => (
              <button key={s}
                style={{ ...S.btn("ghost"), background: batchFilterStatus === s ? C.blueSoft : "#f0f4fa", color: batchFilterStatus === s ? C.blue : C.textMid, padding: "5px 10px", fontSize: 11 }}
                onClick={() => handleBatchStatusFilter(s)}
              >{s || "All"}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: C.textLight, marginRight: 2 }}>Type:</span>
            {["", "RIDER_WEEKLY", "PARTNER_BIMONTHLY"].map((t) => (
              <button key={t}
                style={{ ...S.btn("ghost"), background: batchFilterType === t ? C.blueSoft : "#f0f4fa", color: batchFilterType === t ? C.blue : C.textMid, padding: "5px 10px", fontSize: 11 }}
                onClick={() => handleBatchTypeFilter(t)}
              >{t === "RIDER_WEEKLY" ? "Rider" : t === "PARTNER_BIMONTHLY" ? "Partner" : "All"}</button>
            ))}
          </div>
          {(batchSearchId || batchFilterStatus || batchFilterType) && (
            <button style={{ ...S.btn("ghost"), padding: "5px 10px", fontSize: 11, color: C.red }} onClick={clearBatchFilters}>
              <FiXCircle size={11} /> Clear
            </button>
          )}
        </div>

        {batchLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spinner animation="border" style={{ color: C.blue }} /></div>
        ) : batches.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: C.textLight, fontSize: 13 }}>No batches found</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["#", "Batch", "Type", "Status", "Payees", "Net Amount", "MoMo Debit", "Period", "Actions"].map((h) => (
                    <th key={h} style={
                      h === "#" ? { ...S.th, width: 36, textAlign: "center" }
                        : ["Payees", "Net Amount", "MoMo Debit"].includes(h) ? { ...S.th, textAlign: "right" }
                          : S.th
                    }>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {batches.map((b, idx) => {
                  const bMeta = BATCH_STATUS_META[b.status] || { color: "blue", label: b.status };
                  const needsApproval = b.status === "AWAITING_APPROVAL";
                  const rowNum = (batchPagination.page - 1) * 20 + idx + 1;
                  return (
                    <tr key={b._id}
                      style={{ transition: "background 0.1s", cursor: "pointer" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.inputBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      onClick={() => openBatchDetail(b.batchId)}
                    >
                      <td style={{ ...S.td, textAlign: "center", fontSize: 11, color: C.textLight, fontWeight: 600 }}>{rowNum}</td>
                      {/* The "batch-" prefix repeats on every row, so the cell shows the part that differs. */}
                      <td style={S.td} title={`${b.batchId}${b.createdAt ? `, created ${fmtDate(b.createdAt)}` : ""}`}>
                        <CellStack meta={b.createdAt ? `Created ${fmtDay(b.createdAt)}` : null}>
                          <span style={S.cellMono}>{truncate(String(b.batchId || "").replace(/^batch-/, ""), 13)}</span>
                        </CellStack>
                      </td>
                      <td style={S.td}>
                        <CellStack meta={b.batchType === "RIDER_WEEKLY" ? "Weekly" : b.batchType === "PARTNER_BIMONTHLY" ? "Bimonthly" : null}>
                          <span style={S.badge(b.batchType === "RIDER_WEEKLY" ? "blue" : "purple")}>
                            {b.batchType === "RIDER_WEEKLY" ? <FiUsers size={11} /> : <FiSend size={11} />}
                            {" "}{b.batchType === "RIDER_WEEKLY" ? "Rider" : b.batchType === "PARTNER_BIMONTHLY" ? "Partner" : b.batchType}
                          </span>
                        </CellStack>
                      </td>
                      <td style={S.td}><span style={S.badge(bMeta.color)}>{b.status}</span></td>
                      <td style={{ ...S.td, ...S.cellNum }}>{b.totalPayees}</td>
                      <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{fmtUGX(b.totalNetAmount)}</td>
                      <td style={{ ...S.td, ...S.cellNum }}>{fmtUGX(b.totalMomoDebit)}</td>
                      <td style={S.td}><PeriodCell start={b.periodStart} end={b.periodEnd} /></td>
                      <td style={{ ...S.td }} onClick={(e) => e.stopPropagation()}>
                        {needsApproval && (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              style={S.btnSm("teal")}
                              onClick={() => setConfirmApprove(b.batchId)}
                            >
                              <FiThumbsUp size={12} /> Approve
                            </button>
                            <button
                              style={S.btnSm("danger")}
                              onClick={() => { setConfirmReject(b.batchId); setRejectReason(""); }}
                            >
                              <FiThumbsDown size={12} /> Reject
                            </button>
                          </div>
                        )}
                        {!needsApproval && (
                          <button
                            style={S.btnSm("action")}
                            onClick={() => openBatchDetail(b.batchId)}
                          >
                            Detail <FiChevronRight size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {batchPagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}>
            <button style={S.btn("ghost")} disabled={batchPage <= 1} onClick={() => setBatchPage((p) => p - 1)}>Prev</button>
            <span style={{ padding: "8px 16px", fontSize: 13, color: C.textMid }}>Page {batchPagination.page} of {batchPagination.pages}</span>
            <button style={S.btn("ghost")} disabled={batchPage >= batchPagination.pages} onClick={() => setBatchPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </div>
      )} {/* end batches tab */}

      {/* ── Manual Payout Tab ────────────────────────────────────── */}
      {activeTab === "manual" && (
      <div style={S.panel}>
        <div style={S.panelTitle}>
          <FiSend size={16} color={C.orange} />
          Manual Payout
          <span style={{ marginLeft: 8, fontSize: 12, color: C.textLight, fontWeight: 400 }}>
            Immediately queue a payment to a vendor, rider, partner or customer, to Lusuku staff, or to anyone with an MTN number. Submitted to MTN within seconds; confirmed once MTN answers.
          </span>
        </div>

        {/* ── Warning banner ── */}
        <div style={{ background: "rgba(246,150,30,0.10)", border: `1px solid rgba(246,150,30,0.35)`, borderRadius: 8, padding: "10px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
          <FiAlertTriangle size={15} color={C.orange} style={{ flexShrink: 0 }} />
          <span style={{ color: C.text }}>
            <strong style={{ color: C.orange }}>No approval step.</strong>
            {" "}Payouts are queued immediately and submitted to MTN MoMo within seconds (at most 5 minutes on an older wallet deploy). Every number is checked with MTN and the registered name shown under it before Send is enabled; confirm it is the person you mean to pay. Idempotency protection prevents exact duplicates.
          </span>
        </div>

        {/* ── Payee rows ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
          {manualRows.map((row, idx) => {
            const fee   = estimateFee(row.amount);
            const total = Number(row.amount || 0) + fee;
            const isExternal = row.payeeType === "EXTERNAL";
            const hasEntity = rowPayeeKnown(row);
            const phoneChanged = row.phone && row.registeredPhone && row.phone !== row.registeredPhone;
            // Per-row inline validation (only show errors if the field was touched / has a value)
            const errNoPayee   = !hasEntity && row._searchQuery.length > 0;
            const errNoPhone   = hasEntity && !row.phone.trim();
            const phoneNetwork = row.phone.trim() ? detectPhoneNetwork(row.phone) : null;
            const errAirtel    = phoneNetwork === "AIRTEL";
            const errUnknownPhone = phoneNetwork === "UNKNOWN";
            const errBadAmount = row.amount !== "" && Number(row.amount) < 500;
            const errNoOrder   = row.purpose === "REFUND" && hasEntity && !row.linkedOrderId && row._orderQuery.length > 0;
            const reasonRequired = isExpenseType(row.payeeType) || row.purpose === "OTHER";
            const reasonProblem  = rowReasonProblem(row);
            const errNoNote    = reasonRequired && hasEntity && row.amount !== "" && !!reasonProblem && row.purpose !== "REFUND";
            return (
              <div key={idx} id={`manual-payout-row-${idx}`} style={{ background: C.inputBg, border: `1px solid ${hasEntity ? C.blue + "55" : C.border}`, borderRadius: 8, padding: "14px 16px", position: "relative" }}>
                {/* Row number badge */}
                <div style={{ position: "absolute", top: 10, left: -12, background: hasEntity ? C.blue : C.textLight, color: "#fff", borderRadius: 10, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600 }}>
                  {idx + 1}
                </div>

                {/* ── Row 1: Type · Entity Search · Purpose ── */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                  {/* Payee Type */}
                  <div style={{ minWidth: 115 }}>
                    <label style={{ fontSize: 11, color: C.textLight, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Type</label>
                    <AppSelect
                      style={{ ...S.input, paddingRight: 8 }}
                      value={row.payeeType}
                      onChange={(e) => handlePayeeTypeChange(idx, e.target.value)}
                    >
                      {(payoutPurposes.payeeTypes || []).map((t) => (
                        <option key={t} value={t}>{PAYEE_TYPE_LABEL[t] || t}</option>
                      ))}
                    </AppSelect>
                  </div>

                  {/* An external payee is anyone with an MTN number: named by hand, not searched */}
                  {isExternal ? (
                    <div style={{ flex: "1 1 220px" }}>
                      <label style={{ fontSize: 11, color: C.textLight, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Payee name <span style={{ color: C.red }}>*</span>
                      </label>
                      <input
                        style={{ ...S.input, background: hasEntity ? "rgba(37,117,252,0.06)" : S.input.background }}
                        placeholder="Who is being paid, as the books will show it"
                        title="Someone not on Lusuku. MTN still confirms the number before Send opens."
                        value={row.payeeName}
                        maxLength={80}
                        onChange={(e) => handleManualRowChange(idx, "payeeName", e.target.value)}
                      />
                    </div>
                  ) : (
                  <div style={{ flex: "1 1 220px", position: "relative" }}>
                    <label style={{ fontSize: 11, color: errNoPayee ? C.red : C.textLight, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Search {row.payeeType.charAt(0) + row.payeeType.slice(1).toLowerCase()} <span style={{ color: C.red }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <FiSearch size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.textLight, pointerEvents: "none" }} />
                      <input
                        style={{ ...S.input, paddingLeft: 30, background: hasEntity ? "rgba(37,117,252,0.06)" : S.input.background, borderColor: errNoPayee ? C.red : undefined }}
                        placeholder={row.payeeType === "STAFF" ? "Name, phone or email" : "Name or phone"}
                        value={row._searchQuery}
                        onChange={(e) => handleEntitySearchChange(idx, e.target.value)}
                        onFocus={() => {
                          if (hasEntity) {
                            // Allow re-search by clearing selection
                            setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, _searchResults: [] } : r));
                          }
                        }}
                      />
                      {row._searchLoading && (
                        <Spinner animation="border" size="sm" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: C.blue }} />
                      )}
                    </div>
                    {errNoPayee && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>Select a result from the list</div>}
                    {/* Dropdown results */}
                    {row._searchResults.length > 0 && (
                      <div style={{ position: "absolute", zIndex: 200, top: "100%", left: 0, right: 0, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", maxHeight: 220, overflowY: "auto" }}>
                        {row._searchResults.map((entity) => (
                          <div
                            key={entity.id}
                            style={{ padding: "9px 14px", cursor: "pointer", borderBottom: `1px solid ${C.border}`, transition: "background 0.1s" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = C.inputBg; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                            onClick={() => handleSelectEntity(idx, entity)}
                          >
                            <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{entity.label}</div>
                            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{entity.subLabel}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Purpose: the set depends on who is being paid */}
                  <div style={{ minWidth: 150 }}>
                    <label style={{ fontSize: 11, color: C.textLight, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Purpose</label>
                    <AppSelect
                      style={{ ...S.input, paddingRight: 8 }}
                      value={row.purpose}
                      onChange={(e) => handleManualRowChange(idx, "purpose", e.target.value)}
                    >
                      {purposesFor(row.payeeType).map((p) => (
                        <option key={p} value={p}>{purposeLabel(p)}</option>
                      ))}
                    </AppSelect>
                  </div>

                  {/* Remove button */}
                  {manualRows.length > 1 && (
                    <button
                      style={{ ...S.btn("danger"), padding: "8px 10px", alignSelf: "flex-end", opacity: 0.8 }}
                      onClick={() => handleManualRemoveRow(idx)}
                      title="Remove row"
                    >
                      <FiXCircle size={14} />
                    </button>
                  )}
                </div>

                {/* ── Row 2: Selected entity info pill + Phone + Amount + Note ── */}
                {hasEntity && !isExternal && (
                  <div style={{ background: "rgba(37,117,252,0.06)", border: `1px solid rgba(37,117,252,0.18)`, borderRadius: 6, padding: "6px 12px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 12 }}>
                    <FiCheckCircle size={13} color={C.blue} />
                    <strong style={{ color: C.text }}>{row.payeeName}</strong>
                    {row.registeredPhone && (
                      <span style={{ color: C.textLight }}>Registered: <span style={{ fontFamily: "monospace", color: C.textMid }}>{row.registeredPhone}</span></span>
                    )}
                    <button
                      style={{ ...S.btn("ghost"), padding: "2px 8px", fontSize: 11, marginLeft: "auto" }}
                      onClick={() => setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, payeeId: "", payeeName: "", registeredPhone: "", phone: "", phoneWasOverridden: false, _searchQuery: "", _searchResults: [] } : r))}
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* ── Row 3: MTN Phone · Amount · Note ── */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                  {/* MTN Phone — pre-filled from entity, editable */}
                  <div style={{ flex: "1 1 160px" }}>
                    <label style={{ fontSize: 11, color: errNoPhone || errAirtel || errUnknownPhone ? C.red : C.textLight, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      MTN Phone <span style={{ color: C.red }}>*</span>
                      {phoneChanged && !errAirtel && <span style={{ color: C.orange, marginLeft: 6, textTransform: "none" }}>⚠ differs from registered</span>}
                      {phoneNetwork === "MTN" && <span style={{ color: "#2e7d32", marginLeft: 6, textTransform: "none", fontWeight: 600 }}>✓ MTN</span>}
                      {errAirtel && <span style={{ color: C.red, marginLeft: 6, textTransform: "none", fontWeight: 600 }}>✗ Airtel — not supported</span>}
                      {errUnknownPhone && <span style={{ color: C.red, marginLeft: 6, textTransform: "none" }}>✗ Unrecognised</span>}
                    </label>
                    <input
                      style={{ ...S.input, borderColor: errNoPhone || errAirtel || errUnknownPhone ? C.red : phoneNetwork === "MTN" ? "#2e7d32" : phoneChanged ? C.orange : undefined }}
                      placeholder="07XXXXXXXX"
                      value={row.phone}
                      onChange={(e) => {
                        const newPhone = e.target.value;
                        const overridden = !!(row.registeredPhone && newPhone !== row.registeredPhone);
                        setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, phone: newPhone, phoneWasOverridden: overridden } : r));
                        schedulePayeeLookup(idx, newPhone);
                      }}
                    />
                    {errNoPhone && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>MTN phone is required</div>}
                    {errAirtel && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>Airtel Money payouts are coming soon — use an MTN number (077, 078, 076)</div>}
                    {errUnknownPhone && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>Unrecognised number — use format 07XXXXXXXX</div>}
                    {/* Who the money would reach, from MTN. Send stays off until this says the number is live. */}
                    {phoneNetwork === "MTN" && (() => {
                      const l = lookupFor(row);
                      if (!l) return null;
                      if (l.status === "loading") {
                        return (
                          <div style={{ fontSize: 11, color: C.textMid, marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
                            <Spinner animation="border" size="sm" style={{ width: 11, height: 11, borderWidth: 2 }} /> Checking who this number is registered to
                          </div>
                        );
                      }
                      if (l.status === "error") {
                        return (
                          <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>
                            {l.message}{" "}
                            <button type="button" onClick={() => schedulePayeeLookup(idx, row.phone)} style={{ background: "none", border: 0, padding: 0, color: C.blue, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Try again</button>
                          </div>
                        );
                      }
                      if (!l.active) {
                        return <div style={{ fontSize: 11, color: C.red, marginTop: 3, fontWeight: 600 }}>Not registered on MTN MoMo. A payout to this number would fail.</div>;
                      }
                      if (!l.nameAvailable) {
                        return <div style={{ fontSize: 11, color: "#2e7d32", marginTop: 3 }}>Live on MTN MoMo. MTN did not disclose the registered name.</div>;
                      }
                      const differs = namesLookDifferent(row.payeeName, l.registeredName);
                      // Two names, two sources, side by side: the account on
                      // Lusuku the operator picked, and the subscriber MTN
                      // has on the number. They are often not the same
                      // person, and the operator decides whether that is right.
                      return (
                        <div style={{ fontSize: 12, marginTop: 4, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 8, rowGap: 1 }}>
                          <span style={{ color: C.textLight }}>On Lusuku</span>
                          <span style={{ color: C.text, fontWeight: 600 }}>{row.payeeName || "No payee selected"}</span>
                          <span style={{ color: C.textLight }}>On MTN MoMo</span>
                          <span style={{ color: "#1b5e20", fontWeight: 600 }}>{l.registeredName}</span>
                          {differs && (
                            <div style={{ gridColumn: "1 / -1", fontSize: 11, color: C.orange, marginTop: 2 }}>
                              The MTN name shares no word with the Lusuku account. Confirm this is the right number before sending.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Amount */}
                  <div style={{ flex: "1 1 130px" }}>
                    <label style={{ fontSize: 11, color: errBadAmount ? C.red : C.textLight, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Amount (UGX) <span style={{ color: C.red }}>*</span></label>
                    <input
                      style={{ ...S.input, borderColor: errBadAmount ? C.red : undefined }}
                      type="number"
                      min="500"
                      placeholder="500"
                      value={row.amount}
                      onChange={(e) => handleManualRowChange(idx, "amount", e.target.value)}
                    />
                    {errBadAmount && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>Minimum is UGX 500</div>}
                  </div>

                  {/* Note */}
                  <div style={{ flex: "1 1 200px" }}>
                    <label style={{ fontSize: 11, color: errNoNote ? C.red : C.textLight, display: "flex", justifyContent: "space-between", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      <span>{reasonRequired ? "Reason" : "Note"} {reasonRequired ? <span style={{ color: C.red }}>*</span> : "(optional)"}</span>
                      <span style={{ textTransform: "none", letterSpacing: 0, color: row.note.length >= PAYEE_NOTE_MAX ? C.red : C.textLight }}>{row.note.length}/{PAYEE_NOTE_MAX}</span>
                    </label>
                    <input
                      style={{ ...S.input, borderColor: errNoNote ? C.red : undefined }}
                      placeholder={reasonRequired ? "Required: what this pays for, as the payee will read it" : "What the payee will see as the reason"}
                      value={row.note}
                      maxLength={PAYEE_NOTE_MAX}
                      title="Reaches the payee as the Reason line of their MTN SMS, after the short Lusuku reference"
                      onChange={(e) => handleManualRowChange(idx, "note", e.target.value)}
                    />
                    {errNoNote && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>{reasonProblem}</div>}
                  </div>
                </div>

                {/* ── Row 4: REFUND — Order linkage search ── */}
                {row.purpose === "REFUND" && (
                  <div style={{ marginTop: 10, position: "relative" }}>
                    <label style={{ fontSize: 11, color: C.red, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Linked Order <span style={{ color: C.red }}>*</span>
                      <span style={{ color: C.textLight, textTransform: "none", marginLeft: 6, fontWeight: 400 }}>Search by customer name, phone, or paste Order ID</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <FiSearch size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.textLight, pointerEvents: "none" }} />
                      <input
                        style={{ ...S.input, paddingLeft: 30, borderColor: row.linkedOrderId ? C.teal : C.border, background: row.linkedOrderId ? "rgba(0,182,155,0.06)" : undefined }}
                        placeholder="Customer name, phone or order ID…"
                        value={row._orderQuery}
                        onChange={(e) => handleOrderSearchChange(idx, e.target.value)}
                      />
                      {row._orderLoading && (
                        <Spinner animation="border" size="sm" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14 }} />
                      )}
                    </div>
                    {row._orderResults.length > 0 && (
                      <div style={{ position: "absolute", zIndex: 200, top: "100%", left: 0, right: 0, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", maxHeight: 200, overflowY: "auto" }}>
                        {row._orderResults.map((order) => (
                          <div
                            key={order.id}
                            style={{ padding: "9px 14px", cursor: "pointer", borderBottom: `1px solid ${C.border}`, transition: "background 0.1s" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = C.inputBg; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                            onClick={() => handleSelectOrder(idx, order)}
                          >
                            <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{order.label}</div>
                            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{order.subLabel}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {errNoOrder && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>No results — check the order ID or customer phone</div>}
                    {row.linkedOrderId && (
                      <div style={{ marginTop: 6, fontSize: 12, color: C.teal, display: "flex", alignItems: "center", gap: 6 }}>
                        <FiCheckCircle size={12} />
                        {row.linkedOrderLabel || row.linkedOrderId}
                        <button
                          style={{ ...S.btn("ghost"), padding: "1px 7px", fontSize: 11, marginLeft: 4 }}
                          onClick={() => setManualRows((prev) => prev.map((r, i) => i === idx ? { ...r, linkedOrderId: "", linkedOrderLabel: "", _orderQuery: "", _orderResults: [] } : r))}
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Fee preview ── */}
                {row.amount && Number(row.amount) >= 500 && (
                  <div style={{ marginTop: 10, display: "flex", gap: 16, fontSize: 11, color: C.textLight, paddingTop: 8, borderTop: `1px dashed ${C.border}` }}>
                    <span>Net to payee: <strong style={{ color: C.text }}>{fmtUGX(row.amount)}</strong></span>
                    <span>MTN fee (est.): <strong style={{ color: C.orange }}>{fmtUGX(fee)}</strong></span>
                    <span>Total MoMo debit: <strong style={{ color: C.text }}>{fmtUGX(total)}</strong></span>
                    {row.purpose !== "PAYOUT" && <span style={{ color: C.blue }}>Purpose: <strong>{purposeLabel(row.purpose)}</strong></span>}
                    {isExpenseType(row.payeeType) && <span style={{ color: C.orange }}>Booked as operating expense</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Add row + totals + submit ── */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
          <button
            style={{ ...S.btn("ghost"), border: `1px dashed ${C.border}` }}
            onClick={handleManualAddRow}
            disabled={manualLoading}
          >
            <FiPlusCircle size={14} /> Add Payee
          </button>

          {manualRows.length > 0 && (() => {
            const totalNet  = manualRows.reduce((s, r) => s + Number(r.amount || 0), 0);
            const totalFees = manualRows.reduce((s, r) => s + estimateFee(r.amount), 0);
            const totalDebit = totalNet + totalFees;
            // Wraps on a phone: the totals line takes its own row and the
            // Send button drops under it. Unwrapped, this row and the history
            // toolbar below ran past the panel and gave the whole page a
            // horizontal scroll, so the form's left edge sat off screen
            // (24 Oct 2026).
            return (
              <div style={{ marginLeft: "auto", display: "flex", gap: "10px 20px", fontSize: 13, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
                <span style={{ color: C.textMid, flex: "1 1 220px" }}>
                  {manualRows.length} payee{manualRows.length !== 1 ? "s" : ""} ·
                  Net: <strong>{fmtUGX(totalNet)}</strong> ·
                  Fees: <strong style={{ color: C.orange }}>{fmtUGX(totalFees)}</strong> ·
                  Total debit: <strong style={{ color: C.text }}>{fmtUGX(totalDebit)}</strong>
                </span>
                <button
                  style={{
                    ...S.btn("primary"),
                    background: (manualLoading || !isFormReady) ? C.textLight : C.orange,
                    minWidth: 140,
                    opacity: (manualLoading || !isFormReady) ? 0.55 : 1,
                    cursor: (!manualLoading && !isFormReady) ? "not-allowed" : undefined,
                  }}
                  onClick={isFormReady && !manualLoading ? handleManualSubmit : undefined}
                  disabled={manualLoading || !isFormReady}
                  title={!isFormReady ? "Fill in all required fields and wait for MTN to confirm each number before sending" : undefined}
                >
                  {manualLoading
                    ? <><Spinner animation="border" size="sm" /> Submitting…</>
                    : <><FiSend size={13} /> Send {manualRows.length > 1 ? `${manualRows.length} Payouts` : "Payout"}</>
                  }
                </button>
              </div>
            );
          })()}
        </div>

        {/* ── Results after submit ── */}
        {manualResults && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <FiCheckCircle size={14} color={manualResults.every((r) => r.ok) ? C.teal : C.orange} />
              Submission Results
              <button
                style={{ ...S.btn("ghost"), padding: "3px 10px", fontSize: 11, marginLeft: "auto" }}
                onClick={() => setManualResults(null)}
              >
                <FiXCircle size={11} /> Dismiss
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {manualResults.map((r, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: "6px 10px", padding: "8px 14px", flexWrap: "wrap",
                  background: r.ok ? "rgba(0,182,155,0.08)" : "rgba(230,55,87,0.07)",
                  border: `1px solid ${r.ok ? "rgba(0,182,155,0.25)" : "rgba(230,55,87,0.2)"}`,
                  borderRadius: 6, fontSize: 13,
                }}>
                  {r.ok
                    ? <FiCheckCircle size={14} color={C.teal} />
                    : <FiXCircle size={14} color={C.red} />
                  }
                  <span style={{ fontWeight: 600, color: C.text }}>{r.payeeName || `Row ${i+1}`}</span>
                  <span style={{ color: C.textMid }}>{r.payeeType}</span>
                  {r.purpose && r.purpose !== "PAYOUT" && <span style={{ ...S.badge(r.purpose === "REFUND" ? "purple" : "blue"), fontSize: 11 }}>{r.purpose}</span>}
                  <span style={{ fontWeight: 600 }}>{fmtUGX(r.amount)}</span>
                  {r.phoneWasOverridden && <span style={{ color: C.orange, fontSize: 11 }}>⚠ alt phone</span>}
                  {r.ok
                    ? <CopyValue value={r.disbursementId} style={{ fontSize: 11, color: C.textLight }} />
                    : <span style={{ color: C.red, fontSize: 12 }}>{r.error}</span>
                  }
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Manual Payout History ── */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px 8px", marginBottom: 12, flexWrap: "wrap", minWidth: 0 }}>
            <FiList size={14} color={C.textMid} />
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap" }}>Manual Payout History</span>
            <span style={{ fontSize: 12, color: C.textLight, marginLeft: 4, whiteSpace: "nowrap" }}>{manualHistoryTotal} total</span>
            {/* Six buttons: on a phone this group wraps under the title and
                wraps again within itself rather than overflowing the panel. */}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
              {manualHistory.length > 0 && (
                <button
                  style={{ ...S.btn("ghost"), padding: "4px 10px", fontSize: 11, border: `1px solid ${C.border}`, color: C.teal }}
                  onClick={exportManualHistoryCSV}
                  title="Export current page as CSV"
                >
                  <FiDownload size={12} /> Export CSV
                </button>
              )}
              {["", "SUCCESSFUL", "PROCESSING", "QUEUED", "FAILED"].map((s) => (
                <button key={s}
                  style={S.filterBtn(manualHistoryFilter === s)}
                  onClick={() => { setManualHistoryFilter(s); setManualHistoryPage(1); }}
                >{s || "All"}</button>
              ))}
            </div>
          </div>

          {manualHistoryLoading ? (
            <div style={{ textAlign: "center", padding: 30 }}><Spinner animation="border" style={{ color: C.orange }} /></div>
          ) : manualHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: C.textLight, fontSize: 13 }}>
              No manual payouts yet. Use the form above to send the first one.
            </div>
          ) : (
            <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}`, boxShadow: "0 1px 8px rgba(26,43,74,0.07)" }}>
              <table style={{ ...S.table, borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr style={{ background: C.header }}>
                    {[
                      { label: "REFERENCE ID",  w: 140 },
                      { label: "STATUS",         w: 120 },
                      { label: "TYPE",           w: 90  },
                      { label: "PURPOSE",        w: 100 },
                      { label: "PAYEE",          w: 160 },
                      { label: "PAID BY",        w: 130 },
                      { label: "AMOUNT (UGX)",   w: 110, align: "right" },
                      { label: "MTN FEE (UGX)",  w: 100, align: "right" },
                      { label: "LINKED ORDER",   w: 130 },
                      { label: "NOTE",           w: 180 },
                      { label: "QUEUED / TIMELINE", w: 210 },
                      { label: "CONFIRMATION",   w: 120 },
                      { label: "",               w: 60 },
                    ].map((col) => (
                      <th key={col.label} style={{ ...S.gridTh(col.align), minWidth: col.w }}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {manualHistory.map((d, rowIdx) => {
                    const meta = STATUS_META[d.status] || { color: "blue", icon: <FiClock size={12} /> };
                    const isEven = rowIdx % 2 === 0;
                    const rowBg  = isEven ? "#ffffff" : "#f7f9fd";
                    const tdBase = S.gridTd(rowBg);

                    // Who pressed Send: recorded on the row since 31 Oct 2026;
                    // older rows carry only the admin's id.
                    const paidBy = d.initiatedBy?.name || d.initiatedBy?.email || null;

                    return (
                      <tr key={d._id}
                        onMouseEnter={(e) => { Array.from(e.currentTarget.cells).forEach((c) => { c.style.background = "#eef3fc"; }); }}
                        onMouseLeave={(e) => { Array.from(e.currentTarget.cells).forEach((c) => { c.style.background = rowBg; }); }}
                      >
                        {/* Reference ID */}
                        <td style={tdBase}>
                          {d.payeeReference ? (
                            <>
                              <CopyValue value={d.payeeReference} strong color={C.text} />
                              <div style={S.cellMeta}><CopyValue value={d.disbursementId} max={18} /></div>
                            </>
                          ) : (
                            <CopyValue value={d.disbursementId} max={18} strong color={C.text} />
                          )}
                        </td>

                        {/* Status */}
                        <td style={tdBase}>
                          <span style={S.badge(meta.color)}>
                            {meta.icon} {d.status}
                            </span>
                          {anyInFlight([d]) && (
                            <div style={{ marginTop: 3 }}>
                              <StageTimer row={d} scheduler={scheduler} now={now} />
                            </div>
                          )}
                          {d.status === "FAILED" && d.failureReason && (
                            <div style={{ ...S.cellMeta, color: "#c0152f", maxWidth: 160, whiteSpace: "normal" }}>
                              {truncate(d.failureReason, 60)}
                            </div>
                          )}
                        </td>

                        {/* Type */}
                        <td style={tdBase}>
                          <span style={S.badge(PAYEE_TYPE_TONE[d.payeeType] || "neutral")}>{d.payeeType || "—"}</span>
                        </td>

                        {/* Purpose */}
                        <td style={tdBase}>
                          <span style={S.badge(purposeTone(d.purpose))}>{d.purpose || "PAYOUT"}</span>
                        </td>

                        {/* Payee */}
                        <td style={tdBase}>
                          <CellStack strong meta={d.payeePhone}>
                            {d.payeeName || "—"}
                          </CellStack>
                          {d.linkedEntityRef?.phoneWasOverridden && (
                            <div style={{ ...S.cellMeta, color: C.orange, display: "flex", alignItems: "center", gap: 3 }}>
                              <FiAlertTriangle size={9} /> alt phone used
                            </div>
                          )}
                        </td>

                        {/* Paid by */}
                        <td style={{ ...tdBase, color: paidBy ? C.text : C.textLight }}>
                          {paidBy ? (
                            <CellStack meta={d.initiatedBy?.name && d.initiatedBy?.email ? d.initiatedBy.email : null}>{paidBy}</CellStack>
                          ) : (
                            <span title={d.initiatedBy?.userId ? `Admin ${d.initiatedBy.userId}` : undefined}>Admin</span>
                          )}
                        </td>

                        {/* Amount */}
                        <td style={{ ...tdBase, ...S.cellNum, ...S.cellStrong }}>{fmtNum(d.amount)}</td>

                        {/* MTN Fee */}
                        <td style={{ ...tdBase, ...S.cellNum }}>{fmtNum(d.mtnFeeAbsorbed)}</td>

                        {/* Linked Order */}
                        <td style={{ ...tdBase, ...(d.linkedOrderId ? { ...S.cellMono, color: C.blue, fontWeight: 600 } : { color: C.textLight }) }}>
                          {d.linkedOrderId ? truncate(d.linkedOrderId.toString(), 14) : "—"}
                        </td>

                        {/* Note */}
                        <td style={{ ...tdBase, color: C.textMid, maxWidth: 200 }}>
                          <span style={{ wordBreak: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                            {d.payeeNote || <span style={{ color: C.border }}>—</span>}
                          </span>
                        </td>

                        {/* Queued + lifecycle timeline */}
                        <td style={tdBase}>
                          {d.createdAt || d.queuedAt ? (
                            <>
                              <div style={{ ...S.cellStrong, whiteSpace: "nowrap" }}>
                                {fmtDay(d.queuedAt || d.createdAt)}
                                <span style={{ fontWeight: 400, color: C.textLight, marginLeft: 6, fontSize: 11 }}>
                                  {new Date(d.queuedAt || d.createdAt).toLocaleTimeString("en-UG", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                              </div>
                              <TimelineLine row={d} />
                            </>
                          ) : "—"}
                        </td>

                        {/* Confirmation */}
                        <td style={tdBase}>
                          <ConfirmationChip row={d} />
                        </td>

                        {/* Audit */}
                        <td style={{ ...tdBase, textAlign: "center" }}>
                          <AuditLink onClick={() => openAudit(d)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {manualHistoryPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
              <button style={S.btn("ghost")} disabled={manualHistoryPage <= 1} onClick={() => setManualHistoryPage((p) => p - 1)}>Prev</button>
              <span style={{ padding: "8px 16px", fontSize: 13, color: C.textMid }}>Page {manualHistoryPage} of {manualHistoryPages}</span>
              <button style={S.btn("ghost")} disabled={manualHistoryPage >= manualHistoryPages} onClick={() => setManualHistoryPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>
      )} {/* end manual payout tab */}

      {/* ── Bulk Upload Tab ──────────────────────────────────────── */}
      {activeTab === "bulk" && (
      <div style={S.panel}>
        <div style={S.panelTitle}>
          <FiPlusCircle size={16} color={C.teal} />
          Bulk Payout Upload
          <span style={{ marginLeft: 8, fontSize: 12, color: C.textLight, fontWeight: 400 }}>
            Upload an Excel (.xlsx) or CSV file. All rows are validated before any payment is queued.
          </span>
        </div>

        {/* ── Download Template button ── */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <button
            style={{ ...S.btn("ghost"), display: "flex", alignItems: "center", gap: 6, borderColor: C.teal, color: C.teal, fontWeight: 600 }}
            onClick={downloadBulkTemplate}
          >
            <FiDownload size={14} /> Download Excel Template
          </button>
        </div>

        {/* ── Instructions ── */}
        <div style={{ background: C.tealSoft, border: `1px solid ${C.teal}22`, borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: C.teal }}>
          <strong>Required columns (case-insensitive):</strong>&nbsp;
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>payeeType</code>
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>payeeId</code>
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>payeeName</code>
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>phone</code>
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>amount</code>
          &nbsp;Optional:&nbsp;
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>purpose</code>
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, marginRight: 4 }}>note</code>
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>linkedOrderId</code>
          <div style={{ marginTop: 6, color: C.textMid }}>payeeType: {(payoutPurposes.payeeTypes || []).join(" | ")} &nbsp;·&nbsp; purpose: {purposesFor("RIDER").join(" | ")} for parties; {purposesFor((payoutPurposes.expensePayeeTypes || [])[0] || "RIDER").join(" | ")} for staff and external (reason required) &nbsp;·&nbsp; payeeId is empty for EXTERNAL &nbsp;·&nbsp; Min UGX 500 per row &nbsp;·&nbsp; Max 200 rows</div>
        </div>

        {/* ── File picker ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <input
            ref={bulkFileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              setBulkFile(file);
              setBulkRows(null);
              setBulkSummary(null);
              setBulkResults(null);
              setBulkError("");

              // Parse file client-side with SheetJS
              const reader = new FileReader();
              reader.onload = async (evt) => {
                try {
                  const wb    = XLSX.read(evt.target.result, { type: "binary" });
                  const sheet = wb.Sheets[wb.SheetNames[0]];
                  const raw   = XLSX.utils.sheet_to_json(sheet, { defval: "" });

                  if (raw.length === 0) { setBulkError("The sheet is empty."); return; }
                  if (raw.length > 200)  { setBulkError("Maximum 200 rows per upload."); return; }

                  // Normalise column names to camelCase
                  const normalise = (row) => ({
                    payeeType:     (row.payeeType    || row.PayeeType    || row["Payee Type"]    || "").toString().trim().toUpperCase(),
                    payeeId:       (row.payeeId      || row.PayeeId      || row["Payee ID"]      || "").toString().trim(),
                    payeeName:     (row.payeeName    || row.PayeeName    || row["Payee Name"]    || "").toString().trim(),
                    phone:         (row.phone        || row.Phone        || row["Phone"]         || "").toString().trim(),
                    amount:        (row.amount       || row.Amount       || row["Amount"]        || "").toString().trim(),
                    purpose:       (row.purpose      || row.Purpose      || row["Purpose"]       || "PAYOUT").toString().trim().toUpperCase(),
                    note:          (row.note         || row.Note         || row["Note"]          || "").toString().trim(),
                    linkedOrderId: (row.linkedOrderId|| row.LinkedOrderId|| row["Linked Order ID"]|| "").toString().trim(),
                  });

                  await runBulkValidate(raw.map(normalise));
                } catch (parseErr) {
                  setBulkError("Could not parse file. Make sure it is a valid .xlsx or .csv.");
                }
              };
              reader.readAsBinaryString(file);
              // Reset input so same file can be re-uploaded
              e.target.value = "";
            }}
          />
          <button
            style={{ ...S.btn("primary"), background: C.teal, borderColor: C.teal }}
            onClick={() => { setBulkFile(null); setBulkRows(null); setBulkSummary(null); setBulkResults(null); setBulkError(""); bulkFileRef.current?.click(); }}
          >
            <FiPlusCircle size={14} /> Choose File
          </button>
          {bulkFile && !bulkValidating && !bulkRows && (
            <span style={{ fontSize: 13, color: C.textMid }}>{bulkFile.name}</span>
          )}
          {bulkValidating && <Spinner animation="border" size="sm" style={{ color: C.teal }} />}
        </div>

        {/* ── Validation error banner ── */}
        {bulkError && (
          <div style={{ background: C.accentSoft, border: `1px solid ${C.accent}44`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: C.accent, display: "flex", alignItems: "center", gap: 8 }}>
            <FiAlertTriangle size={14} /> {bulkError}
          </div>
        )}

        {/* ── Summary bar ── */}
        {bulkSummary && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            {[
              { label: "Total Rows",    value: bulkSummary.total,       color: C.blue,   bg: C.blueSoft },
              { label: "Valid",         value: bulkSummary.valid,       color: C.teal,   bg: C.tealSoft },
              { label: "Invalid",       value: bulkSummary.invalid,     color: bulkSummary.invalid > 0 ? C.accent : C.teal, bg: bulkSummary.invalid > 0 ? C.accentSoft : C.tealSoft },
              ...(bulkSummary.payeesVerified ? [
                { label: "Confirmed on MTN", value: bulkSummary.verified ?? 0, color: C.teal, bg: C.tealSoft },
                { label: "Not on MTN MoMo", value: bulkSummary.unregistered ?? 0, color: (bulkSummary.unregistered ?? 0) > 0 ? C.accent : C.teal, bg: (bulkSummary.unregistered ?? 0) > 0 ? C.accentSoft : C.tealSoft },
                { label: "MTN did not answer", value: bulkSummary.unverified ?? 0, color: (bulkSummary.unverified ?? 0) > 0 ? C.orange : C.teal, bg: (bulkSummary.unverified ?? 0) > 0 ? "#fff8ee" : C.tealSoft },
              ] : []),
              { label: "Total Amount",  value: `UGX ${bulkSummary.totalAmount?.toLocaleString()}`, color: C.orange, bg: "#fff8ee" },
            ].map((c) => (
              <div key={c.label} style={{ flex: "1 1 140px", background: c.bg, borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: C.textLight, marginBottom: 2 }}>{c.label}</div>
                <div style={{ fontSize: 17, fontWeight: 600, color: c.color }}>{c.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Preview table ── */}
        {bulkRows && bulkRows.length > 0 && (
          <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  {["#", "Status", "Name on Lusuku", "Type", "Phone", "Registered on MTN MoMo", "Amount (UGX)", "Purpose", "Note / Error"].map((h) => (
                    <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: C.textMid, fontWeight: 600, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bulkRows.map((row) => (
                  <tr key={row.row} style={{ borderBottom: `1px solid ${C.border}`, background: row.ok ? "transparent" : "#fff5f5" }}>
                    <td style={{ padding: "7px 10px", color: C.textLight }}>{row.row}</td>
                    <td style={{ padding: "7px 10px" }}>
                      {row.ok
                        ? <span style={{ color: C.teal, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><FiCheckCircle size={12} /> OK</span>
                        : <span style={{ color: C.accent, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><FiAlertTriangle size={12} /> Error</span>}
                    </td>
                    <td style={{ padding: "7px 10px", fontWeight: 600 }}>{row.payeeName || "—"}</td>
                    <td style={{ padding: "7px 10px" }}><span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, background: C.blueSoft, color: C.blue, fontWeight: 600 }}>{row.payeeType}</span></td>
                    <td style={{ padding: "7px 10px", fontFamily: "monospace" }}>{row.phone}</td>
                    <td style={{ padding: "7px 10px" }}>
                      {row.momoChecked
                        ? (row.momoRegisteredName
                            ? <span style={{ fontWeight: 600, color: "#1b5e20" }}>
                                {row.momoRegisteredName}
                                {namesLookDifferent(row.payeeName, row.momoRegisteredName) && (
                                  <span title="Shares no word with the name on Lusuku. Confirm this is the right number." style={{ marginLeft: 6, color: C.orange, fontSize: 11, fontWeight: 600 }}>differs</span>
                                )}
                              </span>
                            : row.ok
                              ? <span style={{ color: C.textMid }}>Live, name not disclosed</span>
                              : <span style={{ color: C.accent, fontWeight: 600 }}>Not registered</span>)
                        : <span style={{ color: C.textLight }}>{row.unverified ? "Not checked" : "—"}</span>}
                    </td>
                    <td style={{ padding: "7px 10px", fontWeight: 600, color: C.orange }}>{row.amount?.toLocaleString()}</td>
                    <td style={{ padding: "7px 10px", color: C.textMid }}>{row.purpose}</td>
                    <td style={{ padding: "7px 10px", color: row.ok ? C.textLight : C.accent, fontSize: 11 }}>
                      {row.ok ? (row.note || "—") : row.errors.join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Confirm / Reset ── */}
        {bulkSummary && !bulkResults && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {bulkSummary.canProceed ? (
              <>
                <button
                  style={{ ...S.btn("primary"), background: C.teal, borderColor: C.teal, minWidth: 200 }}
                  disabled={bulkSubmitting}
                  onClick={() =>
                    confirmDialog.ask({
                      title: `Queue ${bulkSummary.valid} payouts?`,
                      body: `UGX ${bulkSummary.totalAmount?.toLocaleString()} in total goes to the payout queue and is paid out on the next run. Every number was confirmed live on MTN MoMo; the registered names are in the preview beside the names on Lusuku.`,
                      note: "This cannot be undone once queued.",
                      tone: "primary",
                      confirmLabel: `Queue ${bulkSummary.valid} payouts`,
                      busyLabel: "Queueing...",
                      onConfirm: async () => {
                        setBulkSubmitting(true);
                        try {
                          const payouts = bulkRows.map((r) => ({
                            payeeType:     r.payeeType,
                            payeeId:       r.payeeId || undefined,   // empty for EXTERNAL
                            payeeName:     r.payeeName,
                            phone:         r.phone,
                            amount:        r.amount,
                            purpose:       r.purpose,
                            note:          r.note,
                            linkedOrderId: r.linkedOrderId || undefined,
                            momoRegisteredName: r.momoRegisteredName || undefined,
                          }));
                          let results = null;
                          try {
                            const res = await apiWalletBulkPayout(payouts);
                            results = payoutResultsFrom(res.data) || [];
                          } catch (err) {
                            results = payoutResultsFrom(err?.response?.data);
                            if (!results) throw err;
                          }
                          setBulkResults(results);
                          const ok  = results.filter((r) => r.ok).length;
                          const bad = results.filter((r) => !r.ok).length;
                          if (bad === 0) notify.success(`${ok} payout${ok !== 1 ? "s" : ""} queued`, "Money moves on the next queue run, within 5 minutes.", { id: "bulk-payout-result" });
                          else notify.error(`${ok} queued, ${bad} not queued`, "Each row's reason is in the results below.", { id: "bulk-payout-result" });
                        } catch (err) {
                          setBulkError(reasonFromError(err, "Submission failed"));
                        } finally {
                          setBulkSubmitting(false);
                        }
                      },
                    })
                  }
                >
                  {bulkSubmitting ? <Spinner animation="border" size="sm" /> : <><FiCheckCircle size={14} /> Confirm & Queue {bulkSummary.valid} Payouts</>}
                </button>
                <button style={S.btn("ghost")} onClick={() => { setBulkFile(null); setBulkRows(null); setBulkSummary(null); setBulkError(""); }}>Cancel</button>
              </>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.accent, fontSize: 13, fontWeight: 600, flexWrap: "wrap" }}>
                <FiAlertTriangle size={14} />
                {(bulkSummary.unverified ?? 0) > 0 && (bulkSummary.invalid === bulkSummary.unverified)
                  ? `MTN did not answer for ${bulkSummary.unverified} row${bulkSummary.unverified !== 1 ? "s" : ""}. Check again before queueing.`
                  : `Fix ${bulkSummary.invalid} invalid row${bulkSummary.invalid !== 1 ? "s" : ""} in the spreadsheet and re-upload.`}
                {(bulkSummary.unverified ?? 0) > 0 && (
                  <button
                    style={{ ...S.btn("primary"), marginLeft: 8 }}
                    disabled={bulkValidating}
                    onClick={() => runBulkValidate(bulkPayoutsRef.current)}
                  >
                    {bulkValidating ? <Spinner animation="border" size="sm" /> : "Check with MTN again"}
                  </button>
                )}
                <button style={{ ...S.btn("ghost"), marginLeft: 8 }} onClick={() => { setBulkFile(null); setBulkRows(null); setBulkSummary(null); setBulkError(""); }}>Clear</button>
              </div>
            )}
          </div>
        )}

        {/* ── Post-submit results ── */}
        {bulkResults && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: C.text, marginBottom: 10 }}>Submission Results</div>
            <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: C.bg }}>
                    {["Status", "Name", "Type", "Amount", "Disbursement ID", "Error"].map((h) => (
                      <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: C.textMid, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map((r, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: r.ok ? "transparent" : "#fff5f5" }}>
                      <td style={{ padding: "7px 10px" }}>
                        {r.ok
                          ? <span style={{ color: C.teal, fontWeight: 600 }}>✓ Queued</span>
                          : <span style={{ color: C.accent, fontWeight: 600 }}>✗ Failed</span>}
                      </td>
                      <td style={{ padding: "7px 10px", fontWeight: 600 }}>{r.payeeName}</td>
                      <td style={{ padding: "7px 10px" }}>{r.payeeType}</td>
                      <td style={{ padding: "7px 10px", color: C.orange, fontWeight: 600 }}>UGX {r.amount?.toLocaleString()}</td>
                      <td style={{ padding: "7px 10px", fontFamily: "monospace", fontSize: 11, color: C.textLight }}>{r.disbursementId || "—"}</td>
                      <td style={{ padding: "7px 10px", color: C.accent, fontSize: 11 }}>{r.error || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button style={{ ...S.btn("ghost"), marginTop: 12 }} onClick={() => { setBulkFile(null); setBulkRows(null); setBulkSummary(null); setBulkResults(null); setBulkError(""); }}>
              Upload Another File
            </button>
          </div>
        )}

      </div>
      )} {/* end bulk upload tab */}

      {/* ── Company Commission Tab ──────────────────────────────── */}
      {activeTab === "commission" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={S.panel}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
              <div style={S.panelTitle}>
                <FiPercent size={16} color={C.purple} /> Company Commission
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input type="date" value={commissionStart} onChange={(e) => setCommissionStart(e.target.value)} style={S.input} />
                <input type="date" value={commissionEnd} onChange={(e) => setCommissionEnd(e.target.value)} style={S.input} />
                <AppSelect value={commissionType} onChange={(e) => setCommissionType(e.target.value)} style={S.input}>
                  <option value="">All vendor types</option>
                  {["RESTAURANT", "RETAILER", "WHOLESALER", "HEALTHCARE", "BRAND"].map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </AppSelect>
                <button style={S.btn("ghost")} onClick={loadCommissions} disabled={commissionLoading}>
                  <FiRefreshCw size={12} /> Refresh
                </button>
              </div>
            </div>

            {commissionLoading && !commissionReport ? (
              <div style={{ padding: 30, textAlign: "center", color: C.textLight }}><Spinner size="sm" animation="border" /> Loading commission ledger…</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
                  {[
                    { label: "Company Commission", value: fmtUGX(commissionReport?.summary?.companyCommission), color: C.purple, bg: C.purpleSoft, sub: "Non-reversed collections" },
                    { label: "Recognized", value: fmtUGX(commissionReport?.summary?.recognizedCommission), color: C.teal, bg: C.tealSoft, sub: "Released vendor orders" },
                    { label: "Pending", value: fmtUGX(commissionReport?.summary?.pendingCommission), color: C.orange, bg: C.orangeSoft, sub: "Still held in escrow" },
                    { label: "Vendor Net", value: fmtUGX(commissionReport?.summary?.vendorNet), color: C.blue, bg: C.blueSoft, sub: `${commissionReport?.summary?.orderCount || 0} vendor allocations` },
                    { label: "Reversed", value: fmtUGX(commissionReport?.summary?.reversedCommission), color: C.red, bg: C.accentSoft, sub: "Excluded from company commission" },
                  ].map((card) => (
                    <div key={card.label} style={{ background: card.bg, borderRadius: 10, padding: "13px 16px" }}>
                      <div style={{ fontSize: 11, color: C.textLight, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.7 }}>{card.label}</div>
                      <div style={{ fontSize: 19, color: card.color, fontWeight: 600, marginTop: 4 }}>{card.value}</div>
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{card.sub}</div>
                    </div>
                  ))}
                </div>

                <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <table style={{ ...S.table, minWidth: 760 }}>
                    <thead><tr style={{ background: C.inputBg }}>
                      {["Vendor", "Rate", "Orders", "Gross", "Commission", "Recognized", "Pending", "Vendor net"].map((label) => (
                        <th key={label} style={label === "Vendor" ? S.th : { ...S.th, textAlign: "right" }}>{label}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {(commissionReport?.rows || []).length === 0 ? (
                        <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: C.textLight, padding: 24 }}>No commission-bearing escrow activity in this period.</td></tr>
                      ) : (commissionReport?.rows || []).map((row) => {
                        const contract = commissionContracts.find((c) => c.vendorId === row.vendorId || c.contractId === row.vendorContractId);
                        return (
                          <tr key={`${row.vendorId}-${row.commissionRate}`} style={{ borderTop: `1px solid ${C.border}` }}>
                            <td style={S.td}>
                              <VendorCell name={contract?.name || truncate(row.vendorId, 18)} type={row.vendorType} country={contract?.country} />
                            </td>
                            <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{Number(row.commissionRate || 0).toFixed(2)}%</td>
                            <td style={{ ...S.td, ...S.cellNum }}>{row.orderCount}</td>
                            <td style={{ ...S.td, ...S.cellNum }}>{fmtUGX(row.grossCollected)}</td>
                            <td style={{ ...S.td, ...S.cellNum, color: C.purple, fontWeight: 600 }}>{fmtUGX(row.companyCommission)}</td>
                            <td style={{ ...S.td, ...S.cellNum, color: C.teal }}>{fmtUGX(row.recognizedCommission)}</td>
                            <td style={{ ...S.td, ...S.cellNum, color: C.orange }}>{fmtUGX(row.pendingCommission)}</td>
                            <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{fmtUGX(row.vendorNet)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div style={S.panel}>
            <div style={S.panelTitle}>
              <FiShield size={16} color={C.teal} /> Vendor Commission Contracts<SettingHelp topic="vendorContracts" />
              <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight, fontWeight: 400 }}>
                {isSuperUser ? "Super-user editing enabled" : "Read-only · super user required to edit"}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
                <FiSearch size={14} color={C.textLight} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                <input
                  type="search"
                  value={contractFilters.q}
                  onChange={(e) => setContractFilters((f) => ({ ...f, q: e.target.value }))}
                  placeholder="Search vendor name or TIN"
                  aria-label="Search vendor name or TIN"
                  style={{ ...S.input, paddingLeft: 32 }}
                />
              </div>
              {contractFilterDefs.map(({ key, label, options }) => (
                <AppSelect
                  key={key}
                  value={contractFilters[key]}
                  onChange={(e) => setContractFilters((f) => ({ ...f, [key]: e.target.value }))}
                  aria-label={label}
                  style={{ ...S.input, width: "auto", flex: "0 0 auto", borderColor: contractFilters[key] ? C.action : C.border, color: contractFilters[key] ? C.action : C.textMid }}
                >
                  <option value="">{label}</option>
                  {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                </AppSelect>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 12, color: C.textLight }}>
              <span>
                {contractFiltersActive
                  ? `${visibleContracts.length} of ${commissionContracts.length} vendors`
                  : `${commissionContracts.length} vendors`}
              </span>
              {contractFiltersActive && (
                <button style={S.btnSm("ghost")} onClick={() => setContractFilters(EMPTY_CONTRACT_FILTERS)}>Clear filters</button>
              )}
              <span style={{ marginLeft: "auto" }}>
                EFRIS identity is edited under{" "}
                <Link to="/admin/vendor-tax-settings" style={{ color: C.action, fontWeight: 600 }}>Tax, Vendor tax settings</Link>.
              </span>
            </div>
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table style={{ ...S.table, minWidth: 760 }}>
                <thead><tr style={{ background: C.inputBg }}>
                  {[
                    ["Vendor", null],
                    ["Rate", <SettingHelp topic="vendorContractRate" />],
                    ["Contract status", <SettingHelp topic="vendorContractStatus" />],
                    ["VAT", <SettingHelp topic="vendorVatRegistered" />],
                    ["EFRIS", null],
                    ["Action", null],
                  ].map(([label, tip]) => (
                    <th key={label} style={label === "Rate" ? { ...S.th, textAlign: "right" } : S.th}>{label}{tip}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {visibleContracts.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ ...S.td, textAlign: "center", color: C.textLight, padding: 24 }}>
                        {commissionContracts.length === 0 ? "No vendor contracts yet." : "No vendors match this search or these filters."}
                      </td>
                    </tr>
                  )}
                  {visibleContracts.map((contract) => (
                    <tr key={`${contract.vendorType}-${contract.contractId}`} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={S.td}>
                        <VendorCell name={contract.name} type={contract.vendorType} country={contract.country} />
                      </td>
                      <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{Number(contract.commissionRate || 0).toFixed(2)}%</td>
                      <td style={S.td} title={contract.commissionUpdatedAt ? `Last updated ${fmtDate(contract.commissionUpdatedAt)}` : undefined}>
                        <CellStack meta={contract.commissionUpdatedAt ? `Updated ${fmtDay(contract.commissionUpdatedAt)}` : null}>
                          <span style={S.badge(contract.commissionConfigured ? "green" : "yellow")}>{contract.commissionConfigured ? "CONFIRMED" : "REVIEW REQUIRED"}</span>
                        </CellStack>
                      </td>
                      {/*
                        VAT registration (§17). Registered is the default state
                        for every vendor record, so the badge that stands out is
                        the exception: a vendor NOT registered. The TIN is shown
                        beside it because the server charges VAT only with a TIN
                        on file (vendorChargesVat). The badge reads the server's
                        `chargesVat`, so a registered row with no TIN shows
                        NO TIN, NOT CHARGING: the state ops has to go and fix.
                      */}
                      <td style={S.td}>
                        {contract.vatRegistered === false ? (
                          <span style={S.badge("yellow")}>NOT CHARGING</span>
                        ) : (
                          <CellStack
                            meta={contract.vatRegistrationNumber ? `TIN ${contract.vatRegistrationNumber}` : "No TIN on file"}
                            metaColor={contract.vatRegistrationNumber ? null : C.orange}
                          >
                            {contract.chargesVat
                              ? <span style={S.badge("green")}>CHARGING</span>
                              : <span style={S.badge("yellow")}>NO TIN, NOT CHARGING</span>}
                          </CellStack>
                        )}
                      </td>
                      {/*
                        EFRIS identity (§17.3), read-only. The badge text, its
                        tone and the TIN line come from describeEfrisState so
                        this column says exactly what the Vendor tax settings
                        page says; an inherited TIN is flagged "(from VAT)"
                        because nobody has confirmed it against URA's records.
                      */}
                      <td style={S.td}>
                        {(() => {
                          const described = describeEfrisState(contract);
                          return (
                            <CellStack meta={described.meta}>
                              <span style={S.badge(described.tone)}>{described.label}</span>
                            </CellStack>
                          );
                        })()}
                      </td>
                      <td style={S.td}>
                        {isSuperUser ? (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={S.btnSm("action")} title="Edit commission rate" onClick={() => openCommissionEdit(contract)}>Rate</button>
                            <button style={S.btnSm("action")} title="Edit VAT registration" onClick={() => openVatEdit(contract)}>VAT</button>
                          </div>
                        ) : <span style={{ color: C.textLight }}>Locked</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Refund Tab ──────────────────────────────────── */}
      {activeTab === "refund" && (
        <ProcessRefundForm />
      )} {/* end refund tab */}

      {/* ── Reports Tab ──────────────────────────────────────────── */}
      {activeTab === "reports" && (() => {
        const t   = reportSummary?.totals  || {};
        const lat = reportSummary?.latency || {};
        const tr  = reportSummary?.treasury || {};
        const bucket = reportSummary?.period?.bucket || autoBucket(reportRange);
        const quick = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This week"], ["month", "This month"], ["lastMonth", "Last month"]];
        const sm = mtnResult?.summary || {};
        const mtnTabs = [
          ["matched", "Matched", sm.matched],
          ["amountMismatch", "Amount mismatch", sm.amountMismatch],
          ["statusMismatch", "Status mismatch", sm.statusMismatch],
          ["missingInLusuku", "Missing in Lusuku", sm.missingInLusuku],
          ["missingAtMtn", "Missing at MTN", sm.missingAtMtn],
        ];
        const mtnRows = Array.isArray(mtnResult?.[mtnResultTab]) ? mtnResult[mtnResultTab] : [];
        const mtnCols = mtnRows.length ? Object.keys(mtnRows[0]).filter((k) => typeof mtnRows[0][k] !== "object" || mtnRows[0][k] === null).slice(0, 8) : [];
        const disabled = reportsUnavailable;
        // Shared range controls (Payouts and Payments sections).
        const rangeControls = (onRefresh, busy) => (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input type="date" style={{ ...S.input, width: 150 }} value={reportRange.from} max={reportRange.to} onChange={(e) => setRangeField("from", e.target.value)} />
            <span style={{ color: C.textLight, fontSize: 12 }}>to</span>
            <input type="date" style={{ ...S.input, width: 150 }} value={reportRange.to} min={reportRange.from} max={localDateStr(new Date())} onChange={(e) => setRangeField("to", e.target.value)} />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {quick.map(([k, label]) => {
                const r = quickRange(k);
                const active = r.from === reportRange.from && r.to === reportRange.to;
                return (
                  <button key={k} style={{ ...S.btn("ghost"), background: active ? C.blueSoft : "#f0f4fa", color: active ? C.blue : C.textMid, padding: "6px 12px" }} onClick={() => setReportRange(r)}>
                    {label}
                  </button>
                );
              })}
            </div>
            <button style={{ ...S.btn("ghost"), marginLeft: "auto" }} onClick={onRefresh} disabled={busy}>
              <FiRefreshCw size={13} style={{ animation: busy ? "spin 1s linear infinite" : "none" }} /> Refresh
            </button>
          </div>
        );
        return (
      <div>
        {/* Inner segmented control: Overview / Payouts / Payments / Scheduled & Email */}
        <div style={{ display: "inline-flex", gap: 2, background: "#e8edf5", borderRadius: 8, padding: 3, marginBottom: 16, flexWrap: "wrap" }}>
          {REPORT_SECTIONS.map((sec) => {
            const on = reportSection === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => setReportSection(sec.id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, background: on ? C.panel : "transparent", color: on ? C.blue : C.textMid, boxShadow: on ? C.shadow : "none", transition: "all 0.15s" }}
              >
                {sec.icon} {sec.label}
              </button>
            );
          })}
        </div>

        {/* ── Overview ─────────────────────────────────────────── */}
        {reportSection === "overview" && (() => {
          const w = overview?.windows || {};
          const tre = overview?.treasury || treasury;
          const rag = ragTone(tre?.float?.status);
          const failures = Array.isArray(overview?.recentFailures) ? overview.recentFailures : [];
          return (
        <div>
          {overviewUnavailable && (
            <div style={NOTICE_STYLE}><FiAlertCircle size={14} /> {REPORTS_NEED_WALLET} · the overview appears once the wallet service is deployed.</div>
          )}
          <div style={S.panel}>
            <div style={S.panelTitle}>
              <FiLayers size={16} color={C.blue} /> Overview
              <span style={{ fontSize: 11, color: C.textLight, fontWeight: 500, marginLeft: 4 }}>EAT days · refreshed with the deep tick</span>
              <button style={{ ...S.btn("ghost"), marginLeft: "auto" }} onClick={() => loadOverview()} disabled={overviewLoading || overviewUnavailable}>
                <FiRefreshCw size={13} style={{ animation: overviewLoading ? "spin 1s linear infinite" : "none" }} /> Refresh
              </button>
            </div>
            {overviewLoading && !overview ? (
              <div style={{ textAlign: "center", padding: 30 }}><Spinner animation="border" style={{ color: C.blue }} /></div>
            ) : overviewError ? (
              <div style={{ fontSize: 12, color: C.red, display: "flex", alignItems: "center", gap: 6 }}><FiAlertCircle size={13} /> {overviewError}</div>
            ) : !overview ? (
              <div style={{ fontSize: 12, color: C.textLight }}>{overviewUnavailable ? "Overview unavailable on this wallet version." : "No overview loaded yet."}</div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
                  {["today", "last7d", "last30d"].map((k) => <WindowTile key={k} label={WINDOW_LABELS[k]} win={w[k]} />)}
                  <InFlightTile inFlight={overview.inFlight} scheduler={scheduler} />
                </div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  <div style={{ flex: "2 1 360px", minWidth: 300 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Recent failures <span style={{ color: C.textLight, fontWeight: 500 }}>· last 24 h</span></div>
                    {failures.length === 0 ? (
                      <div style={{ fontSize: 12, color: C.textLight }}>No failed payouts in the last 24 hours</div>
                    ) : (
                      <table style={{ ...S.table, fontSize: 12 }}>
                        <thead><tr><th style={S.th}>When</th><th style={S.th}>Payee</th><th style={{ ...S.th, textAlign: "right" }}>Amount</th><th style={S.th}>Reason</th><th style={S.th}>Trail</th></tr></thead>
                        <tbody>
                          {failures.map((f, i) => (
                            <tr key={f.disbursementId || i}>
                              <td style={{ ...S.td, padding: "7px 10px", whiteSpace: "nowrap" }}>{fmtDate(f.completedAt)}</td>
                              <td style={{ ...S.td, padding: "7px 10px", fontWeight: 600, color: C.text }}>{f.payeeName || "—"}<div style={{ fontSize: 11, color: C.textLight, fontFamily: "monospace", fontWeight: 400 }}>{truncate(f.disbursementId, 22)}</div></td>
                              <td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtUGX(f.amount)}</td>
                              <td style={{ ...S.td, padding: "7px 10px", whiteSpace: "normal", wordBreak: "break-word", color: C.red }}>{f.failureReason || "MTN error"}</td>
                              <td style={{ ...S.td, padding: "7px 10px" }}><AuditLink onClick={() => openAudit({ ...f, status: "FAILED" })} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div style={{ flex: "1 1 260px", minWidth: 240 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Treasury</div>
                    {tre ? (
                      <div style={{ background: rag.bg, border: `1px solid ${rag.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: C.text }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, color: rag.fg, marginBottom: 6 }}><FiShield size={13} /> {rag.label}</div>
                        <div>MTN float <b>{tre.float?.balance != null ? fmtUGX(tre.float.balance) : "unavailable"}</b></div>
                        <div style={{ color: C.textMid }}>reserve {fmtUGX(tre.watermark)} · queued debits {fmtUGX(tre.obligations?.queuedDebit)} ({tre.obligations?.queuedCount || 0})</div>
                        <div style={{ color: C.textMid }}>owed out {fmtUGX(tre.totalOwed)}{tre.coverage != null ? ` · coverage ${(Number(tre.coverage) * 100).toFixed(0)}%` : ""}</div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: C.textLight }}>Treasury snapshot unavailable</div>
                    )}
                    {overview.generatedAt && <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>generated {fmtDate(overview.generatedAt)}</div>}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
          );
        })()}

        {/* ── Payments ─────────────────────────────────────────── */}
        {reportSection === "payments" && (() => {
          const col = payments?.collections || {};
          const ct  = col.totals || {};
          const es  = payments?.escrow || {};
          const nf  = payments?.netFlow || {};
          const pBucket = payments?.period?.bucket || autoBucket(reportRange);
          const moneyCols = [["gross", "Gross"], ["commission", "Commission"], ["net", "Net"]];
          const netNeg = Number(nf.net) < 0;
          return (
        <div>
          {paymentsUnavailable && (
            <div style={NOTICE_STYLE}><FiAlertCircle size={14} /> {REPORTS_NEED_WALLET} · payments analytics appear once the wallet service is deployed.</div>
          )}
          <div style={S.panel}>
            <div style={S.panelTitle}>
              <FiCalendar size={16} color={C.blue} /> Payments period
              <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight, fontWeight: 500 }}>
                {rangeDays(reportRange)} day{rangeDays(reportRange) === 1 ? "" : "s"} · bucket: {pBucket}
              </span>
            </div>
            {rangeControls(() => loadPayments(reportRange), paymentsLoading)}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              <button style={{ ...S.btn("teal"), opacity: payments ? 1 : 0.5 }} disabled={!payments} onClick={exportPaymentsXlsx}>
                <FiDownload size={13} /> Download payments (XLSX)
              </button>
            </div>
          </div>
          <div style={S.panel}>
            <div style={S.panelTitle}>
              <FiPieChart size={16} color={C.purple} /> Collections, escrow and net flow
              {payments?.generatedAt && <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight, fontWeight: 500 }}>generated {fmtDate(payments.generatedAt)}</span>}
            </div>
            {paymentsLoading && !payments ? (
              <div style={{ textAlign: "center", padding: 30 }}><Spinner animation="border" style={{ color: C.purple }} /></div>
            ) : paymentsError ? (
              <div style={{ fontSize: 12, color: C.red, display: "flex", alignItems: "center", gap: 6 }}><FiAlertCircle size={13} /> {paymentsError}</div>
            ) : !payments ? (
              <div style={{ fontSize: 12, color: C.textLight }}>{paymentsUnavailable ? "Payments analytics unavailable on this wallet version." : "No payments data loaded yet."}</div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
                  <ReportTile label="Collections gross" value={fmtUGX(ct.gross)} sub={`${Number(ct.count || 0).toLocaleString()} collection${Number(ct.count) === 1 ? "" : "s"}`} color={C.blue} />
                  <ReportTile label="Commission" value={fmtUGX(ct.commission)} sub={Number(ct.gross) > 0 ? `${fmtPct(Number(ct.commission || 0) / Number(ct.gross))} of gross` : "platform share"} color={C.purple} />
                  <ReportTile label="Collections net" value={fmtUGX(ct.net)} sub="owed to vendors after commission" color={C.teal} />
                  <ReportTile label="Refunds" value={fmtUGX(payments.refunds?.amount)} sub={`${Number(payments.refunds?.count || 0)} refund${Number(payments.refunds?.count) === 1 ? "" : "s"} paid out`} color={C.orange} />
                  <ReportTile label="COD deposits" value={fmtUGX(payments.cod?.deposits?.amount)} sub={`${Number(payments.cod?.deposits?.count || 0)} rider deposit${Number(payments.cod?.deposits?.count) === 1 ? "" : "s"}`} />
                  <ReportTile label="Net flow" value={fmtUGX(nf.net)} sub={`gross ${formatMoneyShort(nf.collectionsGross)} · payouts ${formatMoneyShort(nf.payouts)} · refunds ${formatMoneyShort(nf.refunds)}`} color={netNeg ? C.red : C.teal} />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Collections gross per {pBucket}</div>
                  <SeriesBars series={col.series} bucket={pBucket} valueKey="gross" unit="collections" subKey="commission" subLabel="commission" failedKey={null} />
                </div>

                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 18 }}>
                  <MoneyTable title="By rail"        rows={col.byRail}       keyLabel="Rail"   cols={moneyCols} />
                  <MoneyTable title="By vendor type" rows={col.byVendorType} keyLabel="Vendor" cols={moneyCols} />
                  <MoneyTable title="By status"      rows={col.byStatus}     keyLabel="Status" cols={[["gross", "Gross"], ["net", "Net"]]} />
                </div>

                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Escrow</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                  <ReportTile label="Held now" value={fmtUGX(es.heldNow?.net)} sub={`${Number(es.heldNow?.count || 0)} order${Number(es.heldNow?.count) === 1 ? "" : "s"} still held`} color={C.orange} />
                  <ReportTile label="Released in period" value={fmtUGX(es.releasedInPeriod?.net)} sub={`${Number(es.releasedInPeriod?.count || 0)} released`} color={C.teal} />
                  <ReportTile label="Reversed in period" value={fmtUGX(es.reversedInPeriod?.net)} sub={`${Number(es.reversedInPeriod?.count || 0)} reversed`} color={Number(es.reversedInPeriod?.count) > 0 ? C.red : C.textLight} />
                  <ReportTile label="Average hold" value={fmtHours(es.avgHoldHours)} sub="from collection to release" />
                </div>
                <div style={{ fontSize: 11, color: C.textMid }}>
                  Payouts in period: <b>{Number(payments.payouts?.count || 0).toLocaleString()}</b> · {fmtUGX(payments.payouts?.amount)} · fees {fmtUGX(payments.payouts?.fees)}
                </div>
              </>
            )}
          </div>
        </div>
          );
        })()}

        {/* ── Payouts (the round-1 report pack, unchanged) ─────── */}
        {reportSection === "payouts" && (
        <>
        {reportsUnavailable && (
          <div style={NOTICE_STYLE}>
            <FiAlertCircle size={14} /> {REPORTS_NEED_WALLET} · the controls below activate once the wallet service is deployed.
          </div>
        )}

        {/* Period + downloads */}
        <div style={S.panel}>
          <div style={S.panelTitle}>
            <FiCalendar size={16} color={C.blue} /> Report period
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight, fontWeight: 500 }}>
              {rangeDays(reportRange)} day{rangeDays(reportRange) === 1 ? "" : "s"} · bucket: {bucket}
            </span>
          </div>
          {rangeControls(() => loadReportSummary(reportRange), reportLoading)}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            <button style={{ ...S.btn("primary"), opacity: disabled ? 0.5 : 1 }} disabled={disabled || reportDownloading === "register"} onClick={() => downloadReportCsv("register")}>
              {reportDownloading === "register" ? <Spinner animation="border" size="sm" /> : <FiDownload size={13} />} Download register (CSV)
            </button>
            <button style={{ ...S.btn("primary"), background: C.purple, opacity: disabled ? 0.5 : 1 }} disabled={disabled || reportDownloading === "ledger"} onClick={() => downloadReportCsv("ledger")}>
              {reportDownloading === "ledger" ? <Spinner animation="border" size="sm" /> : <FiDownload size={13} />} Download ledger (CSV)
            </button>
            <button style={{ ...S.btn("teal"), opacity: reportSummary ? 1 : 0.5 }} disabled={!reportSummary} onClick={exportSummaryXlsx}>
              <FiDownload size={13} /> Download summary (XLSX)
            </button>
          </div>
        </div>

        {/* Summary */}
        <div style={S.panel}>
          <div style={S.panelTitle}>
            <FiFileText size={16} color={C.blue} /> Payout summary
            {reportSummary?.generatedAt && <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight, fontWeight: 500 }}>generated {fmtDate(reportSummary.generatedAt)}</span>}
          </div>
          {reportLoading && !reportSummary ? (
            <div style={{ textAlign: "center", padding: 30 }}><Spinner animation="border" style={{ color: C.blue }} /></div>
          ) : reportError ? (
            <div style={{ fontSize: 12, color: C.red, display: "flex", alignItems: "center", gap: 6 }}><FiAlertCircle size={13} /> {reportError}</div>
          ) : !reportSummary ? (
            <div style={{ fontSize: 12, color: C.textLight }}>{reportsUnavailable ? "Summary unavailable on this wallet version." : "No summary loaded yet."}</div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
                <ReportTile label="Payouts" value={Number(t.count || 0).toLocaleString()} sub={`${Number(t.successCount || 0)} ok · ${Number(t.failedCount || 0)} failed · ${Number(t.inFlightCount || 0)} in flight`} />
                <ReportTile label="Amount paid" value={fmtUGX(t.amount)} sub={`MoMo debit ${fmtUGX(t.momoDebit)}`} color={C.blue} />
                <ReportTile label="Fees absorbed" value={fmtUGX(t.feesAbsorbed)} sub="MTN fees paid by Lusuku" color={C.purple} />
                <ReportTile label="Success rate" value={fmtPct(t.successRate)} sub={`${Number(t.successCount || 0)} / ${Number(t.count || 0)}`} color={Number(t.failedCount) > 0 ? C.orange : C.teal} />
                <ReportTile label="Queue latency" value={`${fmtLatency(lat.queue?.p50)} · ${fmtLatency(lat.queue?.p90)}`} sub={`p50 · p90 (max ${fmtLatency(lat.queue?.max)}, n=${lat.queue?.n ?? 0})`} />
                <ReportTile label="MTN confirm latency" value={`${fmtLatency(lat.confirm?.p50)} · ${fmtLatency(lat.confirm?.p90)}`} sub={`p50 · p90 (max ${fmtLatency(lat.confirm?.max)}, n=${lat.confirm?.n ?? 0})`} />
              </div>

              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6, display: "flex", gap: 10, alignItems: "center" }}>
                  Amount per {bucket}
                  <span style={{ fontSize: 11, color: C.textLight, fontWeight: 500 }}><span style={{ display: "inline-block", width: 8, height: 8, background: C.blue, marginRight: 4, borderRadius: 2 }} />amount <span style={{ display: "inline-block", width: 8, height: 8, background: C.red, margin: "0 4px 0 10px", borderRadius: 2 }} />failed share</span>
                </div>
                <SeriesBars series={reportSummary.series} bucket={bucket} />
              </div>

              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 18 }}>
                <DimTable title="By status"       rows={reportSummary.byStatus}      keyLabel="Status" />
                <DimTable title="By trigger"      rows={reportSummary.byTriggerType} keyLabel="Trigger" />
                <DimTable title="By payee type"   rows={reportSummary.byPayeeType}   keyLabel="Payee" />
                <DimTable title="By purpose"      rows={reportSummary.byPurpose}     keyLabel="Purpose" />
              </div>

              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{ flex: "2 1 360px", minWidth: 300 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Failure reasons</div>
                  {(reportSummary.failureReasons || []).length === 0 ? (
                    <div style={{ fontSize: 12, color: C.textLight }}>No failures in this period</div>
                  ) : (
                    <table style={{ ...S.table, fontSize: 12 }}>
                      <thead><tr><th style={S.th}>Reason</th><th style={{ ...S.th, textAlign: "right" }}>Count</th><th style={{ ...S.th, textAlign: "right" }}>Amount</th></tr></thead>
                      <tbody>
                        {reportSummary.failureReasons.map((r, i) => (
                          <tr key={`${r.reason}-${i}`}>
                            <td style={{ ...S.td, padding: "7px 10px", whiteSpace: "normal", wordBreak: "break-word" }}>{r.reason || "—"}</td>
                            <td style={{ ...S.td, padding: "7px 10px", textAlign: "right" }}>{Number(r.count || 0)}</td>
                            <td style={{ ...S.td, padding: "7px 10px", textAlign: "right" }}>{Number(r.amount || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div style={{ flex: "1 1 260px", minWidth: 240 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>Treasury booked in period</div>
                  <table style={{ ...S.table, fontSize: 12 }}>
                    <tbody>
                      <tr><td style={{ ...S.td, padding: "7px 10px" }}>FW settlements ({tr.fwSettlements?.count ?? 0})</td><td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmtUGX(tr.fwSettlements?.net)}<div style={{ fontSize: 11, color: C.textLight, fontWeight: 400 }}>gross {Number(tr.fwSettlements?.gross || 0).toLocaleString()} · fees {Number(tr.fwSettlements?.fees || 0).toLocaleString()}</div></td></tr>
                      <tr><td style={{ ...S.td, padding: "7px 10px" }}>Float top-ups ({tr.floatTopups?.count ?? 0})</td><td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmtUGX(tr.floatTopups?.amount)}</td></tr>
                      <tr><td style={{ ...S.td, padding: "7px 10px" }}>Payout journals ({tr.payoutJournals?.count ?? 0})</td><td style={{ ...S.td, padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmtUGX(tr.payoutJournals?.amount)}</td></tr>
                    </tbody>
                  </table>
                  {(reportSummary.resolvedVia || []).length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 11, color: C.textMid }}>
                      <span style={{ fontWeight: 600, color: C.text }}>Confirmed via:</span>{" "}
                      {reportSummary.resolvedVia.map((r) => `${String(r.key || "legacy").replace(/_/g, " ").toLowerCase()} ${r.count}`).join(" · ")}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* MTN statement reconcile */}
        <div style={S.panel}>
          <div style={S.panelTitle}>
            <FiUpload size={16} color={C.teal} /> MTN statement reconcile
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight, fontWeight: 500 }}>CSV or XLSX export from the MTN MoMo portal · parsed in your browser</span>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            <input ref={mtnFileRef} type="file" accept=".csv,.xlsx,.xls" style={{ fontSize: 12 }} disabled={disabled} onChange={(e) => handleMtnFile(e.target.files?.[0])} />
            {mtnFileName && (
              <span style={{ fontSize: 12, color: C.textMid }}>
                <strong>{mtnFileName}</strong> · {mtnRawRows.length.toLocaleString()} rows · {mtnHeaders.length} columns
                <button style={{ ...S.btn("ghost"), padding: "3px 8px", fontSize: 11, marginLeft: 8 }} onClick={clearMtnFile}>Clear</button>
              </span>
            )}
          </div>

          {mtnMapping && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginBottom: 12 }}>
                {MTN_FIELDS.map((f) => (
                  <label key={f} style={{ fontSize: 11, color: C.textMid }}>
                    <div style={{ fontWeight: 600, marginBottom: 3, color: (f === "amount" && !mtnMapping[f]) ? C.red : C.text }}>
                      {MTN_FIELD_LABELS[f]}{f === "amount" ? " *" : ""}
                    </div>
                    <AppSelect style={{ ...S.input, padding: "6px 8px" }} value={mtnMapping[f] || ""} onChange={(e) => setMtnMapping((m) => ({ ...m, [f]: e.target.value || null }))}>
                      <option value="">— not in file —</option>
                      {mtnHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </AppSelect>
                  </label>
                ))}
              </div>
              {mtnProblems.length > 0 && (
                <div style={{ fontSize: 12, color: C.orange, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><FiAlertTriangle size={12} /> {mtnProblems.join(" · ")}</div>
              )}
              <div style={{ fontSize: 11, fontWeight: 600, color: C.text, marginBottom: 4 }}>Preview (first 5 rows as they will be sent)</div>
              <div style={{ overflowX: "auto", marginBottom: 12 }}>
                <table style={{ ...S.table, fontSize: 11 }}>
                  <thead><tr>{MTN_FIELDS.map((f) => <th key={f} style={S.th}>{f}</th>)}</tr></thead>
                  <tbody>
                    {mtnPreviewRows.length === 0 ? (
                      <tr><td style={S.td} colSpan={MTN_FIELDS.length}>No usable rows with this mapping</td></tr>
                    ) : mtnPreviewRows.map((r, i) => (
                      <tr key={i}>{MTN_FIELDS.map((f) => <td key={f} style={{ ...S.td, padding: "6px 10px", fontFamily: f.endsWith("Id") || f === "phone" ? "monospace" : "inherit" }}>{r[f] == null ? <span style={{ color: C.border }}>—</span> : String(r[f])}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button style={{ ...S.btn("teal"), opacity: mtnProblems.length || disabled ? 0.5 : 1 }} disabled={mtnReconciling || mtnProblems.length > 0 || disabled} onClick={runMtnReconcile}>
                  {mtnReconciling ? <Spinner animation="border" size="sm" /> : <FiActivity size={13} />} Reconcile {mtnRawRows.length ? `${mtnRawRows.length.toLocaleString()} rows` : ""}
                </button>
                <label style={{ fontSize: 12, color: C.textMid, display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={mtnUseRange} onChange={(e) => setMtnUseRange(e.target.checked)} />
                  Restrict Lusuku side to the report period ({reportRange.from} → {reportRange.to})
                </label>
              </div>
            </>
          )}

          {mtnResult && (
            <div style={{ marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                {mtnTabs.map(([k, label, n]) => (
                  <button key={k} style={{ ...S.btn("ghost"), padding: "6px 12px", background: mtnResultTab === k ? C.blueSoft : "#f0f4fa", color: mtnResultTab === k ? C.blue : C.textMid }} onClick={() => setMtnResultTab(k)}>
                    {label} <span style={{ background: k === "matched" ? C.tealSoft : Number(n) > 0 ? C.accentSoft : "#e8edf5", color: k === "matched" ? C.teal : Number(n) > 0 ? C.red : C.textLight, borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 600, marginLeft: 4 }}>{Number(n) || 0}</span>
                  </button>
                ))}
                <span style={{ fontSize: 11, color: C.textLight, marginLeft: 6 }}>MTN {sm.mtnRows ?? 0} rows · Lusuku {sm.lusukuRows ?? 0} rows</span>
                <button style={{ ...S.btn("ghost"), marginLeft: "auto", border: `1px solid ${C.border}`, color: C.teal }} onClick={downloadMismatchCsv}>
                  <FiDownload size={12} /> Download mismatches (CSV)
                </button>
              </div>
              <div style={{ overflowX: "auto" }}>
                {mtnRows.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textLight, padding: "8px 0" }}>Nothing in this bucket.</div>
                ) : (
                  <table style={{ ...S.table, fontSize: 11 }}>
                    <thead><tr>{mtnCols.map((c) => <th key={c} style={S.th}>{c}</th>)}</tr></thead>
                    <tbody>
                      {mtnRows.slice(0, 200).map((r, i) => (
                        <tr key={r.disbursementId || r.financialTransactionId || i}>
                          {mtnCols.map((c) => <td key={c} style={{ ...S.td, padding: "6px 10px", fontFamily: /id$/i.test(c) ? "monospace" : "inherit" }}>{r[c] == null ? "—" : typeof r[c] === "number" ? r[c].toLocaleString() : String(r[c])}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {mtnRows.length > 200 && <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>Showing 200 of {mtnRows.length} — download the CSV for the full list.</div>}
              </div>
            </div>
          )}
        </div>

        </>
        )}

        {/* ── Scheduled & Email ────────────────────────────────── */}
        {reportSection === "schedule" && (
        <>
        {(reportsUnavailable || settingsUnavailable) && (
          <div style={NOTICE_STYLE}>
            <FiAlertCircle size={14} /> {REPORTS_NEED_WALLET} · scheduling and email settings activate once the wallet service is deployed.
          </div>
        )}

        {/* Settings card */}
        <div style={S.panel}>
          <div style={S.panelTitle}>
            <FiSettings size={16} color={C.teal} /> Scheduled report settings<SettingHelp topic="reportSchedule" />
            <span style={{ fontSize: 11, color: C.textLight, fontWeight: 500, marginLeft: 4 }}>hours in Kampala time (EAT)</span>
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.textLight, fontWeight: 500 }}>
              {settingsSaved?.updatedAt
                ? `Last saved by ${settingsSaved.updatedBy || "unknown"} at ${fmtDate(settingsSaved.updatedAt)}`
                : settingsSaved ? "Not saved yet · wallet defaults" : ""}
            </span>
          </div>
          {settingsLoading && !settingsSaved ? (
            <div style={{ textAlign: "center", padding: 24 }}><Spinner animation="border" style={{ color: C.teal }} /></div>
          ) : settingsUnavailable ? (
            <div style={{ fontSize: 12, color: C.textLight }}>Report settings need the updated wallet service.</div>
          ) : (
            <>
              {!canEditSettings && (
                <div style={LOCK_STYLE}><FiShield size={13} /> Read-only: only ADMIN or super users can change the report settings.</div>
              )}
              {settingsMeta?.envLocks?.reportsDisabled && (
                <div style={LOCK_STYLE}><FiAlertTriangle size={13} color={C.orange} /> DISBURSEMENT_REPORTS_ENABLED=false on the wallet service: scheduled reports and emails are off regardless of these settings.</div>
              )}
              {settingsMeta?.envLocks?.autoReconcileDisabled && (
                <div style={LOCK_STYLE}><FiAlertTriangle size={13} color={C.orange} /> DISBURSEMENT_RECON_AUTO=false on the wallet service: auto-reconcile stays off regardless of this switch.</div>
              )}
              <fieldset disabled={!canEditSettings || settingsSaving} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                  <label style={CHECK_LABEL}>
                    <input type="checkbox" checked={settingsForm.dailyEnabled} onChange={(e) => setSetting("dailyEnabled", e.target.checked)} /> Daily report at
                  </label>
                  <SettingHelp topic="reportDaily" />
                  <AppSelect aria-label="Daily report hour (EAT)" style={{ ...S.input, width: 96, padding: "6px 8px" }} value={settingsForm.dailyHourEAT} disabled={!settingsForm.dailyEnabled} onChange={(e) => setSetting("dailyHourEAT", Number(e.target.value))}>
                    {hourOptionsEAT().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </AppSelect>
                  <span style={{ fontSize: 12, color: C.textLight, marginLeft: -10 }}>EAT</span>
                  <label style={CHECK_LABEL}>
                    <input type="checkbox" checked={settingsForm.monthlyEnabled} onChange={(e) => setSetting("monthlyEnabled", e.target.checked)} /> Monthly report on the 1st
                    <span onClick={(e) => e.preventDefault()}><SettingHelp topic="reportMonthly" /></span>
                  </label>
                  <label style={CHECK_LABEL}>
                    <input type="checkbox" checked={settingsForm.autoReconcile} onChange={(e) => setSetting("autoReconcile", e.target.checked)} /> Auto-reconcile the previous day
                    <span onClick={(e) => e.preventDefault()}><SettingHelp topic="reportAutoReconcile" /></span>
                  </label>
                  <label style={CHECK_LABEL}>
                    <input type="checkbox" checked={settingsForm.sendWhenEmpty} onChange={(e) => setSetting("sendWhenEmpty", e.target.checked)} /> Send when the period has no payouts
                    <span onClick={(e) => e.preventDefault()}><SettingHelp topic="reportSendWhenEmpty" /></span>
                  </label>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                    Recipients<SettingHelp topic="reportRecipients" />
                    <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 6 }}>
                      {settingsForm.recipients.length}/{MAX_RECIPIENTS}
                      {settingsMeta?.source?.recipients === "env" ? " · from FINANCE_REPORT_EMAILS in the wallet's hosting environment until a list is saved here" : settingsMeta?.source?.recipients === "default" ? " · none yet, so reports are kept here but not emailed" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", background: C.inputBg, border: `1px solid ${recipientError ? C.red : C.border}`, borderRadius: C.radiusSm, padding: "6px 8px" }}>
                    {settingsForm.recipients.map((e) => (
                      <RecipientChip key={e} email={e} disabled={!canEditSettings} onRemove={() => setSetting("recipients", removeRecipient(settingsForm.recipients, e))} />
                    ))}
                    <input
                      aria-label="Add recipient"
                      placeholder={settingsForm.recipients.length ? "add another, Enter or comma" : "finance@lusuku.ug then Enter or comma"}
                      value={recipientDraft}
                      onChange={(e) => { setRecipientDraft(e.target.value); if (recipientError) setRecipientError(""); }}
                      onKeyDown={onRecipientKeyDown}
                      onBlur={commitRecipientDraft}
                      style={{ flex: "1 1 200px", minWidth: 160, border: "none", background: "transparent", outline: "none", fontSize: 12, color: C.text, padding: "3px 4px" }}
                    />
                  </div>
                  {recipientError && <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>{recipientError}</div>}
                </div>

                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Attachments<SettingHelp topic="reportAttachments" /></span>
                  {ATTACHMENT_KEYS.map((k) => (
                    <label key={k} style={CHECK_LABEL}>
                      <input type="checkbox" checked={!!settingsForm.attachments[k]} onChange={(e) => setAttachment(k, e.target.checked)} /> {ATTACHMENT_LABELS[k]}
                    </label>
                  ))}
                  <span style={{ fontSize: 11, color: C.textLight }}>up to 4 MB per email; larger files are named in the email and stay downloadable here</span>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <button style={{ ...S.btn("primary"), opacity: settingsDirty || recipientDraft.trim() ? 1 : 0.6 }} disabled={settingsSaving || (!settingsDirty && !recipientDraft.trim())} onClick={saveReportSettings}>
                    {settingsSaving ? <Spinner animation="border" size="sm" /> : <FiCheckCircle size={13} />} Save settings
                  </button>
                  {settingsDirty && <span style={{ fontSize: 11, color: C.orange, fontWeight: 600 }}>Unsaved changes</span>}
                </div>
              </fieldset>

              <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 16, paddingTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>Send a test email</div>
                <div style={{ fontSize: 11, color: C.textLight, marginBottom: 8 }}>
                  Builds an ad-hoc summary for the last 7 days and posts it through the same path as the nightly report. Nothing is stored. Leave the box empty to use the saved recipients.
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <input style={{ ...S.input, width: 320 }} aria-label="Test email recipients" placeholder="override recipients (optional), comma separated" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} disabled={!canEditSettings} />
                  <button style={{ ...S.btn("teal"), opacity: canEditSettings ? 1 : 0.5 }} disabled={!canEditSettings || testEmailSending} onClick={sendTestReportEmail}>
                    {testEmailSending ? <Spinner animation="border" size="sm" /> : <FiMail size={13} />} Send test email
                  </button>
                </div>
                {testEmailResult && (testEmailResult.error ? (
                  <div style={{ fontSize: 12, color: C.red, marginTop: 8 }}>{testEmailResult.error}</div>
                ) : (
                  <div style={{ fontSize: 12, color: C.textMid, marginTop: 8 }}>
                    Posted at {testEmailResult.at.toLocaleTimeString()} to <b>{testEmailResult.recipients.join(", ") || "nobody"}</b>
                    {" · "}attachments: {testEmailResult.attachments.length ? testEmailResult.attachments.join(", ") : "none"}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Scheduled reports */}
        <div style={S.panel}>
          <div style={S.panelTitle}>
            <FiHash size={16} color={C.purple} /> Scheduled reports
            <span style={{ fontSize: 11, color: C.textLight, fontWeight: 500, marginLeft: 4 }}>
              {settingsSaved
                ? `${settingsSaved.dailyEnabled ? `daily at ${formatHourEAT(settingsSaved.dailyHourEAT)}` : "daily off"} · ${settingsSaved.monthlyEnabled ? "monthly on the 1st" : "monthly off"} · hash-chained`
                : "daily at 01:00 EAT · monthly on the 1st · hash-chained"}
            </span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <AppSelect style={{ ...S.input, width: 110, padding: "6px 8px" }} value={generateKind} onChange={(e) => setGenerateKind(e.target.value)} disabled={disabled}>
                <option value="DAILY">DAILY</option>
                <option value="MONTHLY">MONTHLY</option>
                <option value="ADHOC">ADHOC</option>
              </AppSelect>
              {generateKind === "ADHOC" && (
                <>
                  <input type="date" style={{ ...S.input, width: 140, padding: "6px 8px" }} value={generateStart} onChange={(e) => setGenerateStart(e.target.value)} />
                  <input type="date" style={{ ...S.input, width: 140, padding: "6px 8px" }} value={generateEnd} onChange={(e) => setGenerateEnd(e.target.value)} />
                </>
              )}
              <button style={{ ...S.btn("primary"), background: C.purple, opacity: disabled ? 0.5 : 1 }} disabled={generating || disabled} onClick={generateReportNow}>
                {generating ? <Spinner animation="border" size="sm" /> : <FiZap size={13} />} Generate now
              </button>
              <button style={{ ...S.btn("ghost"), border: `1px solid ${C.border}` }} disabled={verifyingChain || disabled || !scheduledReports.length} onClick={verifyScheduledChain} title="Fetch each report and check its hash chain">
                {verifyingChain ? <Spinner animation="border" size="sm" /> : <FiShield size={13} />} Verify chain
              </button>
            </div>
          </div>
          {scheduledLoading && !scheduledReports.length ? (
            <div style={{ textAlign: "center", padding: 24 }}><Spinner animation="border" style={{ color: C.purple }} /></div>
          ) : scheduledReports.length === 0 ? (
            <div style={{ fontSize: 12, color: C.textLight }}>{reportsUnavailable ? "Scheduled reports need the updated wallet service." : `No reports generated yet. The first daily report lands at ${formatHourEAT(settingsSaved?.dailyHourEAT ?? 1)}.`}</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ ...S.table, fontSize: 12 }}>
                <thead><tr>
                  {["Kind", "Period", "Generated", "By", "Seq", "Payouts", "Amount", "Hash", "Chain"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {scheduledReports.map((r) => {
                    const chain = scheduledChain[r.reportId];
                    return (
                      <tr key={r.reportId} style={{ cursor: "pointer" }} onClick={() => openScheduledReport(r.reportId)} title={r.reportId}>
                        <td style={S.td}><span style={S.badge(r.kind === "MONTHLY" ? "purple" : r.kind === "ADHOC" ? "yellow" : "blue")}>{r.kind}</span></td>
                        <td style={{ ...S.td, whiteSpace: "nowrap" }}>{String(r.periodStart || "").slice(0, 10)} → {String(r.periodEnd || "").slice(0, 10)}</td>
                        <td style={{ ...S.td, whiteSpace: "nowrap" }}>{fmtDate(r.generatedAt)}</td>
                        <td style={{ ...S.td, fontSize: 11 }}>{r.generatedBy || "—"}</td>
                        <td style={{ ...S.td, fontVariantNumeric: "tabular-nums" }}>{r.sequence ?? "—"}</td>
                        <td style={{ ...S.td, fontVariantNumeric: "tabular-nums" }}>{r.totals?.count ?? "—"}</td>
                        <td style={{ ...S.td, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{r.totals?.amount != null ? fmtUGX(r.totals.amount) : "—"}</td>
                        <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11 }} title={r.contentHash}>{shortHash(r.contentHash)}</td>
                        <td style={S.td}>
                          {chain === true ? <span style={S.badge("green")}><FiCheckCircle size={11} /> valid</span>
                            : chain === false ? <span style={S.badge("red")}><FiXCircle size={11} /> broken</span>
                            : <span style={{ color: C.textLight, fontSize: 11 }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </div>
        );
      })()}

      {/* ── Reconciliation Tab ───────────────────────────────────── */}
      {activeTab === "reconciliation" && (
      <div style={S.panel}>
        {/* Summary stat cards */}
        {reconStats && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            {[
              { label: "Total Reports",     value: reconStats.total,      color: C.blue,   bg: C.blueSoft },
              { label: "Flagged Reports",   value: reconStats.flagged,    color: C.orange, bg: C.orangeSoft },
              { label: "Unresolved",        value: reconStats.unresolved, color: C.red,    bg: C.accentSoft },
              { label: "Latest Status",     value: reconStats.latest?.status || "—", color: reconStats.latest?.hasDiscrepancy ? C.red : C.teal, bg: reconStats.latest?.hasDiscrepancy ? C.accentSoft : C.tealSoft },
            ].map((c) => (
              <div key={c.label} style={{ flex: "1 1 160px", background: c.bg, borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ fontSize: 11, color: C.textLight, marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: c.color }}>{c.value}</div>
              </div>
            ))}
          </div>
        )}

        <div style={S.panelTitle}>
          <FiActivity size={16} color={C.teal} />
          Reconciliation Reports
          <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, fontWeight: 400 }}>
            {reconPagination.total} total
          </span>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: C.textLight }}>Status:</span>
            {["", "GENERATED", "FLAGGED", "REVIEWED", "RESOLVED"].map((s) => (
              <button key={s}
                style={{ ...S.btn("ghost"), background: reconFilterStatus === s ? C.blueSoft : "#f0f4fa", color: reconFilterStatus === s ? C.blue : C.textMid, padding: "5px 10px", fontSize: 11 }}
                onClick={() => { setReconFilterStatus(s); setReconPage(1); }}
              >{s || "All"}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: 8 }}>
            <span style={{ fontSize: 11, color: C.textLight }}>Discrepancies:</span>
            {[["", "All"], ["true", "Has Issues"], ["false", "Clean"]].map(([val, label]) => (
              <button key={val}
                style={{ ...S.btn("ghost"), background: reconFilterDiscrepancy === val ? C.blueSoft : "#f0f4fa", color: reconFilterDiscrepancy === val ? C.blue : C.textMid, padding: "5px 10px", fontSize: 11 }}
                onClick={() => { setReconFilterDiscrepancy(val); setReconPage(1); }}
              >{label}</button>
            ))}
          </div>
          {(reconFilterStatus || reconFilterDiscrepancy) && (
            <button style={{ ...S.btn("ghost"), padding: "5px 10px", fontSize: 11, color: C.red }}
              onClick={() => { setReconFilterStatus(""); setReconFilterDiscrepancy(""); setReconPage(1); }}>
              <FiXCircle size={11} /> Clear
            </button>
          )}
        </div>

        {reconLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spinner animation="border" style={{ color: C.blue }} /></div>
        ) : reconReports.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: C.textLight, fontSize: 13 }}>
            No reconciliation reports found. Click <strong>Run Reconciliation</strong> to generate the first report.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Report Date", "Period", "Status", "Discrepancies", "Balance Diff", "Disbursed", "Success / Failed", "Actions"].map((h) => (
                    <th key={h} style={["Balance Diff", "Disbursed", "Success / Failed"].includes(h) ? { ...S.th, textAlign: "right" } : S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reconReports.map((r) => {
                  const isFlagged  = r.hasDiscrepancy;
                  const statusColor = { GENERATED: "blue", FLAGGED: "red", REVIEWED: "yellow", RESOLVED: "green" }[r.status] || "blue";
                  const openCount  = (r.discrepancies || []).filter((d) => !d.resolved).length;
                  return (
                    <tr key={r._id}
                      style={{ cursor: "pointer", transition: "background 0.1s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.inputBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      onClick={() => setReconDetail(r)}
                    >
                      <td style={S.td}><DateTimeCell value={r.reportDate} /></td>
                      <td style={S.td}><PeriodCell start={r.periodStart} end={r.periodEnd} /></td>
                      <td style={S.td}><span style={S.badge(statusColor)}>{r.status}</span></td>
                      <td style={{ ...S.td, fontWeight: 600, color: isFlagged ? C.red : C.teal }}>
                        {isFlagged ? `${openCount} open / ${r.discrepancies?.length || 0} total` : "None"}
                      </td>
                      <td style={{ ...S.td, ...S.cellNum, color: r.balanceDiscrepancy > 0 ? C.orange : C.textMid }}>
                        {r.balanceDiscrepancy > 0 ? fmtUGX(r.balanceDiscrepancy) : "—"}
                      </td>
                      <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{fmtUGX(r.ledger?.totalDisbursed)}</td>
                      <td style={{ ...S.td, ...S.cellNum, color: r.ledger?.failedCount > 0 ? C.red : C.textMid }}>
                        {r.ledger?.successCount} / {r.ledger?.failedCount}
                      </td>
                      <td style={{ ...S.td }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button style={S.btnSm("action")} onClick={() => setReconDetail(r)}>
                            View <FiChevronRight size={12} />
                          </button>
                          {r.status === "FLAGGED" && (
                            <button style={S.btnSm("teal")} onClick={() => handleMarkReviewed(r.reportId)}>
                              <FiCheckCircle size={12} /> Review
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {reconPagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}>
            <button style={S.btn("ghost")} disabled={reconPage <= 1} onClick={() => setReconPage((p) => p - 1)}>Prev</button>
            <span style={{ padding: "8px 16px", fontSize: 13, color: C.textMid }}>Page {reconPagination.page} of {reconPagination.pages}</span>
            <button style={S.btn("ghost")} disabled={reconPage >= reconPagination.pages} onClick={() => setReconPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </div>
      )} {/* end reconciliation tab */}

      {/* ── Super-user Commission Contract Modal ─────────────────── */}
      <Modal show={!!commissionEdit} onHide={() => !commissionSaving && setCommissionEdit(null)} centered size="sm">
        <Modal.Header closeButton={!commissionSaving} style={{ background: C.header, color: "#fff" }} className="wallet-modal-header">
          <Modal.Title>
            <FiPercent size={14} style={{ marginRight: 6 }} /> Edit Commission Contract
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {commissionEdit && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>{commissionEdit.name}</div>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 14 }}>{commissionEdit.vendorType} · {commissionEdit.country}</div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.textMid, marginBottom: 5 }}>Commission rate (%)<SettingHelp topic="vendorContractRate" /></label>
              <input type="number" min="0" max="100" step="0.01" value={commissionEditRate} onChange={(e) => setCommissionEditRate(e.target.value)} style={{ ...S.input, width: "100%", marginBottom: 12 }} />
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.textMid, marginBottom: 5 }}>Reason for change</label>
              <textarea value={commissionEditReason} onChange={(e) => setCommissionEditReason(e.target.value)} rows={3} placeholder="e.g. Signed commercial agreement dated 22 Jul 2026" style={{ ...S.input, width: "100%", resize: "vertical" }} />
              <div style={{ marginTop: 10, padding: 9, borderRadius: 7, background: C.orangeSoft, color: C.textMid, fontSize: 11 }}>
                Saving confirms this contract. Orders placed from now on use this rate and every product in the vendor's catalogue has its commission recomputed; selling prices do not change. Orders and escrow already recorded keep their rate.
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setCommissionEdit(null)} disabled={commissionSaving}>Cancel</Button>
          <Button size="sm" onClick={saveCommissionContract} disabled={commissionSaving} style={{ background: C.purple, border: "none" }}>
            {commissionSaving ? <Spinner size="sm" animation="border" /> : "Save contract"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── VAT Registration Modal (WIRE_CONTRACT §17.2) ─────────────
          The copy here is the whole control. Ops reads it once and acts, and
          getting it backwards means either a vendor charging 18% tax it cannot
          remit, or a registered vendor quietly under-collecting. So: the toggle
          is phrased as a STATE ("this vendor is VAT registered"), never as an
          action; each state spells out what changes and when; and the default is
          stated explicitly. Prices are VAT inclusive, so neither state changes a
          price, and the server charges VAT only with the box ticked AND a TIN on
          file (vendorChargesVat in models/shared/vatRegistrationFields.js). */}
      <Modal show={!!vatEdit} onHide={() => !vatSaving && setVatEdit(null)} centered size="sm">
        <Modal.Header closeButton={!vatSaving} style={{ background: C.header, color: "#fff" }} className="wallet-modal-header">
          <Modal.Title>
            <FiShield size={14} style={{ marginRight: 6 }} /> VAT registration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {vatEdit && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>{vatEdit.name}</div>
              <div style={{ fontSize: 11, color: C.textLight, marginBottom: 14 }}>{vatEdit.vendorType} · {vatEdit.country}</div>

              <label style={{ display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer", marginBottom: 4 }}>
                <input
                  type="checkbox"
                  checked={vatEditRegistered}
                  onChange={(e) => setVatEditRegistered(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text }}>
                    This vendor is VAT registered<span onClick={(e) => e.preventDefault()}><SettingHelp topic="vendorVatRegistered" /></span>
                  </span>
                  <span style={{ display: "block", fontSize: 11, color: C.textLight, marginTop: 2 }}>
                    Tick this only if the vendor is genuinely registered for VAT
                    with the URA and can remit what it collects.
                  </span>
                </span>
              </label>

              {vatEditRegistered ? (
                <>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.textMid, margin: "14px 0 5px" }}>
                    VAT registration number (TIN), required<SettingHelp topic="vendorVatNumber" />
                  </label>
                  <input
                    type="text"
                    maxLength={32}
                    value={vatEditNumber}
                    onChange={(e) => setVatEditNumber(e.target.value)}
                    placeholder="e.g. 1000123456"
                    style={{ ...S.input, width: "100%" }}
                  />
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 5 }}>
                    Printed as the vendor's TIN on its terminal slips. While it is
                    blank the server charges this vendor no VAT, which is what the
                    amber "No TIN on file" note means. Customer receipts and EFRIS use
                    the EFRIS TIN under Vendor tax settings when one is recorded.
                  </div>
                  <div style={{ marginTop: 12, padding: 9, borderRadius: 7, background: C.tealSoft || C.inputBg, color: C.textMid, fontSize: 11 }}>
                    <strong>VAT will be backed out of this vendor's taxable lines.</strong>{" "}
                    Prices do not change, because they already include VAT; orders,
                    receipts and terminal slips show the VAT inside them. Every vendor
                    record starts registered until it is changed here.
                  </div>
                </>
              ) : (
                <div style={{ marginTop: 12, padding: 10, borderRadius: 7, background: C.orangeSoft, color: C.textMid, fontSize: 11, lineHeight: 1.5 }}>
                  <strong style={{ color: C.orange }}>
                    This vendor will STOP charging VAT.
                  </strong>
                  <div style={{ marginTop: 5 }}>
                    Prices stay the same and no VAT is backed out of them: new orders
                    and receipts carry no VAT line, and its terminals stop showing one
                    from their next sync. Existing orders are not changed.
                  </div>
                  <div style={{ marginTop: 5 }}>
                    Only do this for a vendor that is genuinely NOT VAT
                    registered. Turning it off for a registered vendor means
                    under-declaring VAT the vendor still owes.
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setVatEdit(null)} disabled={vatSaving}>Cancel</Button>
          <Button size="sm" onClick={saveVatSettings} disabled={vatSaving} style={{ background: C.purple, border: "none" }}>
            {vatSaving ? <Spinner size="sm" animation="border" /> : "Save VAT settings"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Reconciliation Detail Modal ───────────────────────────── */}
      <Modal show={!!reconDetail} onHide={() => setReconDetail(null)} centered size="lg">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>
            <FiActivity size={14} style={{ marginRight: 6 }} />
            Reconciliation Report — {reconDetail ? new Date(reconDetail.reportDate).toLocaleDateString() : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 20, maxHeight: "72vh", overflowY: "auto" }}>
          {reconDetail && (() => {
            const r = reconDetail;
            const statusColor = { GENERATED: "blue", FLAGGED: "red", REVIEWED: "yellow", RESOLVED: "green" }[r.status] || "blue";
            const vendorCommissionRows = r.vendorCommissions || r.restaurantCommissions || [];
            const SectionTitle = ({ children }) => (
              <div style={{ fontSize: 12, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, marginTop: 16, paddingBottom: 4, borderBottom: `1px solid ${C.border}` }}>
                {children}
              </div>
            );
            const InfoCell = ({ label, value, highlight, mono }) => (
              <Col xs={6} sm={3}>
                <div style={{ background: C.inputBg, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: C.textLight, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: highlight || C.text, fontFamily: mono ? "monospace" : undefined }}>{value}</div>
                </div>
              </Col>
            );
            return (
              <div>
                <SectionTitle>Summary</SectionTitle>
                <Row className="g-2 mb-1">
                  <InfoCell label="Status"          value={<span style={S.badge(statusColor)}>{r.status}</span>} />
                  <InfoCell label="Report Date"     value={new Date(r.reportDate).toLocaleDateString()} />
                  <InfoCell label="Period"          value={`${new Date(r.periodStart).toLocaleDateString()} → ${new Date(r.periodEnd).toLocaleDateString()}`} />
                  <InfoCell label="Balance Diff"    value={fmtUGX(r.balanceDiscrepancy)} highlight={r.balanceDiscrepancy > 0 ? C.orange : undefined} />
                </Row>

                <SectionTitle>Ledger (Internal Records)</SectionTitle>
                <Row className="g-2 mb-1">
                  <InfoCell label="Total Disbursed"    value={fmtUGX(r.ledger?.totalDisbursed)} />
                  <InfoCell label="Total MoMo Debited" value={fmtUGX(r.ledger?.totalMomoDebited)} />
                  <InfoCell label="MTN Fees Absorbed"  value={fmtUGX(r.ledger?.totalMtnFeesAbsorbed)} />
                  <InfoCell label="Disbursements"      value={r.ledger?.disbursementCount} />
                  <InfoCell label="Successful"         value={r.ledger?.successCount} highlight={C.teal} />
                  <InfoCell label="Failed"             value={r.ledger?.failedCount}  highlight={r.ledger?.failedCount > 0 ? C.red : undefined} />
                </Row>

                <SectionTitle>Vendor Commission Settlement</SectionTitle>
                {vendorCommissionRows.length === 0 ? (
                  <div style={{ padding: "10px 0", fontSize: 12, color: C.textMid }}>
                    No vendor escrow activity was recorded in this period.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
                    <table style={{ ...S.table, minWidth: 760 }}>
                      <thead>
                        <tr style={{ background: C.inputBg }}>
                          {[
                            "Vendor",
                            "Type",
                            "Rate",
                            "Orders",
                            "Gross sales",
                            "Lusuku commission",
                            "Vendor net",
                            "Payouts in period",
                          ].map((label) => (
                            <th key={label} style={["Vendor", "Type"].includes(label) ? S.th : { ...S.th, textAlign: "right" }}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {vendorCommissionRows.map((row, index) => {
                          const balanced = Math.abs(Number(row.gross || 0) - Number(row.commission || 0) - Number(row.vendorNet || 0)) <= 1;
                          return (
                            <tr key={`${row.vendorId}-${row.commissionRate}-${index}`} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td style={{ ...S.td, ...S.cellMono }} title={row.vendorContractId || row.restaurantId || row.vendorId}>
                                {truncate(row.vendorContractId || row.restaurantId || row.vendorId, 16)}
                              </td>
                              <td style={S.td}>{fmtVendorType(row.vendorType || "RESTAURANT")}</td>
                              <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{Number(row.commissionRate || 0).toFixed(2)}%</td>
                              <td style={{ ...S.td, ...S.cellNum }}>{row.orderCount || 0}</td>
                              <td style={{ ...S.td, ...S.cellNum }}>{fmtUGX(row.gross)}</td>
                              <td style={{ ...S.td, ...S.cellNum, color: C.orange, fontWeight: 600 }}>{fmtUGX(row.commission)}</td>
                              <td style={{ ...S.td, ...S.cellNum, color: balanced ? C.teal : C.red, fontWeight: 600 }}>
                                {fmtUGX(row.vendorNet)}
                              </td>
                              <td style={{ ...S.td, ...S.cellNum }}>{fmtUGX(row.vendorDisbursedInPeriod)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <SectionTitle>MTN Wallet</SectionTitle>
                <Row className="g-2 mb-1">
                  <InfoCell label="Opening balance" value={r.momo?.openingBalance != null ? fmtUGX(r.momo.openingBalance) : "N/A (first report)"} />
                  <InfoCell label="Money in (landed)" value={fmtUGX(r.momo?.totalCollected)} highlight={r.momo?.totalCollected > 0 ? C.teal : undefined} />
                  <InfoCell label="Payouts debited" value={fmtUGX(r.ledger?.totalMomoDebited)} />
                  <InfoCell label="Expected closing" value={r.momo?.expectedBalance != null ? fmtUGX(r.momo.expectedBalance) : "Not computed"} />
                  <InfoCell label="Closing balance" value={r.momo?.closingBalance != null ? fmtUGX(r.momo.closingBalance) : "Unavailable"} />
                  <InfoCell
                    label="Gap (closing less expected)"
                    value={r.momo?.balanceGap != null ? `${r.momo.balanceGap > 0 ? "+" : ""}${fmtUGX(r.momo.balanceGap)}` : "n/a"}
                    highlight={r.momo?.balanceGap ? (Math.abs(r.momo.balanceGap) > (r.discrepancyThreshold || 0) ? C.red : C.orange) : undefined}
                  />
                </Row>
                {r.momo?.inflows?.kinds && (() => {
                  const k = r.momo.inflows.kinds;
                  const momoPayments = k.customerPayments?.byRail?.MOMO;
                  const momoDelivery = k.deliveryFees?.byRail?.MOMO;
                  const lines = [
                    ["Customer MoMo payments", momoPayments],
                    ["Delivery fees (MoMo)", momoDelivery],
                    ["Rider cash deposits", k.riderDeposits],
                    ["Float top-ups", k.floatTopUps],
                    ["Adjustments", k.adjustments],
                  ].filter(([, v]) => v && v.count);
                  const est = r.momo.inflows.feeBasis?.estimated || 0;
                  return (
                    <div style={{ marginTop: 8 }}>
                      {lines.length === 0 ? (
                        <div style={{ fontSize: 12, color: C.textMid }}>Nothing reached the MTN wallet in this period.</div>
                      ) : (
                        <table style={S.table}>
                          <thead>
                            <tr>
                              <th style={S.th}>Money in</th>
                              <th style={{ ...S.th, textAlign: "right" }}>Gross</th>
                              <th style={{ ...S.th, textAlign: "right" }}>MTN kept</th>
                              <th style={{ ...S.th, textAlign: "right" }}>Landed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines.map(([label, v]) => (
                              <tr key={label}>
                                <td style={S.td}>{label} <span style={{ color: C.textLight }}>({v.count})</span></td>
                                <td style={{ ...S.td, ...S.cellNum }}>{fmtUGX(v.gross)}</td>
                                <td style={{ ...S.td, ...S.cellNum, color: v.fee ? C.orange : C.textLight }}>{v.fee ? fmtUGX(v.fee) : "none"}</td>
                                <td style={{ ...S.td, ...S.cellNum, ...S.cellStrong }}>{fmtUGX(v.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>
                        MTN tariff {r.momo.inflows.ratePct}% of each MoMo collection{est ? `; ${est} inflow${est === 1 ? "" : "s"} had no recorded fee and ${est === 1 ? "was" : "were"} estimated at it` : ""}.
                      </div>
                      {/* Delivery offers: Lusuku's part of a free or reduced
                          delivery moved no cash, so it is outside the
                          expected balance above, and shown so it is not
                          mistaken for a gap. */}
                      {k.deliveryOffersFunded?.gross ? (
                        <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                          Delivery fees funded by Lusuku under customer offers: {fmtUGX(k.deliveryOffersFunded.gross)} on {k.deliveryOffersFunded.count} order{k.deliveryOffersFunded.count === 1 ? "" : "s"}
                          {k.deliveryOffersFunded.reversed ? `, after ${fmtUGX(k.deliveryOffersFunded.reversed)} reversed on orders cancelled before delivery` : ""}.
                          {" "}A promotional cost booked in the ledger; no cash moved, so it is not part of money in.
                        </div>
                      ) : null}
                    </div>
                  );
                })()}

                <SectionTitle>Discrepancies ({(r.discrepancies || []).length})</SectionTitle>
                {(r.discrepancies || []).length === 0 ? (
                  <div style={{ padding: "12px 0", fontSize: 12, color: C.teal }}>
                    <FiCheckCircle size={13} style={{ marginRight: 5 }} />No discrepancies detected — ledger is clean.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {r.discrepancies.map((d) => {
                      const sevColor = { LOW: C.textMid, MEDIUM: C.orange, HIGH: C.red, CRITICAL: C.red }[d.severity] || C.textMid;
                      return (
                        <div key={d._id}
                          style={{ background: d.resolved ? "rgba(0,182,155,0.07)" : "rgba(230,55,87,0.06)", border: `1px solid ${d.resolved ? "rgba(0,182,155,0.25)" : "rgba(230,55,87,0.2)"}`, borderRadius: 8, padding: "10px 14px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: sevColor, textTransform: "uppercase", letterSpacing: 0.5 }}>{d.severity}</span>
                                <span style={S.badge(d.resolved ? "green" : "red")}>{DISCREPANCY_LABEL[d.type] || d.type.replace(/_/g, " ")}</span>
                                {d.amount > 0 && <span style={{ fontSize: 11, color: C.textMid }}>{fmtUGX(d.amount)}</span>}
                              </div>
                              <div style={{ fontSize: 12, color: C.textMid }}>{d.description}</div>
                              {d.resolved && (
                                <div style={{ fontSize: 11, color: C.teal, marginTop: 4 }}>
                                  ✓ Resolved — {d.resolutionNote}
                                </div>
                              )}
                            </div>
                            {!d.resolved && (
                              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                {d.type === "FEE_BAND_MISMATCH" && (
                                  <AppButton size="sm" onClick={handleCorrectBookedFees} loading={feeCorrectionBusy}>
                                    Correct booked fees
                                  </AppButton>
                                )}
                                <AppButton size="sm" variant="quiet"
                                  onClick={() => { setResolveModal({ reportId: r.reportId, discrepancyId: d._id, description: d.description }); setResolveNote(""); }}
                                >
                                  <FiCheckCircle size={11} /> Resolve
                                </AppButton>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {r.status === "FLAGGED" && (
                  <div style={{ marginTop: 16 }}>
                    <button style={{ ...S.btn("teal") }} onClick={() => { handleMarkReviewed(r.reportId); setReconDetail(null); }}>
                      <FiCheckCircle size={13} /> Mark Report as Reviewed
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setReconDetail(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Run Reconciliation Modal ──────────────────────────────── */}
      <Modal show={reconRunModal} onHide={() => setReconRunModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>
            <FiActivity size={13} style={{ marginRight: 6 }} />
            Run Reconciliation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>
            Compare the books for the period (money in net of MTN's fee, payouts debited) against the live MTN balance. A period that already has a report is refreshed in place rather than saved twice.
          </div>

          {/* ── Quick presets ── */}
          {(() => {
            const daysAgo = (n) => {
              const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
            };
            const monthStart = () => {
              const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10);
            };
            const presets = [
              { label: "Last 7 days",  start: daysAgo(7),  end: daysAgo(1) },
              { label: "Last 14 days", start: daysAgo(14), end: daysAgo(1) },
              { label: "Last 30 days", start: daysAgo(30), end: daysAgo(1) },
              { label: "Month to date", start: monthStart(), end: daysAgo(1) },
            ];
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: C.textLight, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Quick select</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {presets.map((p) => {
                    const active = reconPeriodStart === p.start && reconPeriodEnd === p.end;
                    return (
                      <button key={p.label}
                        style={{ fontSize: 11, padding: "5px 10px", borderRadius: 6, border: `1px solid ${active ? C.blue : C.border}`, background: active ? `${C.blue}22` : C.inputBg, color: active ? C.blue : C.textMid, cursor: "pointer", fontWeight: active ? 700 : 400 }}
                        onClick={() => { setReconPeriodStart(p.start); setReconPeriodEnd(p.end); }}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Manual override ── */}
          <div style={{ fontSize: 11, color: C.textLight, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Or set manually</div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: C.textMid, marginBottom: 3, display: "block" }}>From</label>
              <input type="date" style={{ ...S.input, width: "100%" }}
                value={reconPeriodStart}
                max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setReconPeriodStart(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: C.textMid, marginBottom: 3, display: "block" }}>To</label>
              <input type="date" style={{ ...S.input, width: "100%" }}
                value={reconPeriodEnd}
                max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setReconPeriodEnd(e.target.value)} />
            </div>
          </div>

          {/* Period summary */}
          {reconPeriodStart && reconPeriodEnd && (
            <div style={{ marginTop: 10, background: C.inputBg, borderRadius: 6, padding: "7px 12px", fontSize: 12, color: C.textMid }}>
              <FiCalendar size={11} style={{ marginRight: 5 }} />
              {new Date(reconPeriodStart).toLocaleDateString()} → {new Date(reconPeriodEnd).toLocaleDateString()}
              {" · "}{Math.round((new Date(reconPeriodEnd) - new Date(reconPeriodStart)) / 86400000) + 1} days
            </div>
          )}

          {/* Future-date warning */}
          {reconPeriodEnd && (reconPeriodEnd > new Date(Date.now() - 86400000).toISOString().slice(0, 10)) && (
            <div style={{ marginTop: 8, fontSize: 11, color: C.red, display: "flex", alignItems: "center", gap: 5 }}>
              <FiAlertCircle size={11} /> Period end cannot include today or future dates.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setReconRunModal(false)}>Cancel</Button>
          <Button size="sm" onClick={handleRunReconciliation}
            disabled={reconRunLoading || !reconPeriodStart || !reconPeriodEnd || reconPeriodEnd > new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
            style={{ background: C.teal, border: "none" }}>
            {reconRunLoading ? <Spinner animation="border" size="sm" /> : <><FiActivity size={12} style={{ marginRight: 4 }} /> Run</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Resolve Discrepancy Modal ─────────────────────────────── */}
      <Modal show={!!resolveModal} onHide={() => setResolveModal(null)} centered size="sm">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>Resolve Discrepancy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 10 }}>{resolveModal?.description}</div>
          <textarea
            style={{ ...S.input, width: "100%", height: 80, resize: "vertical" }}
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="Explain how this was resolved…"
          />
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setResolveModal(null)}>Cancel</Button>
          <Button size="sm" onClick={handleResolveDiscrepancy}
            disabled={resolveLoading || !resolveNote.trim()}
            style={{ background: C.teal, border: "none" }}>
            {resolveLoading ? <Spinner animation="border" size="sm" /> : <><FiCheckCircle size={12} style={{ marginRight: 4 }} /> Mark Resolved</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Batch Detail Modal ───────────────────────────── */}
      <Modal show={!!selectedBatch} onHide={() => { setSelectedBatch(null); setBatchDetail(null); }} centered size="lg">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>
            <FiPackage size={14} style={{ marginRight: 6 }} />
            Batch Detail — {selectedBatch ? truncate(selectedBatch, 24) : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 20, maxHeight: "70vh", overflowY: "auto" }}>
          {batchDetailLoading && <div style={{ textAlign: "center", padding: 30 }}><Spinner animation="border" style={{ color: C.blue }} /></div>}
          {!batchDetailLoading && batchDetail && (() => {
            const { batch, disbursements: dlist = [], riderRecords = [] } = batchDetail;
            const bMeta   = BATCH_STATUS_META[batch.status] || { color: "blue" };
            const fmtDate = (d) => d ? new Date(d).toLocaleString() : "—";
            const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString() : "—";
            const InfoCell = ({ label, value, mono = false, highlight }) => (
              <Col xs={6} sm={3}>
                <div style={{ background: C.inputBg, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: C.textLight, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: highlight || C.text, fontFamily: mono ? "monospace" : undefined }}>{value}</div>
                </div>
              </Col>
            );
            const SectionTitle = ({ children }) => (
              <div style={{ fontSize: 12, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, marginTop: 16, paddingBottom: 4, borderBottom: `1px solid ${C.border}` }}>
                {children}
              </div>
            );
            return (
              <div>
                {/* ── Summary stats ── */}
                <SectionTitle>Summary</SectionTitle>
                <Row className="g-2 mb-1">
                  <InfoCell label="Status"     value={<span style={S.badge(bMeta.color)}>{batch.status}</span>} />
                  <InfoCell label="Type"        value={batch.batchType} />
                  <InfoCell label="Payees"      value={batch.totalPayees} />
                  <InfoCell label="Net Amount"  value={fmtUGX(batch.totalNetAmount)} />
                  <InfoCell label="MoMo Debit"  value={fmtUGX(batch.totalMomoDebit)} />
                  <InfoCell label="MTN Fees"    value={fmtUGX(batch.totalMtnFees)} />
                  <InfoCell label="Processed"   value={`${batch.processedCount || 0} / ${batch.totalPayees}`} />
                  <InfoCell label="Success / Failed" value={`${batch.successCount} / ${batch.failedCount}`}
                    highlight={batch.failedCount > 0 ? C.red : undefined} />
                </Row>

                {/* ── Period & origin ── */}
                <SectionTitle>Period & Origin</SectionTitle>
                <Row className="g-2 mb-1">
                  <InfoCell label="Period Start"  value={fmtDateShort(batch.periodStart)} />
                  <InfoCell label="Period End"    value={fmtDateShort(batch.periodEnd)} />
                  <InfoCell label="Triggered By"  value={batch.createdBy?.source || "—"} />
                  <InfoCell label="Requires Approval" value={batch.requiresApproval ? "Yes" : "No"}
                    highlight={batch.requiresApproval ? C.yellow : undefined} />
                  {batch.approvalThreshold > 0 && (
                    <InfoCell label="Approval Threshold" value={fmtUGX(batch.approvalThreshold)} />
                  )}
                </Row>

                {/* ── Timeline ── */}
                <SectionTitle>Timeline</SectionTitle>
                <Row className="g-2 mb-1">
                  <InfoCell label="Created"            value={fmtDate(batch.createdAt)} />
                  <InfoCell label="Preparation Started" value={fmtDate(batch.preparationStartedAt)} />
                  <InfoCell label="Processing Started"  value={fmtDate(batch.processingStartedAt)} />
                  <InfoCell label="Completed At"        value={fmtDate(batch.completedAt)} />
                </Row>

                {/* ── Approval / Rejection audit trail ── */}
                {(batch.approvedBy || batch.rejectedBy) && (
                  <>
                    <SectionTitle>{batch.approvedBy ? "Approval" : "Rejection"}</SectionTitle>
                    <Row className="g-2 mb-1">
                      {batch.approvedBy && <>
                        <InfoCell label="Approved By"  value={truncate(batch.approvedBy?.toString(), 16)} mono />
                        <InfoCell label="Approved At"  value={fmtDate(batch.approvedAt)} />
                        {batch.approvalNote && <Col xs={12}>
                          <div style={{ background: "rgba(0,182,155,0.08)", border: `1px solid rgba(0,182,155,0.25)`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
                            <span style={{ fontWeight: 600, color: C.teal }}>Note: </span>{batch.approvalNote}
                          </div>
                        </Col>}
                      </>}
                      {batch.rejectedBy && <>
                        <InfoCell label="Rejected By"  value={truncate(batch.rejectedBy?.toString(), 16)} mono />
                        <InfoCell label="Rejected At"  value={fmtDate(batch.rejectedAt)} />
                        {batch.rejectionReason && <Col xs={12}>
                          <div style={{ background: "rgba(230,55,87,0.08)", border: `1px solid rgba(230,55,87,0.25)`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
                            <span style={{ fontWeight: 600, color: C.red }}>Reason: </span>{batch.rejectionReason}
                          </div>
                        </Col>}
                      </>}
                    </Row>
                  </>
                )}

                {/* ── Error summary ── */}
                {batch.errorSummary && (
                  <>
                    <SectionTitle>Error</SectionTitle>
                    <div style={{ background: "rgba(230,55,87,0.08)", border: `1px solid rgba(230,55,87,0.3)`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: C.red, marginBottom: 4 }}>
                      {batch.errorSummary}
                    </div>
                  </>
                )}

                {/* ── Approve / Reject actions ── */}
                {batch.status === "AWAITING_APPROVAL" && (
                  <div style={{ display: "flex", gap: 10, marginTop: 16, marginBottom: 4 }}>
                    <button style={S.btn("teal")} onClick={() => { setSelectedBatch(null); setConfirmApprove(batch.batchId); }}>
                      <FiThumbsUp size={13} /> Approve Batch
                    </button>
                    <button style={S.btn("danger")} onClick={() => { setSelectedBatch(null); setConfirmReject(batch.batchId); setRejectReason(""); }}>
                      <FiThumbsDown size={13} /> Reject Batch
                    </button>
                  </div>
                )}

                {/* ── Rider payout records ── */}
                <SectionTitle>Rider Payout Records ({riderRecords.length})</SectionTitle>
                {riderRecords.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textLight, padding: "10px 0" }}>
                    {batch.batchType === "RIDER_WEEKLY"
                      ? batch.status === "FAILED" ? "Batch failed before any payout records were created." : "No payout records — batch may still be preparing."
                      : "Rider records only apply to RIDER_WEEKLY batches."}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto", marginBottom: 4 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                        {["Rider", "Phone", "Gross", "COD Offset", "Net Payout", "Deliveries", "Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {riderRecords.map((r) => (
                          <tr key={r._id}>
                            <td style={S.td}>{r.riderName || truncate(r.riderId?.toString(), 10)}</td>
                            <td style={S.td}>{r.riderPhone || "—"}</td>
                            <td style={S.td}>{fmtUGX(r.grossEarnings)}</td>
                            <td style={{ ...S.td, color: C.red }}>{fmtUGX(r.codOffset)}</td>
                            <td style={{ ...S.td, fontWeight: 600 }}>{fmtUGX(r.netPayout)}</td>
                            <td style={S.td}>{r.totalDeliveries}</td>
                            <td style={S.td}><span style={S.badge(r.status === "DISBURSED" ? "green" : r.status === "FAILED" ? "red" : r.status === "SKIPPED" ? "yellow" : "blue")}>{r.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── Disbursement requests ── */}
                <SectionTitle>Disbursement Requests ({dlist.length})</SectionTitle>
                {dlist.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textLight, padding: "10px 0" }}>
                    {batch.status === "FAILED" || batch.status === "REJECTED"
                      ? "No disbursements were submitted — batch did not reach processing stage."
                      : batch.status === "PREPARING" || batch.status === "AWAITING_APPROVAL"
                      ? "Disbursements will appear here once the batch is approved and processing begins."
                      : "No disbursement requests found for this batch."}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                        {["ID", "Payee", "Phone", "Amount", "MTN Fee", "Status", "MTN Ref", "Failure"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {dlist.map((d) => {
                          const dm = STATUS_META[d.status] || { color: "blue" };
                          return (
                            <tr key={d._id}>
                              <td style={S.td}><CopyValue value={d.payeeReference || d.disbursementId} max={16} style={{ fontSize: 11 }} /></td>
                              <td style={S.td}>{d.payeeName || "—"}</td>
                              <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11 }}>{d.payeePhone || "—"}</td>
                              <td style={{ ...S.td, fontWeight: 600 }}>{fmtUGX(d.amount)}</td>
                              <td style={{ ...S.td, color: C.textLight }}>{fmtUGX(d.mtnFeeAbsorbed)}</td>
                              <td style={S.td}>
                                <span style={S.badge(dm.color)}>{d.status}</span>
                                {anyInFlight([d]) && <div style={{ marginTop: 3 }}><StageTimer row={d} scheduler={scheduler} now={now} /></div>}
                              </td>
                              <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11 }}>{truncate(d.momoReferenceId, 14) || "—"}</td>
                              <td style={{ ...S.td, color: C.red, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.failureReason}>{d.failureReason || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => { setSelectedBatch(null); setBatchDetail(null); }}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Approve Batch Modal ──────────────────────────── */}
      <Modal show={!!confirmApprove} onHide={() => setConfirmApprove(null)} centered size="sm">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>Approve Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13, color: C.textMid }}>
          Approve batch <strong>{confirmApprove && truncate(confirmApprove, 20)}</strong>? All queued disbursements will be submitted to MTN immediately.
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setConfirmApprove(null)}>Cancel</Button>
          <Button size="sm" onClick={handleApproveBatch} disabled={batchActionLoading} style={{ background: C.teal, border: "none" }}>
            {batchActionLoading ? <Spinner animation="border" size="sm" /> : <><FiThumbsUp size={12} style={{ marginRight: 4 }} /> Approve</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Reject Batch Modal ───────────────────────────── */}
      <Modal show={!!confirmReject} onHide={() => setConfirmReject(null)} centered size="sm">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>Reject Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 13, color: C.textMid, marginBottom: 10 }}>Provide a reason for rejecting batch <strong>{confirmReject && truncate(confirmReject, 20)}</strong>:</div>
          <textarea
            style={{ ...S.input, width: "100%", height: 80, resize: "vertical" }}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Incorrect period, insufficient MoMo balance"
          />
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setConfirmReject(null)}>Cancel</Button>
          <Button size="sm" onClick={handleRejectBatch} disabled={batchActionLoading || !rejectReason.trim()} style={{ background: C.red, border: "none" }}>
            {batchActionLoading ? <Spinner animation="border" size="sm" /> : <><FiThumbsDown size={12} style={{ marginRight: 4 }} /> Reject</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Initiate Batch Modal ─────────────────────────── */}
      <Modal show={!!initiateModal} onHide={() => setInitiateModal(null)} centered size="sm">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>
            <FiCalendar size={13} style={{ marginRight: 6 }} />
            Initiate {initiateModal === "rider" ? "Rider Weekly" : "Partner Bimonthly"} Batch
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>
            Choose the earnings period to pay out. Only past dates are valid — a draft batch will be created for your review before any money moves.
          </div>

          {/* ── Quick presets ── */}
          {(() => {
            const daysAgo = (n) => {
              const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
            };
            const monthStart = () => {
              const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10);
            };
            const presets = initiateModal === "rider"
              ? [
                  { label: "Last 7 days",  start: daysAgo(7),  end: daysAgo(1) },
                  { label: "Last 14 days", start: daysAgo(14), end: daysAgo(1) },
                  { label: "Last 30 days", start: daysAgo(30), end: daysAgo(1) },
                  { label: "Month to date", start: monthStart(), end: daysAgo(1) },
                ]
              : [
                  { label: "Last 15 days", start: daysAgo(15), end: daysAgo(1) },
                  { label: "Last 30 days", start: daysAgo(30), end: daysAgo(1) },
                  { label: "Last 60 days", start: daysAgo(60), end: daysAgo(1) },
                  { label: "Month to date", start: monthStart(), end: daysAgo(1) },
                ];
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: C.textLight, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Quick select</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {presets.map((p) => {
                    const active = initPeriodStart === p.start && initPeriodEnd === p.end;
                    return (
                      <button key={p.label}
                        style={{ fontSize: 11, padding: "5px 10px", borderRadius: 6, border: `1px solid ${active ? C.blue : C.border}`, background: active ? `${C.blue}22` : C.inputBg, color: active ? C.blue : C.textMid, cursor: "pointer", fontWeight: active ? 700 : 400 }}
                        onClick={() => { setInitPeriodStart(p.start); setInitPeriodEnd(p.end); }}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Manual override ── */}
          <div style={{ fontSize: 11, color: C.textLight, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Or set manually</div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: C.textMid, marginBottom: 3, display: "block" }}>From</label>
              <input type="date" style={{ ...S.input, width: "100%" }}
                value={initPeriodStart}
                max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setInitPeriodStart(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: C.textMid, marginBottom: 3, display: "block" }}>To</label>
              <input type="date" style={{ ...S.input, width: "100%" }}
                value={initPeriodEnd}
                max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setInitPeriodEnd(e.target.value)} />
            </div>
          </div>

          {/* Period summary */}
          {initPeriodStart && initPeriodEnd && (
            <div style={{ marginTop: 10, background: C.inputBg, borderRadius: 6, padding: "7px 12px", fontSize: 12, color: C.textMid }}>
              <FiCalendar size={11} style={{ marginRight: 5 }} />
              {new Date(initPeriodStart).toLocaleDateString()} → {new Date(initPeriodEnd).toLocaleDateString()}
              {" · "}{Math.round((new Date(initPeriodEnd) - new Date(initPeriodStart)) / 86400000) + 1} days
            </div>
          )}

          {/* Future-date warning */}
          {(initPeriodEnd > new Date(Date.now() - 86400000).toISOString().slice(0, 10)) && (
            <div style={{ marginTop: 8, fontSize: 11, color: C.red, display: "flex", alignItems: "center", gap: 5 }}>
              <FiAlertCircle size={11} /> Period end cannot include today or future dates.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => { setInitiateModal(null); setInitPeriodStart(""); setInitPeriodEnd(""); }}>Cancel</Button>
          <Button size="sm" onClick={handleInitiateBatch}
            disabled={batchActionLoading || !initPeriodStart || !initPeriodEnd || initPeriodEnd > new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
            style={{ background: C.blue, border: "none" }}>
            {batchActionLoading ? <Spinner animation="border" size="sm" /> : <><FiPlusCircle size={12} style={{ marginRight: 4 }} /> Initiate</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Audit trail drawer (right side) ─────────────────── */}
      <Offcanvas show={!!auditRow} onHide={() => setAuditRow(null)} placement="end" style={{ width: 520 }}>
        <Offcanvas.Header closeButton style={{ background: C.header, color: "#fff" }} className="wallet-modal-header">
          <Offcanvas.Title style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>
            <FiClipboard size={14} style={{ marginRight: 6 }} />
            Audit trail — {auditRow ? truncate(auditRow.disbursementId, 26) : ""}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ padding: 20, fontSize: 13, color: C.textMid }}>
          {auditRow && (() => {
            const feed = mergeAuditTimeline(auditRow, auditLogs);
            const { label: confLabel } = confirmationMeta(auditRow);
            return (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                  {[
                    ["Payee",   `${auditRow.payeeName || "—"}${auditRow.payeePhone ? ` · ${auditRow.payeePhone}` : ""}`],
                    ["Amount",  `${fmtUGX(auditRow.amount)}${auditRow.mtnFeeAbsorbed ? ` (+${Number(auditRow.mtnFeeAbsorbed).toLocaleString()} fee)` : ""}`],
                    ["Status",  `${auditRow.status || "—"} · ${confLabel}`],
                    ["Trigger", `${(auditRow.triggerType || "—").replace(/_/g, " ")}${auditRow.purpose ? ` · ${auditRow.purpose}` : ""}`],
                    ["MTN ref", auditRow.momoReferenceId || "—"],
                    ["MTN fin. tx", auditRow.momoFinancialTxId || "—"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: C.inputBg, borderRadius: 8, padding: "8px 12px" }}>
                      <div style={{ fontSize: 11, color: C.textLight, marginBottom: 2 }}>{k}</div>
                      <div style={{ fontSize: 12, color: C.text, fontWeight: 600, wordBreak: "break-all" }}>{v}</div>
                    </div>
                  ))}
                </div>
                {auditRow.failureReason && auditRow.status !== "SUCCESSFUL" && (
                  <div style={{ background: C.accentSoft, border: "1px solid #f5bdc6", borderRadius: 8, padding: "8px 12px", marginBottom: 16, fontSize: 12, color: "#c0152f" }}>
                    <FiAlertCircle size={12} style={{ marginRight: 4 }} /> {auditRow.failureReason}{auditRow.failureCode ? ` (${auditRow.failureCode})` : ""}
                  </div>
                )}
                {auditUnavailable && (
                  <div style={{ fontSize: 12, color: C.orange, marginBottom: 12 }}><FiAlertCircle size={12} /> Wallet audit log unavailable on this version — showing the lifecycle only.</div>
                )}
                {auditLoading ? (
                  <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" size="sm" style={{ color: C.blue }} /></div>
                ) : feed.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textLight }}>No events recorded.</div>
                ) : (
                  <div style={{ position: "relative", paddingLeft: 18 }}>
                    <div style={{ position: "absolute", left: 5, top: 4, bottom: 4, width: 2, background: C.border }} />
                    {feed.map((ev) => {
                      const life = ev.kind === "lifecycle";
                      const dot = life ? (ev.key === "life-failed" ? C.red : ev.key === "life-confirmed" ? C.teal : C.blue) : C.purple;
                      return (
                        <div key={ev.key} style={{ position: "relative", marginBottom: 14 }}>
                          <span style={{ position: "absolute", left: -18, top: 4, width: 12, height: 12, borderRadius: "50%", background: dot, border: "2px solid #fff", boxShadow: `0 0 0 1px ${dot}` }} />
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 600, color: C.text, fontSize: 12 }}>{ev.title}</span>
                            <span style={{ fontSize: 11, color: C.textLight, fontFamily: "monospace" }}>{ev.at != null ? `${new Date(ev.at).toLocaleDateString("en-UG", { day: "numeric", month: "short" })} ${formatClock(ev.at)}` : "time unknown"}</span>
                            <span style={{ ...S.badge(life ? "blue" : "purple"), padding: "1px 7px", fontSize: 11 }}>{life ? "lifecycle" : "audit"}</span>
                          </div>
                          {ev.subtitle && <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>{ev.subtitle}</div>}
                          {ev.detail && (
                            <pre style={{ margin: "6px 0 0", fontSize: 11, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 8px", whiteSpace: "pre-wrap", wordBreak: "break-word", color: C.textMid, maxHeight: 140, overflow: "auto" }}>
                              {JSON.stringify(ev.detail, null, 1).replace(/^\{\n?|\n?\}$/g, "").trim()}
                            </pre>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </Offcanvas.Body>
      </Offcanvas>

      {/* ── Scheduled report detail ─────────────────────────── */}
      <Modal show={!!scheduledDetail} onHide={() => setScheduledDetail(null)} centered size="lg">
        <Modal.Header closeButton style={{ background: C.header, borderBottom: `1px solid ${C.border}` }} className="wallet-modal-header">
          <Modal.Title>
            <FiHash size={14} style={{ marginRight: 6 }} />
            {scheduledDetail?.reportId || "Report"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 20, fontSize: 13, color: C.textMid, maxHeight: "70vh", overflowY: "auto" }}>
          {scheduledDetailLoading ? (
            <div style={{ textAlign: "center", padding: 30 }}><Spinner animation="border" style={{ color: C.purple }} /></div>
          ) : scheduledDetail && (() => {
            const d = scheduledDetail;
            const pt = d.payload?.totals || d.totals || {};
            const chainOk = d.chainValid;
            return (
              <>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  <ReportTile label="Kind" value={d.kind || "—"} sub={`seq ${d.sequence ?? "—"} · by ${d.generatedBy || "—"}`} />
                  <ReportTile label="Period" value={`${String(d.periodStart || "").slice(0, 10)} → ${String(d.periodEnd || "").slice(0, 10)}`} sub={`generated ${fmtDate(d.generatedAt)}`} />
                  <ReportTile label="Chain" value={chainOk === true ? "valid" : chainOk === false ? "BROKEN" : "—"} color={chainOk === false ? C.red : chainOk ? C.teal : C.textLight} sub="hash + previous hash recomputed" />
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  <ReportTile label="Payouts" value={Number(pt.count || 0).toLocaleString()} sub={`${pt.successCount ?? 0} ok · ${pt.failedCount ?? 0} failed · ${pt.inFlightCount ?? 0} in flight`} />
                  <ReportTile label="Amount" value={fmtUGX(pt.amount)} sub={`MoMo debit ${fmtUGX(pt.momoDebit)}`} color={C.blue} />
                  <ReportTile label="Fees absorbed" value={fmtUGX(pt.feesAbsorbed)} color={C.purple} />
                  <ReportTile label="Success rate" value={fmtPct(pt.successRate)} sub={d.payload?.registerRowCount != null ? `${d.payload.registerRowCount} register rows` : null} />
                </div>
                <div style={{ fontSize: 11, fontFamily: "monospace", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", wordBreak: "break-all" }}>
                  <div><span style={{ color: C.textLight }}>contentHash </span>{d.contentHash || "—"}</div>
                  <div style={{ marginTop: 4 }}><span style={{ color: C.textLight }}>previousHash</span> {d.previousHash || "— (first in chain)"}</div>
                </div>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setScheduledDetail(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Poll Stuck Confirm Modal */}
      <Modal show={confirmPoll} onHide={() => setConfirmPoll(false)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.border}`, background: C.header, color: "#fff" }} className="wallet-modal-header">
          <Modal.Title>Poll Stuck Disbursements</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13, color: C.textMid }}>
          This will query MTN for the status of every disbursement stuck in <strong>PROCESSING</strong> state.
          It triggers real API calls to MTN. Proceed?
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <Button variant="secondary" size="sm" onClick={() => setConfirmPoll(false)}>Cancel</Button>
          <Button size="sm" onClick={handlePollStuck} style={{ background: C.teal, border: "none" }}>
            <FiZap size={13} style={{ marginRight: 4 }} /> Confirm & Poll
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .wallet-modal-header .btn-close { filter: invert(1) grayscale(100%) brightness(200%); }
      `}</style>
    </div>
  );
}
